/**
 * DAS PRODUKTIONS- UND LOGISTIKPORTAL -- ein Arbeitsbereich, kein Bericht.
 *
 * DREI DINGE STEHEN HIER NEBENEINANDER, und die Reihenfolge ist die der
 * Arbeit:
 *
 *   DIE ZEITACHSE     wo stehen die laufenden Bestellungen, und wo
 *                     laufen sie zusammen?
 *   DIE ARBEITSLISTE  was muss als Naechstes geschehen?
 *   DAS FENSTER       passt zeitlich noch eine Bestellung in die
 *                     naechste Consolidation?
 *
 * WAS DIE ZEITACHSE IST: eine SICHT. Sie besitzt keinen Termin. Jeder
 * Punkt kommt aus einer Bestellung, einer Consolidation oder einem
 * erfassten Termin -- und fuehrt per Klick dorthin zurueck.
 *
 * DIE X-ACHSE IST ECHTE ZEIT. Ein Ereignis steht dort, wo sein Datum
 * liegt, nicht dort, wo der naechste freie Platz waere. Gleichabstaendige
 * Punkte waeren eine Ablaufgrafik und keine Planung.
 *
 * FORECAST, ZUSAGE UND IST SIND OHNE FARBE UNTERSCHEIDBAR: ein Forecast
 * ist ein gestricheltes BAND ueber seinen Zeitraum, eine Zusage eine
 * halbe Marke, ein Ist eine gefuellte. Wer die Sicherheit nur an der
 * Farbe erkennt, erkennt sie auf einem schlechten Bildschirm gar nicht.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ConsolidationDetail, ConsolidationGoods, ConsolidationWindow, LeadTimeRule, MetaResponse,
  BestelldokumentCode, ChinaEvent, UpsertChinaEventRequest,
  OrderDocumentGroup, OrderDocumentsResponse,
  ConsolidationDocumentsResponse,
  Milestone, MilestoneCode, MilestoneCodeInfo, PurchaseOrderCapture, PurchaseOrderDetail,
  PurchaseOrderListItem, StoredCertainty, TimelineEvent, TimelineLane,
  TimelineResponse,
  PurchaseOrderPayment,
  OpenProcessStep, ProcessGuidance,
} from "@homebase/shared";
import { CONSOLIDATION_TRANSPORT_MODES, LEAD_TIME_SOURCES } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { heuteLokal } from "../lib/datum.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { OrderCapture } from "./OrderCapture.tsx";
import { OrderFinance } from "./OrderFinance.tsx";
import { HilfeRahmen, HilfeSchalter, HilfeHinweis } from "./Hilfe";
import "./ProcurementPortal.css";

const PENCIL = "✎";

/**
 * WAS AN EINEM CHINA-EREIGNIS DRANSTEHT, WENN MAN DARAUF ZEIGT.
 *
 * Name, Kernzeitraum, Vor- und Nachlauf -- und ob das Kerndatum amtlich
 * ist. Die Achse selbst bleibt ruhig; alles Weitere steht im Detail.
 *
 * DER STATUS GEHOERT DAZU. Ein vorlaeufiges Datum als sicheres
 * auszugeben waere eine Behauptung ueber eine Entscheidung, die der
 * chinesische Staatsrat noch nicht getroffen hat.
 */
function chinaTitel(c: ChinaEvent, i18n: I18n): string {
  const zeit = c.coreStart === c.coreEnd
    ? kurzDatum(c.coreStart, i18n.lang)
    : `${kurzDatum(c.coreStart, i18n.lang)} – ${kurzDatum(c.coreEnd, i18n.lang)}`;
  const teile = [`${c.name} · ${zeit}`];
  if (c.leadInDays > 0 || c.leadOutDays > 0) {
    teile.push(i18n.t("china.buffer", {
      vor: String(c.leadInDays), nach: String(c.leadOutDays) }));
  }
  teile.push(i18n.tCode("chinaStatus", c.sourceStatus));
  return teile.join(" · ");
}

/** Wie viele Pixel ein Tag breit ist. Drei Stufen, mehr braucht niemand. */
const STUFEN = { tag: 30, woche: 9, monat: 3 } as const;
type Stufe = keyof typeof STUFEN;

const TAG_MS = 86_400_000;
const alsTag = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);
const alsIso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const plusTage = (iso: string, n: number): string => alsIso(alsTag(iso) + n * TAG_MS);
const tageZwischen = (von: string, bis: string): number =>
  Math.round((alsTag(bis) - alsTag(von)) / TAG_MS);

/**
 * Die Kalenderwoche nach ISO 8601 -- Montag beginnt, Woche 1 traegt
 * den ersten Donnerstag.
 *
 * NICHT „der wievielte Sonntag im Jahr". Die amerikanische Zaehlung
 * liefert fuer denselben Tag eine andere Zahl, und die Lieferanten
 * rechnen nach ISO. Eine Woche, die um eins danebenliegt, ist
 * schlimmer als keine.
 */
function kalenderwoche(d: Date): number {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  /* Auf den Donnerstag derselben Woche schieben -- er entscheidet,
     zu welchem Jahr die Woche gehoert. */
  t.setUTCDate(t.getUTCDate() + 3 - ((t.getUTCDay() + 6) % 7));
  const ersterDonnerstag = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  ersterDonnerstag.setUTCDate(
    ersterDonnerstag.getUTCDate() + 3 - ((ersterDonnerstag.getUTCDay() + 6) % 7));
  return 1 + Math.round((t.getTime() - ersterDonnerstag.getTime()) / (7 * TAG_MS));
}

const kurzDatum = (iso: string, lang: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(lang,
    { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: "UTC" });

/** „nicht bestimmt" -- dieselbe Auskunft wie ueberall in HOMEBASE. */
/** Der Entwurf des Bestellkopfs, solange der Stift an ist. */
interface Kopfentwurf {
  nummer: string; supplier: string; datum: string;
  incoterm: string; waehrung: string; notiz: string;
}
/** Der Entwurf einer Position -- Menge, Preis, Notiz. Mehr ist nicht aenderbar. */
interface Zeilenentwurf { menge: string; preis: string; notiz: string; }

/**
 * Ein Betrag in der Sprache des Benutzers -- und ohne Nachkommaballast.
 *
 * Die Datenbank fuehrt `numeric(14,4)`, weil Stueckpreise das brauchen;
 * „14.7000" auf dem Bildschirm ist trotzdem niemandes Zahl. Gezeigt
 * werden zwei Nachkommastellen, bei feineren Preisen so viele wie noetig.
 */
function betragText(roh: string, lang: string): string {
  const n = Number(roh);
  if (!Number.isFinite(n)) return roh;
  const stellen = Math.max(2, Math.min(4, (roh.split(".")[1] ?? "").replace(/0+$/, "").length));
  return n.toLocaleString(lang, {
    minimumFractionDigits: 2, maximumFractionDigits: stellen,
  });
}

/**
 * DER BESTELLWERT -- oder die ehrliche Auskunft, dass es ihn nicht gibt.
 *
 * Ohne einen einzigen Preis gibt es keinen Wert und keine „0,00". Eine
 * Null waere eine Aussage ueber den Auftrag; die Wahrheit ist, dass
 * niemand ihn beziffert hat.
 */
function wertText(
  po: { totalAmount: string | null; currency: string | null }, i18n: I18n,
): string {
  if (po.totalAmount === null) return i18n.t("plp.noValue");
  return `${betragText(po.totalAmount, i18n.lang)} ${po.currency ?? ""}`.trim();
}

function Unknown(): React.JSX.Element {
  const { t } = useI18n();
  return <span className="detail__unknown">{t("common.unknownValue")}</span>;
}

/**
 * DIE GRUNDLAGE EINES FORECASTS -- in Worten, nicht als Kuerzel.
 *
 * Der Server liefert sie sprachneutral zusammengesetzt; hier wird daraus
 * ein Satz. Ein Forecast ohne nachvollziehbare Herkunft waere eine Zahl,
 * der niemand widersprechen kann.
 */
function basisText(e: TimelineEvent, i18n: I18n): string | null {
  if (e.basis === null) return null;
  const teile = e.basis.split("|");
  if (teile[0] === "orders") {
    return i18n.t("plp.basisOrders", { n: teile[1] ?? "?" });
  }
  const [min, max, anker, ankerTag, bereich, quelle, wortlaut] = teile;
  return i18n.t("plp.basisRule", {
    min: min ?? "?", max: max ?? "?",
    anker: i18n.tCode("ms", anker ?? "", anker ?? ""),
    datum: kurzDatum(ankerTag ?? "", i18n.lang),
    bereich: i18n.tCode("plpScope", bereich ?? "", bereich ?? ""),
    quelle: i18n.tCode("plpSource", quelle ?? "", quelle ?? ""),
  }) + (wortlaut ? ` · „${wortlaut}“` : "");
}

/* ── Die Zeitachse ──────────────────────────────────────────────────────── */

/**
 * Gehoert diese Spur zur gewaehlten Sendung?
 *
 * Die Sendung selbst und ihre Bestellungen -- mehr nicht. Ohne
 * Auswahl gehoert alles dazu: ein Filter, der nichts wegfiltert, ist
 * keiner.
 *
 * DIESELBE FRAGE WIE VORHER, EINE ANDERE ANTWORT DARAUF. Bis zur
 * CONSOLIDATION 002 entschied sie, was BETONT wird; seit der
 * Praxisabnahme entscheidet sie, was ueberhaupt DASTEHT.
 */
/**
 * DIE AUSWAHL OBEN -- drei Zustaende, kein vierter.
 *
 *   null     kein Filter: alles steht da
 *   <Zahl>   genau diese Sendung und ihre Bestellungen
 *   "ohne"   die Bestellungen OHNE aktive Sendungszuordnung
 *
 * `"ohne"` IST EINE ANSICHTSGRUPPE UND KEINE SENDUNG. Es entsteht
 * keine Spur dafuer, keine Zeile in der Datenbank und keine
 * Zuordnung. Wer sie waehlt, sieht Bestellungen -- nicht eine
 * Lieferung, die es nicht gibt.
 */
type Auswahl = number | "ohne" | null;

function gehoertZu(l: TimelineLane, gewaehlt: Auswahl): boolean {
  if (gewaehlt === null) return true;
  if (gewaehlt === "ohne") {
    return l.kind === "purchase_order" && l.consolidationId === null;
  }
  return (l.kind === "consolidation" && l.id === gewaehlt)
    || (l.kind === "purchase_order" && l.consolidationId === gewaehlt);
}

/**
 * Eine Zeile der Achse -- eine Spur oder eine Gruppenueberschrift.
 *
 * DER BEFUND DER BROWSER-ABNAHME (§8A.3). PO70305 hat KEINE
 * Sendungszuordnung und stand in der ungefilterten Achse direkt unter
 * den Bestellungen von CN-4737. Aus der Reihenfolge allein las sich
 * das wie Zugehoerigkeit -- und war das Gegenteil davon.
 *
 * Die Sendungen brauchen keine Ueberschrift: ihre eigene Spur ist
 * eine. Die Unzugeordneten haben keine, also bekommen sie eine
 * eigene Zeile, die gleich aussieht und keine Spur ist.
 *
 * SIE BELEGT IN BEIDEN SPALTEN EINE ZEILE. Die Beschriftungsspalte
 * und der Zeitkoerper lesen dieselbe Liste; eine Ueberschrift nur
 * links wuerde das Raster um eine Zeilenhoehe verschieben.
 */
type Achsenzeile =
  | { readonly art: "spur"; readonly lane: TimelineLane }
  | { readonly art: "gruppe"; readonly titel: string };

function Zeitachse({ daten, stufe, i18n, onLane, onEvent, gewaehlt = null,
                    onChinaPflege, chinaOffen = false }: {
  daten: TimelineResponse; stufe: Stufe; i18n: I18n;
  /** Der Weg in die China-Pflege -- fehlt er, steht kein Stift da. */
  onChinaPflege?: (() => void) | undefined;
  /**
   * Ob die Pflege gerade aufgeklappt ist.
   *
   * Der Stift ist ein UMSCHALTER und kein Oeffner: derselbe Klick
   * schliesst wieder. Damit er sagen kann, in welchem Zustand er
   * steht, muss er ihn kennen.
   */
  chinaOffen?: boolean;
  onLane: (lane: TimelineLane) => void;
  onEvent: (lane: TimelineLane, e: TimelineEvent) => void;
  /**
   * Die oben gewaehlte Sendung -- und sie FILTERT.
   *
   * UMGEDREHT GEGENUEBER DER CONSOLIDATION 002. Dort stand: „KEIN
   * FILTER: wer eine Sendung waehlt, will die anderen nicht
   * verlieren." Die produktive Nutzung hat das widerlegt.
   *
   * DER BEFUND: mit CN-03 gewaehlt blieben CN-02 und deren
   * Bestellungen ausgegraut stehen. Ausgegraut ist nicht fort -- die
   * Zeilen nehmen weiter Hoehe weg, die Achse muss weiter ihren
   * Zeitraum umspannen, und das Auge sucht trotzdem. Wer eine Sendung
   * waehlt, arbeitet AN IHR.
   *
   * Die Auswahl oben zeigt weiter ALLE Sendungen -- dort wechselt
   * man. Es ist ein Ansichtsfilter der Arbeitsflaeche, keine
   * Einschraenkung des Bestands.
   */
  gewaehlt?: Auswahl;
}): React.JSX.Element {
  const t = i18n.t;
  const px = STUFEN[stufe];
  const scroller = useRef<HTMLDivElement | null>(null);

  /*
   * ── DIE SPUREN DIESER ANSICHT ──────────────────────────────────
   *
   * Jede Sendung mit ihren Bestellungen darunter, die ungeklammerten
   * zuletzt -- und bei getroffener Auswahl AUSSCHLIESSLICH die
   * gewaehlte Sendung samt ihren Bestellungen.
   *
   * SIE STEHEN HIER OBEN, WEIL ALLES ANDERE AUF IHNEN AUFBAUT. Das
   * Zeitfenster, die Flusslinien und die Hoehe lesen sie; berechnete
   * man das Fenster noch aus `daten.lanes`, umspannte die Achse
   * weiterhin den Zeitraum der ANDEREN Sendung, und die Ereignisse
   * der gewaehlten draengten sich in einem Bruchteil der Breite --
   * genau der Eindruck, der in der Abnahme als „zusammengedraengt"
   * beschrieben wurde.
   *
   * DIE REIHENFOLGE IST FACHLICH UND NICHT ALPHABETISCH: die Sendung
   * IST die Lieferung, die Bestellungen sind ihre Bestandteile. Eine
   * Bestellung ohne Sendung behaelt ihren Platz am Ende -- sie
   * gehoert zu keiner Lieferung, und das soll man sehen.
   *
   * WELCHE Spuren es gibt und welche Ereignisse darin liegen,
   * entscheidet weiterhin `zeitachse.ts`. Hier wird ausgewaehlt und
   * geordnet, nicht gerechnet.
   */
  const zeilenliste = useMemo<readonly Achsenzeile[]>(() => {
    const sichtbar = daten.lanes.filter((l) => gehoertZu(l, gewaehlt));
    const cns = sichtbar.filter((l) => l.kind === "consolidation");
    const pos = sichtbar.filter((l) => l.kind === "purchase_order");
    const raus: Achsenzeile[] = [];
    for (const c of cns) {
      raus.push({ art: "spur", lane: c });
      for (const p of pos) {
        if (p.consolidationId === c.id) raus.push({ art: "spur", lane: p });
      }
    }
    const ohne = pos.filter((p) => p.consolidationId === null);
    if (ohne.length > 0) {
      /*
       * DIE UEBERSCHRIFT STEHT NUR DA, WO SIE GEBRAUCHT WIRD.
       *
       * Ist „Nicht zugeordnet" GEWAEHLT, stehen ohnehin nur diese
       * Bestellungen in der Achse -- eine Ueberschrift darueber
       * wiederholte die Auswahl oben. Sie trennt in der
       * UNGEFILTERTEN Ansicht, und dort ist sie noetig.
       */
      if (gewaehlt === null) raus.push({ art: "gruppe", titel: t("plp.unassigned") });
      for (const p of ohne) raus.push({ art: "spur", lane: p });
    }
    return raus;
  }, [daten.lanes, gewaehlt, t]);
  /* Nur die Spuren -- fuer alles, was rechnet statt darzustellen. */
  const spuren = useMemo(
    () => zeilenliste.flatMap((z) => (z.art === "spur" ? [z.lane] : [])), [zeilenliste]);

  /*
   * DAS FENSTER FOLGT DEN DATEN -- mit HEUTE als festem Bezugspunkt.
   *
   * Vier Wochen Vergangenheit reichen, um zu sehen, was liegen blieb;
   * nach vorn wird gezeigt, was da ist, mindestens aber ein Vierteljahr.
   * Ein fester Ausschnitt waere bei der ersten weit entfernten
   * Bestellung falsch.
   */
  const { von, bis } = useMemo(() => {
    const tage = spuren.flatMap((l) => l.events.flatMap((e) => [e.from, e.to]));
    const frueh = tage.length === 0 ? daten.today : tage.slice().sort()[0]!;
    const spaet = tage.length === 0 ? daten.today : tage.slice().sort().at(-1)!;
    /*
     * ── DER ANFANG LIEGT AUF DEM MONATSERSTEN ──────────────────────
     *
     * DER BEFUND DER ABNAHME: die Achse begann korrekt am 16.06., der
     * Benutzer las aber „Juli". Grund ist die Beschriftung: eine
     * Monatsmarke wird nur aufgenommen, wenn sie NICHT vor dem
     * Achsenbeginn liegt. Der 01.06. liegt vor dem 16.06. und fiel
     * weg -- die erste sichtbare Marke war der 01.07.
     *
     * Die zweieinhalb Wochen davor WAREN da und scrollbar. Sie trugen
     * nur keinen Namen, und was keinen Namen traegt, liest niemand.
     *
     * DIE ABRUNDUNG LOEST BEIDES MIT EINER ZEILE: der Monat, in dem
     * das erste Ereignis liegt, beginnt vollstaendig, seine Marke
     * steht da, und das erste Ereignis klebt nicht mehr an der Kante.
     *
     * KEINE ZWEITE ZAHL. Ein fester Vorlauf von „einer Woche" waere
     * ein Wert, den jemand pflegen muss und der bei der naechsten
     * Massstabsstufe wieder falsch ist. Die Monatsgrenze ist bereits
     * da -- sie ist dasselbe Raster, nach dem die Achse beschriftet
     * wird.
     */
    const anfang = [plusTage(daten.today, -28), frueh].sort()[0]!;
    /*
     * ── DAS ENDE LIEGT HINTER DEM LETZTEN EREIGNIS ───────────────
     *
     * DER BEFUND DER BROWSER-ABNAHME (§4.3). Die Achse endete GENAU
     * am spaetesten Termin. Wer eine Lieferung fuer Juni 2027
     * erwartet, sah Juni 2027 als Schlusskante -- und konnte dahinter
     * nichts mehr planen, obwohl genau dort die naechste Bestellung
     * liegt.
     *
     * HOMEBASE FUEHRT LANGE VORLAEUFE: Toolings, Sonderanfertigungen,
     * eigene Formen. Ein Horizont, der am letzten bekannten Datum
     * aufhoert, taugt fuer kurze Bestellungen und fuer sonst nichts.
     *
     * DREI MONATE HINTER DEM SPAETESTEN EREIGNIS, aufgerundet auf
     * den Monatsletzten -- dieselbe Logik wie am Anfang, nur
     * andersherum. Keine feste Jahreszahl, keine Obergrenze: der
     * Horizont waechst mit den Daten.
     *
     * UND NIE WENIGER ALS ZWOELF MONATE -- siehe unten.
     */
    /*
     * ── UND MINDESTENS EIN JAHR, IMMER ─────────────────────────────
     *
     * DER BEFUND DER BROWSER-ABNAHME 002 (Abschnitt 4). Die
     * Monatsansicht endete bei Maerz 2027. Fuer greenius ist das zu
     * kurz: Toolings, Sonderanfertigungen und Produktionsvorlaeufe
     * reichen weiter, und seit der China-Spur muss das Neujahrsfest
     * des FOLGEJAHRES schon im Oktober sichtbar sein.
     *
     * DIE REGEL IST DAMIT EIN MAXIMUM AUS ZWEI GRUENDEN:
     *   das spaeteste Ereignis plus Puffer  -- der Horizont waechst
     *                                          mit den Daten
     *   heute plus zwoelf Monate            -- der Horizont faellt
     *                                          nie darunter
     *
     * KEINE FESTE JAHRESZAHL. Beide Enden sind auf den Monatsletzten
     * aufgerundet -- dieselbe Logik wie am Anfang, nur andersherum,
     * und dasselbe Raster, nach dem die Achse beschriftet wird.
     */
    const monatsEnde = (iso: string, plus: number): string => {
      const m = new Date(`${iso.slice(0, 7)}-01T00:00:00Z`);
      m.setUTCMonth(m.getUTCMonth() + plus + 1);
      return alsIso(m.getTime() - TAG_MS);
    };
    return {
      von: `${anfang.slice(0, 7)}-01`,
      bis: [monatsEnde(daten.today, 12), monatsEnde(spaet, 3)].sort().at(-1)!,
    };
    /*
     * DIE ABHAENGIGKEITEN NENNEN `spuren` UND NICHT `daten`.
     *
     * EIN BEFUND AUS DEM VORBLOCK, den ich selbst hinterlassen habe:
     * seit die Auswahl filtert, liest diese Rechnung `spuren` -- die
     * Liste stand aber nicht in den Abhaengigkeiten. Ein Wechsel der
     * Sendung aenderte die sichtbaren Spuren und NICHT das Fenster;
     * die Achse behielt den Zeitraum der vorigen Auswahl, bis
     * irgendetwas anderes die Daten neu lud.
     */
  }, [spuren, daten.today]);

  const breite = Math.max(1, tageZwischen(von, bis) + 1) * px;
  const x = useCallback((iso: string) => tageZwischen(von, iso) * px, [von, px]);

  /* „Heute" springt zu HEUTE und laesst zwei Drittel der Breite nach
     vorn: geplant wird vorwaerts, nicht rueckwaerts. Das ist der
     Knopf, und er bleibt, wie er war. */
  const zuHeute = useCallback(() => {
    const el = scroller.current;
    if (el === null) return;
    el.scrollLeft = Math.max(0, x(daten.today) - el.clientWidth / 3);
  }, [x, daten.today]);

  /*
   * ── DER ANFANGSSTAND SCHNEIDET NICHTS AB ───────────────────────
   *
   * DER BEFUND DER BROWSER-ABNAHME (§8). Beim Oeffnen stand die Achse
   * auf „heute minus ein Drittel der Breite". Liegt das frueheste
   * Ereignis dichter an heute, als dieses Drittel breit ist, faellt
   * es nach links aus dem Bild -- auf dem Schirm stand „estellt"
   * statt „Bestellt".
   *
   * DAS WAR KEIN FEHLER DER RECHNUNG, SONDERN IHRE FOLGE. Die
   * Position stimmte; sie kannte nur die Daten nicht, die links von
   * ihr lagen.
   *
   * BEIM OEFFNEN WIRD DESHALB NICHT WEITER NACH RECHTS GESCROLLT ALS
   * BIS ZUM ERSTEN EREIGNIS. Es bleibt sichtbar, und dahinter gilt
   * weiterhin die Drittel-Regel.
   *
   * „HEUTE" BLEIBT UNVERAENDERT. Wer den Knopf drueckt, will zu
   * heute -- und nimmt dafuer in Kauf, dass Frueheres links
   * hinausfaellt. Das ist eine Absicht und kein Zufall, und genau
   * darin unterscheiden sich die beiden Faelle.
   */
  const anfangsstand = useCallback(() => {
    const el = scroller.current;
    if (el === null) return;
    const nachHeute = Math.max(0, x(daten.today) - el.clientWidth / 3);
    const tage = spuren.flatMap((l) => l.events.map((e) => e.from)).sort();
    const erstes = tage[0];
    if (erstes === undefined) { el.scrollLeft = nachHeute; return; }
    /* Ein Rand von 24 px, damit die Marke nicht an der Kante klebt.
       Die Zahl steht hier und nicht als Marke: sie ist ein
       Scrollabstand, keine Gestaltung. */
    const nachErstem = Math.max(0, x(erstes) - 24);
    /*
     * ── ABER HEUTE BLEIBT IM BILD ──────────────────────────────────
     *
     * DER BEFUND DER BROWSER-ABNAHME 002 (Abschnitt 5). In Wochen
     * und Monaten stand die gruene Heute-Linie beim Oeffnen da; in
     * der TAGESANSICHT nicht. Der Grund ist derselbe Satz, der das
     * erste Ereignis rettet: ein Tag ist dort 30 px breit, und
     * zwischen dem ersten Ereignis und heute liegen leicht mehr
     * Pixel, als der Bereich breit ist. Die Achse stand dann korrekt
     * am Anfang der Daten -- und heute lag weit rechts ausserhalb.
     *
     * BEIDES GEHT NUR, SOLANGE BEIDES HINEINPASST. Passt es nicht,
     * gewinnt HEUTE: wer die Achse oeffnet, will wissen, wo er
     * steht, nicht wo es anfing. Das Frueheste bleibt erreichbar --
     * es ist nur nicht mehr der Startpunkt.
     */
    const passtBeides = x(daten.today) < nachErstem + el.clientWidth;
    el.scrollLeft = passtBeides ? Math.min(nachHeute, nachErstem) : nachHeute;
  }, [x, daten.today, spuren]);
  /*
   * ── UND ER GREIFT BEIM OEFFNEN UND BEIM MASSSTABSWECHSEL ────────
   *
   * Abschnitt 5 ausdruecklich: NICHT bei jeder Datenaktualisierung
   * zurueckspringen. Vorher hing dieser Effekt an `anfangsstand` und
   * damit an `spuren` -- jede Aenderung an irgendeinem Termin riss
   * die Ansicht an den Anfang zurueck, mitten im Lesen.
   *
   * ER GREIFT AN GENAU ZWEI STELLEN:
   *
   *   beim MASSSTABSWECHSEL  die Pixel je Tag aendern sich, und ein
   *                          beibehaltener Scrollwert zeigt danach
   *                          auf einen anderen Tag
   *   beim ERSTEN DATENEINGANG  die Achse rendert, bevor die Termine
   *                          da sind; ohne diesen Fall stuende sie
   *                          danach auf einer Position, die die
   *                          Daten nicht kannte -- und das erste
   *                          Ereignis fiele wieder links hinaus
   *
   * UND SONST NIE. Der Schluessel merkt sich, wofuer zuletzt
   * gestellt wurde; eine spaetere Aenderung an irgendeinem Termin
   * laesst die Ansicht stehen, wo der Benutzer sie hat.
   */
  /*
   * ══════════════════════════════════════════════════════════════════
   * DAS MAUSRAD GEHOERT DER SEITE (Re-Abnahme 004, Abschnitt 2)
   * ══════════════════════════════════════════════════════════════════
   *
   * HIER STAND EIN ZUHOERER, UND ER IST FORT.
   *
   * Er uebersetzte das senkrechte Mausrad in waagerechtes Scrollen der
   * Achse. Technisch tat er, was er sollte; in der Hand war er falsch.
   * Unter der Zeitachse liegen weitere Bloecke -- die Arbeitsliste,
   * das Konsolidierungsfenster, die Aufmerksamkeitspunkte. Wer mit dem
   * Zeiger ueber der Achse das Rad dreht, will die SEITE bewegen und
   * nicht die Achse. Ein Bereich, der das Rad an sich zieht, macht die
   * halbe Seite unerreichbar, sobald der Zeiger zufaellig darueber
   * steht.
   *
   * WAS DIE ACHSE STATTDESSEN HAT, STEHT GLEICH DARUNTER: mit der
   * Maus wird gezogen. Das ist die ausdrueckliche Hauptbedienung, und
   * sie nimmt niemandem etwas weg.
   *
   * WAS WEITERHIN OHNE EINE ZEILE CODE FUNKTIONIERT: waagerechtes
   * Trackpad-Wischen und echtes waagerechtes Geraetescrollen. Beides
   * meldet `deltaX`, und das wendet der Browser von selbst auf einen
   * `overflow-x`-Bereich an -- dafuer brauchte es nie einen Zuhoerer.
   * Genau deshalb kostet das Entfernen auch nichts.
   */

  /* Welches Ereignis gerade aufgeschlagen ist -- `null` heisst: keines.
     Die Achse bleibt ruhig und kompakt; die Metadaten stehen im Detail
     und nicht in der Zeile. */
  const [chinaDetail, setChinaDetail] = useState<ChinaEvent | null>(null);

  /*
   * ══════════════════════════════════════════════════════════════════
   * AN DER ZEITACHSE GREIFEN UND ZIEHEN (Abschnitt 4 bis 8)
   * ══════════════════════════════════════════════════════════════════
   *
   * EINE ERGAENZUNG, KEIN ERSATZ. Trackpad, Mausrad, Tastatur und der
   * Knopf „Heute" bleiben unveraendert -- wer die Achse schon bedienen
   * konnte, bedient sie weiterhin genauso.
   *
   * AUF FREIER FLAECHE, NICHT AUF DEN DINGEN DARIN. Ein Zug beginnt
   * nur dort, wo nichts anderes liegt. Ueber einer Bestellzeile, einem
   * Ereignis oder einem China-Fenster gehoert der Druck dem Element;
   * wer dorthin zielt, will es anklicken und nicht die Achse
   * verschieben.
   *
   * UND EINE SCHWELLE, WEIL HAENDE ZITTERN. Drei Pixel sind noch ein
   * Klick. Ohne Schwelle wuerde jeder etwas unruhige Klick zum Zug --
   * und der Klick danach unterschlagen. Erst jenseits der Schwelle
   * gilt es als Zug, und dann wird der folgende Klick EINMAL
   * abgefangen: sonst oeffnete ein Zug, der ueber einer Zeile endet,
   * deren Akte.
   *
   * DER ZEIGER WIRD NUR IM BEREICH GEFANGEN. `setPointerCapture` auf
   * dem Scrollbereich selbst -- damit folgt der Zug auch, wenn die
   * Maus kurz hinausgeraet, und endet trotzdem sauber. Nichts haengt
   * am Fenster oder am Dokument; das senkrechte Scrollen der Seite
   * bleibt unberuehrt.
   */
  const zug = useRef<{ x: number; stand: number; echt: boolean } | null>(null);
  const [zieht, setZieht] = useState(false);
  const SCHWELLE = 3;

  const zugStart = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    const el = scroller.current;
    if (el === null) return;
    /* Alles, was selbst etwas tut, behaelt seinen Druck. */
    if ((e.target as HTMLElement).closest(
      "button, a, input, select, textarea, label, [role=button]") !== null) return;
    zug.current = { x: e.clientX, stand: el.scrollLeft, echt: false };
  }, []);

  const zugBewegt = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const z = zug.current;
    const el = scroller.current;
    if (z === null || el === null) return;
    const weg = e.clientX - z.x;
    if (!z.echt) {
      if (Math.abs(weg) < SCHWELLE) return;
      z.echt = true;
      setZieht(true);
      /* Erst JETZT den Zeiger fangen -- vorher war es vielleicht ein
         Klick, und ein gefangener Zeiger verschluckt ihn. */
      try { el.setPointerCapture(e.pointerId); } catch { /* ohne Fang geht es auch */ }
    }
    el.scrollLeft = z.stand - weg;
  }, []);

  const zugEnde = useCallback((e: React.PointerEvent<HTMLDivElement>): void => {
    const z = zug.current;
    zug.current = null;
    if (z === null) return;
    const el = scroller.current;
    if (el !== null) {
      try { el.releasePointerCapture(e.pointerId); } catch { /* war nie gefangen */ }
    }
    if (!z.echt) return;
    setZieht(false);
    /* Der Klick, der auf das Loslassen folgt, gehoert zum Zug und
       nicht zu dem, worueber er endet. Genau einer wird abgefangen. */
    const schlucken = (k: MouseEvent): void => {
      k.stopPropagation(); k.preventDefault();
    };
    el?.addEventListener("click", schlucken, { capture: true, once: true });
    window.setTimeout(() => {
      el?.removeEventListener("click", schlucken, { capture: true });
    }, 0);
  }, []);

  const gestellt = useRef("");
  useEffect(() => {
    /*
     * ── UND BEIM WECHSEL DER LIEFERUNG ZENTRIERT SIE AUF HEUTE ────
     *
     * DER BEFUND DER BROWSER-RE-ABNAHME (Abschnitt 1). In der
     * Gesamtuebersicht und beim Massstabswechsel stand die
     * Heute-Linie da; beim Wechsel des Lieferungsfilters nicht. Der
     * Grund: der Wechsel baut die Achse neu auf -- andere Spuren,
     * anderes Zeitfenster, andere Breite --, aber der Scrollwert
     * blieb stehen. Er zeigte danach auf einen anderen Tag, und bei
     * einer Sendung mit wenigen Terminen lag heute weit ausserhalb.
     *
     * ZWEI ANLAESSE, ZWEI ZIELE:
     *
     *   MASSSTAB oder ERSTER DATENEINGANG  -> `anfangsstand`
     *       Dort geht es darum, nichts abzuschneiden: das erste
     *       Ereignis bleibt sichtbar, solange heute dabei hineinpasst.
     *   LIEFERUNGSFILTER                   -> `zuHeute`
     *       Hier geht es um die Frage „wo stehe ich in DIESER
     *       Lieferung" -- und die beantwortet sich von heute aus.
     *       Die Skala bleibt, was sie war.
     *
     * UND DANACH NIE WIEDER. Der Schluessel merkt sich, wofuer
     * zuletzt gestellt wurde; wer anschliessend scrollt, bleibt, wo
     * er ist. Eine Datenaktualisierung aendert den Schluessel nicht.
     */
    const schluessel = `${stufe}|${String(gewaehlt)}|${spuren.length > 0}`;
    if (gestellt.current === schluessel) return;
    const vorher = gestellt.current.split("|");
    const nurFilter = vorher.length === 3 && vorher[0] === stufe
      && vorher[2] === String(spuren.length > 0);
    gestellt.current = schluessel;
    if (nurFilter) { zuHeute(); return; }
    anfangsstand();
  }, [stufe, gewaehlt, spuren.length, anfangsstand, zuHeute]);

  /* Monatsmarken statt eines Lineals mit 365 Strichen. */
  /*
   * ── DREI MASSSTAEBE, DREI BESCHRIFTUNGEN ───────────────────────
   *
   * DER BEFUND DER BROWSER-ABNAHME (§4). Die Umschalter aenderten
   * nur die Breite: „Tage" gewaehlt, und die Achse trug weiterhin
   * „Sept. 26" und „Okt. 26". Wer wissen wollte, WO in einem Monat
   * ein Ereignis liegt, konnte es nicht ablesen -- die Ansicht war
   * dieselbe, nur dreissigmal so breit.
   *
   * JEDER MODUS HAT JETZT SEINE EIGENE EINTEILUNG:
   *
   *   Tage    Tagesmarken in lesbarem Abstand -- nicht jeder Tag,
   *           sondern so viele, wie nebeneinander passen
   *   Wochen  Kalenderwochen, beginnend am Montag
   *   Monate  Monatsmarken wie bisher
   *
   * DER MONATSWECHSEL BLEIBT IN ALLEN DREI SICHTBAR und steht
   * staerker da als die Marken dazwischen: er ist der Anker, an dem
   * man sich orientiert, auch wenn die Tage danebenstehen.
   *
   * KEINE WAND AUS DREISSIG LABELS. Der Abstand wird aus der
   * verfuegbaren Breite gerechnet: `px` ist die Breite eines Tages,
   * und eine Beschriftung braucht rund 56 Pixel. Was nicht
   * hineinpasst, wird uebersprungen -- und zwar in glatten
   * Schritten, damit die Abstaende gleich bleiben.
   */
  const marken = useMemo(() => {
    const raus: { iso: string; label: string; stark: boolean }[] = [];
    const monatsName = (d: Date): string => d.toLocaleDateString(i18n.lang,
      { month: "short", year: "2-digit", timeZone: "UTC" });

    if (stufe === "monat") {
      const d = new Date(`${von}T00:00:00Z`);
      d.setUTCDate(1);
      for (let i = 0; i < 400; i++) {
        const iso = d.toISOString().slice(0, 10);
        if (iso > bis) break;
        if (iso >= von) raus.push({ iso, label: monatsName(d), stark: true });
        d.setUTCMonth(d.getUTCMonth() + 1);
      }
      return raus;
    }

    /* Wie viele Einheiten liegen zwischen zwei Beschriftungen? */
    const PLATZ = 56;
    const einheit = stufe === "woche" ? 7 : 1;
    const proEinheit = px * einheit;
    const glatt = stufe === "woche" ? [1, 2, 4, 8, 13, 26] : [1, 2, 5, 10, 15, 30];
    const schritt = glatt.find((n) => n * proEinheit >= PLATZ) ?? glatt.at(-1)!;

    /* Die Wochenansicht beginnt am Montag -- eine Kalenderwoche, die
       mitten in der Woche anfaengt, ist keine. */
    const start = new Date(`${von}T00:00:00Z`);
    if (stufe === "woche") {
      const wochentag = (start.getUTCDay() + 6) % 7;
      start.setUTCDate(start.getUTCDate() - wochentag);
    }

    /*
     * DER ABSTAND WIRD GEMESSEN, NICHT GEZAEHLT.
     *
     * DER BEFUND DER EIGENEN PRUEFUNG. Ein gezaehlter Schritt reicht
     * nicht: der Monatsanfang bekommt IMMER eine Marke, und wenn er
     * neben eine Schrittmarke faellt, stehen zwei uebereinander. Mit
     * sechs Ueberlappungen sah die Achse aus wie ein Druckfehler.
     *
     * Gemerkt wird deshalb die letzte vergebene Stelle, und was ihr
     * zu nahe kommt, faellt aus -- der Monatsanfang gewinnt dabei,
     * weil er die grobe Orientierung traegt.
     */
    let zaehler = 0;
    let letzteStelle = Number.NEGATIVE_INFINITY;
    for (const d = new Date(start); ; d.setUTCDate(d.getUTCDate() + einheit), zaehler += 1) {
      const iso = d.toISOString().slice(0, 10);
      if (iso > bis) break;
      if (iso < von) continue;
      const monatsanfang = stufe === "tag"
        ? d.getUTCDate() === 1
        : d.getUTCDate() <= 7;
      if (zaehler % schritt !== 0 && !monatsanfang) continue;
      const stelle = tageZwischen(von, iso) * px;
      if (!monatsanfang && stelle - letzteStelle < PLATZ) continue;
      /* Faellt eine Schrittmarke direkt VOR einen Monatsanfang, weicht
         sie ihm -- nicht umgekehrt. */
      if (monatsanfang && raus.length > 0 && stelle - letzteStelle < PLATZ) raus.pop();
      raus.push({
        iso,
        label: monatsanfang && stufe === "tag" ? monatsName(d)
          : stufe === "woche"
            ? t("plp.calendarWeek", { n: String(kalenderwoche(d)) })
            : d.toLocaleDateString(i18n.lang,
                { day: "2-digit", month: "2-digit", timeZone: "UTC" }),
        stark: monatsanfang,
      });
      letzteStelle = stelle;
      if (raus.length > 400) break;
    }
    /*
     * DER ACHSENBEGINN TRAEGT IMMER EINE MARKE.
     *
     * DER BEFUND DER EIGENEN PRUEFUNGEN. In der Wochenstufe beginnt
     * das Raster am Montag -- und der liegt bis zu sechs Tage NACH
     * dem Achsenanfang. Die ersten Pixel trugen damit keinen Namen,
     * und was keinen Namen traegt, liest niemand. Genau diesen
     * Befund hatte die Abnahme schon einmal: der Monat, in dem das
     * erste Ereignis liegt, muss beschriftet sein.
     *
     * `von` ist durch die Fensterrechnung immer ein Monatserster --
     * die Marke ist also der Monatsname und keine Sonderform.
     */
    if (raus[0]?.iso !== von) {
      raus.unshift({ iso: von, label: monatsName(new Date(`${von}T00:00:00Z`)),
        stark: true });
    }
    return raus;
  }, [von, bis, i18n.lang, stufe, px, t]);

  /*
   * ── EINE ZEILENHOEHE, EINE QUELLE ──────────────────────────────
   *
   * DER BEFUND DER BROWSER-ABNAHME. Die Hoehe stand an DREI Stellen:
   * hier als Zahl, in `.plp__lanekopf` und in `.plp__lane` jeweils
   * als `46px`. Drei Werte, die dasselbe meinen -- und beim naechsten
   * Mal nicht mehr.
   *
   * Die Beschriftungsspalte und der Zeitkoerper sind zwei Spalten
   * EINES Rasters und muessen wie eine Tabelle wirken. Zwei
   * unabhaengig gepflegte Hoehen sind das Gegenteil davon: sie
   * stimmen so lange ueberein, bis jemand eine davon anfasst.
   *
   * DIE ZAHL STEHT JETZT NUR NOCH HIER und geht als Marke an das
   * Raster. Das CSS liest sie; es setzt sie nicht.
   */
  const ZEILE = 46;
  const LINEAL = 26;
/* Die Spuren beginnen eine Zeile tiefer, seit die China-Spur ueber
   ihnen liegt. Ohne das `+ 1` zeigten die Verbindungslinien auf die
   Zeile ueber ihrem Ereignis. */
  const laneY = (i: number): number => (i + 1) * ZEILE + ZEILE / 2;

  /*
   * DIE KONVERGENZ -- der „Order River".
   *
   * Von der Versandbereitschaft jeder Bestellung zu dem Cutoff ihrer
   * Consolidation. Gestrichelt, solange einer der beiden Termine ein
   * Forecast ist: eine durchgezogene Linie zwischen zwei Vermutungen
   * waere eine Zusage, die niemand gegeben hat.
   */
  const fluesse = useMemo(() => {
    /*
     * GERECHNET WIRD MIT DER ZEILENNUMMER, NICHT MIT DER SPURNUMMER.
     *
     * Seit es eine Gruppenueberschrift gibt, sind das zwei
     * verschiedene Zaehlungen: die Ueberschrift belegt eine Zeile und
     * ist keine Spur. Liefe die Linie weiter nach der Spurnummer,
     * saesse sie um eine Zeilenhoehe daneben -- und zwar nur in der
     * ungefilterten Ansicht, also genau dort, wo es niemand sucht.
     */
    const cnZeile = new Map<number, number>();
    zeilenliste.forEach((z, i) => {
      if (z.art === "spur" && z.lane.kind === "consolidation") cnZeile.set(z.lane.id, i);
    });
    const raus: { d: string; unsicher: boolean }[] = [];
    zeilenliste.forEach((z, i) => {
      if (z.art !== "spur") return;
      const l = z.lane;
      if (l.kind !== "purchase_order" || l.consolidationId === null) return;
      const zi = cnZeile.get(l.consolidationId);
      if (zi === undefined) return;
      const zielzeile = zeilenliste[zi];
      if (zielzeile === undefined || zielzeile.art !== "spur") return;
      const ready = l.events.find((e) => e.code === "ready_for_shipment");
      const ziel = zielzeile.lane.events.find((e) => e.code === "consolidation_cutoff")
        ?? zielzeile.lane.events.find((e) => e.code === "consolidation_ready");
      if (ready === undefined || ziel === undefined) return;
      const x1 = x(ready.to), y1 = laneY(i), x2 = x(ziel.from), y2 = laneY(zi);
      const mx = x1 + Math.max(16, (x2 - x1) / 2);
      raus.push({
        d: `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`,
        unsicher: ready.certainty === "forecast" || ziel.certainty === "forecast",
      });
    });
    return raus;
  }, [zeilenliste, x]);

  return (
    <section className="plp__achse" data-testid="plp-zeitachse">
      <div className="plp__achsekopf">
        <h3 className="plp__achsetitel">{t("plp.timeline")}</h3>
        <span className="plp__achsehinweis">{t("plp.timelineHint")}</span>
        <button type="button" className="btn btn--small" data-testid="plp-heute"
          onClick={zuHeute}>{t("plp.today")}</button>
      </div>
      <div className="plp__gitter"
        style={{ "--plp-zeile": `${ZEILE}px`,
                 "--plp-lineal": `${LINEAL}px` } as React.CSSProperties}>
        <div className="plp__lanekoepfe">
          <div className="plp__lanezeile plp__lanezeile--leer">
            <div className="plp__lanekopf plp__lanekopf--leer" />
          </div>
          {/*
            * ── DIE CHINA-SPUR STEHT OBEN ─────────────────────────
            *
            * Sie ist kein Vorgang und gehoert deshalb zu keiner
            * Gruppe: ueber den Sendungen, nicht zwischen ihnen. Die
            * Reihenfolge sagt die Ebene -- China ist der Rahmen, die
            * Sendung die Lieferung, die Bestellung der Vorgang.
            *
            * SIE IST KEIN KNOPF. Wie die Sendungszeile benennt sie,
            * was darunter liegt; gepflegt wird sie in ihrer eigenen
            * Karte unter der Achse.
            */}
          <div className="plp__lanezeile plp__lanezeile--china"
            data-testid="plp-chinazeile">
            <span className="plp__lanekopf plp__lanekopf--text plp__lanekopf--china">
              {/*
                * VERSALIEN, WEIL ES EINE ANDERE EBENE IST.
                *
                * „CHINA" steht nicht neben CN-6436 und PO79701 -- es
                * steht ueber ihnen. Die Schreibweise sagt das auf
                * einen Blick, ohne ein zweites Zeichen und ohne eine
                * zweite Farbe.
                */}
              <span className="plp__lanename plp__lanename--china">
                {t("china.track")}
              </span>
              {/*
                * UND HIER IST DER EINZIGE EINSTIEG IN DIE PFLEGE
                * (Abschnitt 12 bis 15).
                *
                * Die grosse Karte stand dauerhaft in der Uebersicht
                * und nahm Platz, den im Alltag die Zeitachse braucht
                * -- gepflegt wird sie selten. Der Zugang sitzt jetzt
                * dort, wo die Ebene steht: an ihrer Spur.
                *
                * EINE FUNKTION, EIN EINSTIEG. Einen zweiten
                * gleichwertigen Knopf gibt es nicht.
                */}
              {onChinaPflege !== undefined && (
                <button type="button" data-testid="plp-chinastift"
                  className={chinaOffen
                    ? "subnav__pencil subnav__pencil--on plp__stift plp__chinastift"
                      + " plp__chinastift--an"
                    : "subnav__pencil plp__stift plp__chinastift"}
                  /*
                   * EIN UMSCHALTER SAGT, WO ER STEHT -- und zwar
                   * nicht nur farblich. `aria-pressed` ist die
                   * Auskunft fuer Vorleseprogramme; die Abstufung
                   * daneben ist die fuer das Auge. Eine Farbe allein
                   * waere fuer beide zu wenig.
                   */
                  aria-pressed={chinaOffen}
                  title={t("props.edit")} aria-label={t("china.editAria")}
                  onClick={onChinaPflege}>
                  <span aria-hidden="true">{PENCIL}</span>
                </button>
              )}
            </span>
          </div>
          {zeilenliste.map((z) => z.art === "gruppe" ? (
            /*
              * DIE GRUPPENUEBERSCHRIFT -- eine Zeile, keine Spur.
              *
              * Sie sieht aus wie eine Sendungszeile und ist keine: kein
              * Knopf, kein Ziel, keine Kennung. Was sie leistet, ist
              * eine Trennung -- damit niemand aus der Reihenfolge
              * ableitet, dass die Bestellung darunter zur Sendung
              * darueber gehoert.
              */
            <div key="gruppe-ohne" className="plp__lanezeile plp__lanezeile--gruppe"
              data-testid="plp-lanegruppe">
              <span className="plp__lanegruppe">{z.titel}</span>
            </div>
          ) : ((l) => (
            /*
              * ── DER SENDUNGSNAME IST EINE UEBERSCHRIFT ───────────
              *
              * DER BEFUND DER BROWSER-ABNAHME (§5). Derselbe
              * sichtbare Text „CN-5308" stand an zwei Stellen und tat
              * Verschiedenes: oben unter „Lieferungen" waehlte er,
              * hier in der Achse sprang er in die Akte. Gleicher
              * Name, zwei Bedeutungen -- und keine davon ist
              * ablesbar.
              *
              * DIE SENDUNGSZEILE IST JETZT KEIN KNOPF MEHR. Sie
              * benennt die Gruppe, so wie „Nicht zugeordnet" ihre
              * benennt. In die Sendungsakte fuehrt der Knopf, der es
              * sagt: „Vollstaendige Sendungsakte" an der Auswahl
              * oben.
              *
              * DIE BESTELLNUMMER BLEIBT EIN KNOPF. Sie steht nur an
              * einer Stelle und hat nur eine Bedeutung -- die
              * Bestellakte. Es gibt nichts, womit sie verwechselt
              * werden koennte.
              */
            <div key={`${l.kind}${l.id}`}
              className={[
                "plp__lanezeile",
                l.kind === "consolidation" ? "plp__lanezeile--cn" : null,
              ].filter(Boolean).join(" ")}>
              {l.kind === "consolidation" ? (
                <span className="plp__lanekopf plp__lanekopf--cn plp__lanekopf--text"
                  data-testid="plp-lanekopf" data-lane={`${l.kind}:${l.id}`}>
                  <span className="plp__lanename">{l.label}</span>
                  {l.supplierName !== null && (
                    <span className="plp__lanesupplier">{l.supplierName}</span>
                  )}
                </span>
              ) : (
                <button type="button" className="plp__lanekopf"
                  data-testid="plp-lanekopf" data-lane={`${l.kind}:${l.id}`}
                  onClick={() => onLane(l)}>
                  <span className="plp__lanename">{l.label}</span>
                  {l.supplierName !== null && (
                    <span className="plp__lanesupplier">{l.supplierName}</span>
                  )}
                </button>
              )}
              {/*
                * DER INHALTSKNOPF IST FORT, UND ZWAR ERSATZLOS.
                *
                * Er war der einzige Weg zum Lieferinhalt und damit ein
                * versteckter Hauptweg. Seit die Auswahl oben die
                * Sendung fuehrt, folgt der Inhalt ihr von selbst --
                * zwei Bedienwege fuer dieselbe Sache waeren einer zu
                * viel.
                */}
            </div>
          ))(z.lane))}
        </div>

        {/*
          * DER ZEITKOERPER IST EIN SCROLLBEREICH -- und muss einer
          * bleiben, auch ohne sichtbaren Balken.
          *
          * `tabindex` macht ihn mit der Tastatur erreichbar; ohne ihn
          * waere der Balken fort UND der Inhalt rechts davon nicht
          * mehr zu erreichen. Ein Name sagt einem Vorleseprogramm,
          * worin man sich dann bewegt.
          */}
        <div className={zieht ? "plp__scroller plp__scroller--zieht" : "plp__scroller"}
          ref={scroller} data-testid="plp-scroller" data-zieht={zieht ? "ja" : "nein"}
          tabIndex={0} role="group" aria-label={t("plp.timelineScroll")}
          onPointerDown={zugStart} onPointerMove={zugBewegt}
          onPointerUp={zugEnde} onPointerCancel={zugEnde}>
          <div className="plp__flaeche" style={{ width: `${breite}px` }}>
            <div className="plp__lineal">
              {/* Der Monatswechsel steht staerker da als die Marken
                  dazwischen -- er ist der Anker, auch wenn Tage
                  danebenstehen. */}
              {marken.map((m) => (
                <span key={m.iso}
                  className={m.stark ? "plp__monat plp__monat--stark" : "plp__monat"}
                  data-testid="plp-marke" data-stark={m.stark ? "ja" : "nein"}
                  style={{ left: `${x(m.iso)}px` }}>
                  {m.label}
                </span>
              ))}
            </div>
            {/* HEUTE steht fest im Bild und ist keine Beschriftung, sondern
                die Linie, an der man sich orientiert. */}
            <div className="plp__heute" data-testid="plp-heutelinie"
              style={{ left: `${x(daten.today)}px` }} />

            {/* Eine Zeile mehr, weil die China-Spur ueber den Spuren
                liegt -- sonst endete die Zeichenflaeche der
                Verbindungslinien vor der letzten Bestellung. */}
            <svg className="plp__fluss" width={breite}
                 height={(zeilenliste.length + 2) * ZEILE} aria-hidden="true">
              {fluesse.map((f, i) => (
                <path key={i} d={f.d} className={f.unsicher
                  ? "plp__flusslinie plp__flusslinie--unsicher" : "plp__flusslinie"} />
              ))}
            </svg>

            {/*
              * ── DIE CHINA-EREIGNISSE ──────────────────────────────
              *
              * EIN EREIGNIS IST EIN OBJEKT MIT DREI PHASEN und nicht
              * drei Objekte: Vorlauf, Kernereignis, Nachlauf. Der
              * Kern steht kraeftiger da als seine Raender -- er ist
              * die Tatsache, die Raender sind Erfahrung.
              *
              * KEINE WARNFARBE. Ein Feiertag ist kein Fehler. Rot,
              * Orange oder ein Ausrufezeichen hiessen „hier stimmt
              * etwas nicht"; richtig ist „hier ist etwas, das du
              * wissen solltest". Deshalb eine ruhige blaue Familie,
              * die neben dem HOMEBASE-Gruen steht, statt gegen es.
              */}
            <div className="plp__lane plp__lane--china" data-testid="plp-chinaspur">
              {daten.china.map((c) => {
                const vor = plusTage(c.coreStart, -c.leadInDays);
                const nach = plusTage(c.coreEnd, c.leadOutDays);
                const links = x(vor);
                const kern = x(c.coreStart) - links;
                const kernBreite = Math.max(2, x(c.coreEnd) - x(c.coreStart) + px);
                const ganz = Math.max(2, x(nach) - links + px);
                return (
                  <button key={c.id} type="button" className="plp__china"
                    data-testid="plp-chinaevent" data-china={String(c.id)}
                    style={{ left: `${links}px`, width: `${ganz}px` }}
                    title={chinaTitel(c, i18n)}
                    onClick={() => setChinaDetail(c)}>
                    {c.leadInDays > 0 && (
                      <span className="plp__chinarand" aria-hidden="true"
                        style={{ left: 0, width: `${kern}px` }} />
                    )}
                    <span className="plp__chinakern" data-testid="plp-chinakern"
                      aria-hidden="true"
                      style={{ left: `${kern}px`, width: `${kernBreite}px` }} />
                    {c.leadOutDays > 0 && (
                      <span className="plp__chinarand" aria-hidden="true"
                        style={{ left: `${kern + kernBreite}px`,
                                 width: `${Math.max(0, ganz - kern - kernBreite)}px` }} />
                    )}
                    {/* Der Name steht IM Objekt und kuerzt, wenn der
                        Platz nicht reicht -- ueberlappende Texte sind
                        schlimmer als ein gekuerzter. Vollstaendig
                        steht er im Titel und im Detail. */}
                    <span className="plp__chinaname">{c.name}</span>
                  </button>
                );
              })}
            </div>
            {zeilenliste.map((z) => z.art === "gruppe" ? (
              /* Die Ueberschrift belegt hier eine LEERE Zeile. Ohne
                 sie saesse jede Spur darunter um eine Zeilenhoehe
                 ueber ihrer Beschriftung. */
              <div key="gruppe-ohne" className="plp__lane plp__lane--gruppe"
                aria-hidden="true" />
            ) : ((l) => (
              <div key={`${l.kind}${l.id}`} data-lane={`${l.kind}:${l.id}`}
                className={[
                  "plp__lane",
                  l.kind === "consolidation" ? "plp__lane--cn" : null,
                ].filter(Boolean).join(" ")}>
                {l.events.map((e, idx) => (
                  <Ereignis key={e.code} e={e} x={x} i18n={i18n}
                    /*
                     * WIE VIEL PLATZ BIS ZUM NAECHSTEN EREIGNIS.
                     *
                     * In der Wochen- und Monatsstufe liegen Termine
                     * dicht beieinander; mit Beschriftung verschmolzen
                     * sie zu einem Block aus abgeschnittenen Woertern.
                     * Reicht der Platz nicht, bleibt die Marke und der
                     * Name steht im Titel -- lieber lesbar weniger als
                     * unlesbar mehr.
                     */
                    platz={idx + 1 < l.events.length
                      ? x(l.events[idx + 1]!.from) - x(e.from)
                      : Number.POSITIVE_INFINITY}
                    onClick={() => onEvent(l, e)} />
                ))}
              </div>
            ))(z.lane))}
          </div>
        </div>
      </div>
      {daten.lanes.length === 0 && (
        <p className="log__hint">{t("plp.noLanes")}</p>
      )}
      {/*
        * ── DAS DETAIL EINES CHINA-EREIGNISSES ──────────────────────
        *
        * Die Achse bleibt ruhig und kompakt; die Metadaten stehen
        * hier. Alles in die Zeile zu quetschen hiesse, eine
        * Planungsebene in eine Datenbankansicht zu verwandeln.
        *
        * GETRENNT GEHALTEN, WAS AUS GETRENNTEN QUELLEN STAMMT: der
        * Kernzeitraum ist das offizielle Datum, Vor- und Nachlauf
        * sind greenius-Erfahrungswerte. Der Status sagt, wie
        * belastbar das Kerndatum ist.
        */}
      {chinaDetail !== null && (
        <div className="log__group plp__chinadetail" data-testid="plp-chinadetail">
          <h4 className="log__grouptitle">
            {t("china.detailTitle")}
            <button type="button" className="btn btn--small"
              data-testid="plp-chinadetailzu"
              onClick={() => setChinaDetail(null)}>{t("common.close")}</button>
          </h4>
          <div className="log__rows">
            <div className="log__item"><span className="log__label">{t("china.name")}</span>
              <span className="log__vals">{chinaDetail.name}</span></div>
            <div className="log__item"><span className="log__label">{t("china.core")}</span>
              <span className="log__vals" data-testid="plp-chinakernzeit">
                {chinaDetail.coreStart === chinaDetail.coreEnd
                  ? kurzDatum(chinaDetail.coreStart, i18n.lang)
                  : `${kurzDatum(chinaDetail.coreStart, i18n.lang)} – `
                    + `${kurzDatum(chinaDetail.coreEnd, i18n.lang)}`}
                {` · ${i18n.tCode("chinaStatus", chinaDetail.sourceStatus)}`}</span></div>
            <div className="log__item"><span className="log__label">{t("china.leadIn")}</span>
              <span className="log__vals">{String(chinaDetail.leadInDays)}</span></div>
            <div className="log__item"><span className="log__label">{t("china.leadOut")}</span>
              <span className="log__vals">{String(chinaDetail.leadOutDays)}</span></div>
            {chinaDetail.sourceNote !== null && (
              <div className="log__item">
                <span className="log__label">{t("china.sourceNote")}</span>
                <span className="log__vals">{chinaDetail.sourceNote}</span></div>
            )}
            {chinaDetail.checkedOn !== null && (
              <div className="log__item">
                <span className="log__label">{t("china.checkedOn")}</span>
                <span className="log__vals">
                  {kurzDatum(chinaDetail.checkedOn, i18n.lang)}</span></div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * EIN PUNKT AUF DER ACHSE.
 *
 * Ein Forecast ist ein BAND von `from` bis `to` -- „5-6 Wochen" ist eine
 * Spanne, und sie auf die Mitte zu reduzieren waere eine Genauigkeit,
 * die niemand zugesagt hat. Abgeloeste Zusagen stehen blass an ihrem
 * urspruenglichen Datum; so liest man die Verschiebung, ohne eine
 * Auswertung zu oeffnen.
 */
/**
 * WELCHE GEWISSHEIT WELCHE FORM TRAEGT.
 *
 * Die Zuordnung steht EINMAL da und nicht in jeder Klassenliste
 * verstreut: `actual` ist eine Tatsache, `confirmed` eine Zusage,
 * `forecast` eine Rechnung. Kommt eine vierte Gewissheit hinzu, faellt
 * hier auf, dass sie noch keine Form hat.
 */
const ZUSTANDSFORM: Readonly<Record<string, string>> = {
  actual: "tatsaechlich",
  confirmed: "geplant",
  forecast: "gerechnet",
};

function Ereignis({ e, x, platz, i18n, onClick }: {
  e: TimelineEvent; x: (iso: string) => number;
  /** Pixel bis zum naechsten Ereignis derselben Spur. */
  platz: number;
  i18n: I18n; onClick: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const name = i18n.tCode("ms", e.code, e.code);
  const titel = [
    name,
    e.certainty === "forecast"
      ? `${kurzDatum(e.from, i18n.lang)} – ${kurzDatum(e.to, i18n.lang)}`
      : kurzDatum(e.from, i18n.lang),
    i18n.tCode("cert", e.certainty),
    basisText(e, i18n),
    e.deviationDays === null ? null
      : t("plp.deviation", { n: e.deviationDays > 0 ? `+${e.deviationDays}` : String(e.deviationDays) }),
    e.reasonNote,
    ...e.superseded.map((s) => t("plp.wasPromised", { datum: kurzDatum(s.happensOn, i18n.lang) })
      + (s.reasonNote === null ? "" : `: ${s.reasonNote}`)),
  ].filter(Boolean).join(" · ");

  const bandbreite = Math.max(10, x(e.to) - x(e.from));
  /* Beschriftet wird, wo sie hinpasst: sonst steht der Name im Titel. */
  /*
   * Ein Band wird nur beschriftet, wenn es breit genug ist UND bis zum
   * naechsten Ereignis Platz bleibt. Ein Cutoff, der mitten in einem
   * Forecast-Band liegt, ueberdeckt dessen Beschriftung sonst genau in
   * der Mitte des Wortes.
   */
  const beschriftet = e.certainty === "forecast"
    ? bandbreite >= 90 && platz >= 90
    : platz >= 80;

  return (
    <>
      {e.superseded.map((s, i) => (
        <span key={`v${i}`} className="plp__alt" style={{ left: `${x(s.happensOn)}px` }}
          data-testid="plp-abgeloest" title={titel} aria-hidden="true" />
      ))}
      <button type="button" onClick={onClick} title={titel}
        data-testid="plp-event" data-code={e.code} data-certainty={e.certainty}
        aria-label={titel}
        /*
          * DIE ZUSTANDSFORM KOMMT AUS DEM GEMEINSAMEN BAUSTEIN
          * (`styles/zustandsformen.css`) -- gefuellt ist eingetreten,
          * Kontur ist zugesagt, gestrichelt ist gerechnet. Was HIER
          * bleibt, ist nur Groesse und Anordnung.
          */
        className={[
          "plp__event", `plp__event--${e.certainty}`, "zustand",
          `zustand--${ZUSTANDSFORM[e.certainty] ?? "gerechnet"}`,
          beschriftet ? null : "plp__event--eng",
        ].filter(Boolean).join(" ")}
        style={e.certainty === "forecast"
          ? { left: `${x(e.from)}px`, width: `${bandbreite}px` }
          : { left: `${x(e.from)}px` }}>
        {beschriftet && <span className="plp__eventname">{name}</span>}
      </button>
    </>
  );
}

/**
 * ── WAS KOMMT MIT DIESER LIEFERUNG AN? ──────────────────────────────
 *
 * DIE UEBERSICHT WIRD NICHT NUR VON EINKAEUFERN GELESEN. Wer im
 * Lager, im Vertrieb oder in der Projektierung sitzt, hat eine
 * einzige Frage an eine ankommende Lieferung: WAS ist drin? Dafuer
 * soll niemand drei Bestellungen oeffnen, Lieferanten kennen oder
 * Bestellstruktur verstehen muessen.
 *
 * DESHALB STEHT HIER AUSDRUECKLICH WENIG: Artikelnummer,
 * Kurzbezeichnung, Menge. Keine Bestellnummer, kein Lieferant, kein
 * Preis, kein Termin. Jede dieser Angaben beantwortet eine ANDERE
 * Frage und hat ihren eigenen Ort -- die vollstaendige Akte ist einen
 * Klick entfernt und bleibt es.
 *
 * DIE ZUSAMMENFASSUNG GESCHIEHT IM FACHKERN (`lieferinhalt`), nicht
 * hier. Eine Oberflaeche, die Mengen addiert, ist eine zweite
 * Wahrheit neben der Datenbank.
 */
function Lieferinhalt({ id, nummer, i18n }: {
  id: number; nummer: string; i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const [ware, setWare] = useState<ConsolidationGoods | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  useEffect(() => {
    let lebt = true;
    setWare(null); setFehler(null);
    api.consolidationGoods(id)
      .then((w) => { if (lebt) setWare(w); })
      .catch((e) => { if (lebt) setFehler(e); });
    return () => { lebt = false; };
  }, [id]);

  return (
    <section className="log__group plp__ware" data-testid="plp-ware" data-cn={id}>
      <h4 className="log__grouptitle plp__waretitel">
        <span>{t("plp.goodsTitle")}</span>
        {/*
          * EIN ZIEL, EIN BEDIENWEG.
          *
          * Hier stand ein zweiter Knopf „Vollstaendige Sendungsakte" --
          * mit derselben Beschriftung und demselben Ziel wie der in der
          * Auswahlkarte oben. Zwei gleiche Wege auf einer Seite sind
          * genau die Verdopplung, die Teil B gerade abgebaut hat: wer
          * sie sieht, fragt sich, worin sie sich unterscheiden.
          *
          * KEINE ERSATZAKTION. Die Karte sagt, WAS ankommt; der Weg in
          * die Akte gehoert zur Auswahl, und dort steht er.
          */}
        <span className="plp__warecn" data-testid="plp-ware-cn">
          {t("plp.goodsFor", { cn: nummer })}</span>
      </h4>
      {fehler !== null ? <p className="log__error">{errorText(i18n, fehler)}</p>
        : ware === null ? <p className="log__hint">{t("common.loading")}</p>
        : ware.items.length === 0
          ? <p className="log__hint" data-testid="plp-ware-leer">{t("plp.goodsEmpty")}</p>
          : (
        <>
          {/* Eine freie Position traegt keine Artikelnummer -- dann steht
              dort, dass es keine gibt, und ihre Bezeichnung rueckt an die
              Stelle des Namens. Eine erfundene Nummer waere bequemer und
              falsch. */}
          <ul className="plp__wareliste">
            {ware.items.map((w: ConsolidationGoods["items"][number]) => (
              <li key={w.articleNumber ?? `frei:${w.shortDescription ?? ""}`}
                className="plp__warezeile"
                data-testid="plp-warezeile" data-artikel={w.articleNumber ?? ""}
                data-frei={w.articleNumber === null ? "ja" : "nein"}>
                <span className="plp__warenummer">
                  {w.articleNumber ?? <span className="plp__wareohne">{t("plp.lineFree")}</span>}
                </span>
                <span className="plp__waretext">{w.shortDescription ?? <Unknown />}</span>
                <span className="plp__waremenge" data-testid="plp-waremenge">
                  {/* Ganze Stueck ohne Nachkommastellen -- eine Menge
                      ist keine Waehrung. */}
                  {t("plp.goodsPieces", {
                    n: Number(w.quantity).toLocaleString(i18n.lang,
                      { maximumFractionDigits: 3 }) })}
                </span>
              </li>
            ))}
          </ul>
          {/* Dass zusammengefasst wurde, steht da -- sonst sieht die Zahl
              aus wie die einer einzelnen Bestellung. */}
          <p className="log__hint plp__warehinweis">{t("plp.goodsHint")}</p>
          {ware.linesWithoutArticle > 0 && (
            <p className="log__hint" data-testid="plp-ware-ohne">
              {t("plp.goodsNoArticle", { n: String(ware.linesWithoutArticle) })}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * ── DIE SENDUNGSAUSWAHL ─────────────────────────────────────────────
 *
 * WARUM KNOEPFE UND KEIN REITER-MECHANISMUS. Reiter tragen drei
 * Sendungen und zerfallen bei dreissig. Das hier ist eine Liste von
 * Schaltern, die umbricht: bei drei Sendungen sieht sie aus wie
 * Reiter, bei dreissig wie eine Liste -- und in beiden Faellen
 * funktioniert sie. Keine neue Navigationsarchitektur, kein Menue,
 * keine zweite Ebene.
 *
 * DIE SKALIERUNG IST GEPRUEFT UND NICHT GERATEN: gezeigt werden die
 * AKTIVEN Sendungen der Zeitachse, und die Zeitachse zeigt ohnehin
 * nur, was nicht verworfen ist. Produktiv sind das heute zwei.
 * Werden es viele, bricht die Zeile um -- sie wird laenger, nicht
 * kaputt.
 *
 * DIE NUMMER OEFFNET WEITERHIN DIE AKTE. Der Schalter waehlt aus, der
 * kleine Pfeil daneben fuehrt in die Sendungsakte -- dasselbe Muster
 * wie im Spurkopf.
 */
function CnWahl({ lanes, gewaehlt, i18n, onWahl, onAkte, onPo }: {
  lanes: readonly TimelineLane[]; gewaehlt: Auswahl; i18n: I18n;
  onWahl: (id: Auswahl) => void; onAkte: (id: number) => void;
  onPo: (id: number) => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  const cns = lanes.filter((l) => l.kind === "consolidation");
  /*
   * DIE BESTELLUNGEN OHNE AKTIVE SENDUNGSZUORDNUNG.
   *
   * DER BEFUND DER BROWSER-ABNAHME (§8A). PO70305 traegt keine
   * Zuordnung und stand trotzdem mitten zwischen den Bestellungen
   * einer Sendung.
   *
   * SIE BEKOMMEN EINEN GLEICHRANGIGEN KNOPF -- und nur dann, wenn es
   * sie gibt. Ein Knopf, der immer dasteht und meistens nichts
   * zeigt, waere eine Gruppe, die HOMEBASE behauptet.
   *
   * ES ENTSTEHT KEINE SENDUNG. Kein Datensatz, keine Nummer, keine
   * Zuordnung -- nur ein Filter auf das, was ohnehin da ist.
   */
  const ohne = lanes.filter(
    (l) => l.kind === "purchase_order" && l.consolidationId === null);
  if (cns.length === 0 && ohne.length === 0) return null;
  const aktiv = typeof gewaehlt === "number"
    ? cns.find((c) => c.id === gewaehlt) ?? null : null;
  /* Die Bestellungen DIESER Sendung -- aus denselben Spuren, keine
     zweite Abfrage. */
  const dazu = aktiv === null ? []
    : lanes.filter((l) => l.kind === "purchase_order" && l.consolidationId === aktiv.id);

  return (
    <section className="log__group plp__cnwahl" data-testid="plp-cnwahl">
      <h4 className="log__grouptitle">{t("plp.shipments")}</h4>
      <div className="plp__cnknoepfe" role="group" aria-label={t("plp.shipments")}>
        {cns.map((c) => (
          <button key={c.id} type="button"
            className={c.id === gewaehlt
              ? "plp__cnknopf plp__cnknopf--an" : "plp__cnknopf"}
            data-testid="plp-cnknopf" data-cn={c.id}
            aria-pressed={c.id === gewaehlt}
            onClick={() => onWahl(c.id === gewaehlt ? null : c.id)}>
            {c.label}
          </button>
        ))}
        {ohne.length > 0 && (
          <button type="button"
            className={gewaehlt === "ohne"
              ? "plp__cnknopf plp__cnknopf--an plp__cnknopf--ohne"
              : "plp__cnknopf plp__cnknopf--ohne"}
            data-testid="plp-cnknopf-ohne" aria-pressed={gewaehlt === "ohne"}
            onClick={() => onWahl(gewaehlt === "ohne" ? null : "ohne")}>
            {t("plp.unassigned")}
          </button>
        )}
      </div>
      {aktiv !== null && (
        <div className="plp__cnkopfzeile" data-testid="plp-cnwahlkopf">
          <span className="plp__cnwahlnummer">{aktiv.label}</span>
          <span className="plp__cnwahlsatz" data-testid="plp-cnwahlsatz">
            {[
              dazu.length === 0 ? null : t("plp.ordersCount", { n: String(dazu.length) }),
              aktiv.transportMode === null
                ? null : i18n.tCode("cnTransport", aktiv.transportMode),
              i18n.tCode("cnStatus", aktiv.status),
            ].filter((x) => x !== null && x !== "").join(" · ")}
          </span>
          {/* Die erwartete Anlieferung ist die eine Zahl, nach der hier
              gefragt wird -- sie steht deshalb im Kopf und nicht erst
              drei Karten weiter unten. */}
          {(() => {
            const eta = aktiv.events.find((e) => e.code === "eta_lueneburg");
            return eta === undefined ? null : (
              <span className="plp__cnwahleta" data-testid="plp-cnwahleta">
                {t("plp.etaLueneburg", { datum: kurzDatum(eta.from, i18n.lang) })}
              </span>
            );
          })()}
          <button type="button" className="btn btn--small plp__cnwahlakte"
            data-testid="plp-cnwahlakte"
            onClick={() => onAkte(aktiv.id)}>{t("plp.goodsOpenFile")}</button>
        </div>
      )}
      {aktiv !== null && dazu.length > 0 && (
        <ul className="plp__cnpos" data-testid="plp-cnwahlpos">
          {dazu.map((p) => (
            <li key={p.id} data-testid="plp-cnwahlpo" data-po={p.label}>
              <button type="button" className="plp__punktziel"
                onClick={() => onPo(p.id)}>{p.label}</button>
              {p.supplierName !== null && (
                <span className="plp__lanesupplier">{p.supplierName}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Die Dokumente einer Bestellung ──────────────────────────────────────── */

/** Die Beschriftung je Fach. Sie steht einmal da und gilt fuer beide Akten. */
const FACHNAME: Readonly<Record<BestelldokumentCode, "pod.po" | "pod.pi" | "pod.packing">> = {
  bestellung_po: "pod.po",
  proforma_pi: "pod.pi",
  packing_list: "pod.packing",
};

/** Die Datei selbst -- derselbe Endpunkt wie fuer Produktdokumente. */
function dateiAdresse(id: number): string {
  return `/api/v1/documents/${id}/content?disposition=inline`;
}

/**
 * WAS AN EINEM FACH DRANSTEHT -- aus Pflicht UND Vorhandensein.
 *
 * DER BEFUND DER BROWSER-ABNAHME (Abschnitt 18). Die Karte kannte nur
 * „fehlt" und „vorhanden" und hat deshalb an einer frisch angelegten
 * Bestellung dreimal gewarnt -- waehrend die Prozessfuehrung ueber
 * denselben Vorgang sagte, es sei alles in Ordnung. Zwei Anzeigen,
 * zwei Aussagen, ein Vorgang.
 *
 * „FEHLT" HEISST JETZT: ERFORDERLICH UND NICHT DA. Was im Ablauf noch
 * nicht an der Reihe ist, steht ruhig da und traegt kein Warnzeichen.
 *
 * WELCHE DER DREI ES IST, ENTSCHEIDET HIER NIEMAND. Die Faelligkeit
 * kommt als `obligation` aus dem Fachkern (`DOCUMENT_OBLIGATIONS`);
 * diese Funktion legt sie nur mit der Frage zusammen, ob etwas da
 * ist. Eine zweite Ableitung in der Oberflaeche gaebe irgendwann die
 * andere Antwort als die Prozessfuehrung -- und genau das war der
 * Befund.
 */
type DokStand = "da" | "fehlt" | "nochnicht";
const dokStand = (g: OrderDocumentGroup): DokStand =>
  g.current !== null ? "da"
    : g.obligation === "erforderlich" ? "fehlt" : "nochnicht";
const DOK_ZEICHEN: Record<DokStand, string> =
  { da: "\u2713", fehlt: "\u26A0", nochnicht: "\u00B7" };
const DOK_SATZ: Readonly<Record<DokStand, "pod.present" | "pod.missing" | "pod.notYet">> =
  { da: "pod.present", fehlt: "pod.missing", nochnicht: "pod.notYet" };

/*
 * ── DER DOKUMENTBEREICH DER BESTELLAKTE ─────────────────────────────
 *
 * DREI FAECHER, IMMER ALLE DREI -- auch die leeren. Ein fehlendes
 * Dokument ist eine AUSKUNFT und keine fehlende Zeile: nur wer sieht,
 * dass die Packing List fehlt, kann sie anfordern.
 *
 * ES ENTSTEHT KEINE DATEIABLAGE. Keine Ordner, keine Schlagworte,
 * keine freie Benennung des Fachs: drei feste Faecher, und was nicht
 * hineinpasst, gehoert nicht hierher.
 *
 * ABGELOEST, NICHT ERSETZT. Eine neue Fassung ist eine neue Zeile; die
 * alte bleibt vollstaendig erhalten und ist unter „Ältere Fassungen"
 * erreichbar. Es gibt hier bewusst keinen Loeschknopf.
 */
function Bestelldokumente({ poId, i18n, mayWrite }: {
  poId: number; i18n: I18n; mayWrite: boolean;
}): React.JSX.Element {
  const t = i18n.t;
  const [stand, setStand] = useState<OrderDocumentsResponse | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = useCallback(() => {
    api.orderDocuments(poId).then(setStand).catch(setFehler);
  }, [poId]);
  useEffect(() => { laden(); }, [laden]);

  const ablegen = useCallback(async (
    gruppe: BestelldokumentCode, datei: File, ersetzt: number | null,
  ): Promise<void> => {
    setLaeuft(gruppe); setFehler(null);
    try {
      const roh = new Uint8Array(await datei.arrayBuffer());
      let binaer = "";
      for (const b of roh) binaer += String.fromCharCode(b);
      setStand(await api.uploadOrderDocument(poId, {
        group: gruppe, fileName: datei.name, contentBase64: btoa(binaer),
        replacesId: ersetzt,
      }));
    } catch (e) { setFehler(e); } finally { setLaeuft(null); }
  }, [poId]);

  return (
    <section className="log__group plp__dok" data-testid="plp-podokumente">
      <h4 className="log__grouptitle">{t("pod.title")}</h4>
      <HilfeHinweis bereich="po.documents" />
      {fehler !== null && <p className="log__error">{errorText(i18n, fehler)}</p>}
      {stand === null ? <p className="log__hint">{t("doc.loading")}</p> : (
        <>
          <ul className="plp__dokliste">
            {stand.groups.map((g) => (
              <li key={g.group} className="plp__dokfach" data-testid="plp-dokfach"
                data-gruppe={g.group} data-stand={dokStand(g)}>
                <span className="plp__dokname">{t(FACHNAME[g.group])}</span>
                <span className={dokStand(g) === "fehlt"
                  ? "plp__dokstand plp__dokstand--fehlt"
                  : dokStand(g) === "nochnicht"
                    ? "plp__dokstand plp__dokstand--nochnicht" : "plp__dokstand"}
                  data-testid="plp-dokstand">
                  <span aria-hidden="true">{DOK_ZEICHEN[dokStand(g)]}</span>
                  {t(DOK_SATZ[dokStand(g)])}
                </span>
                <span className="plp__dokdatei">
                  {g.current !== null && (
                    <>
                      <a className="plp__dokziel" data-testid="plp-dokoeffnen"
                        href={dateiAdresse(g.current.id)} target="_blank" rel="noreferrer">
                        {g.current.fileName}
                      </a>
                      <span className="plp__dokmeta">
                        {g.current.documentDate !== null && (
                          <>{t("pod.documentDate", {
                            datum: kurzDatum(g.current.documentDate, i18n.lang) })}{" · "}</>
                        )}
                        {t("pod.uploadedOn", {
                          datum: kurzDatum(g.current.uploadedAt.slice(0, 10), i18n.lang) })}
                        {g.current.uploadedBy !== null && ` · ${g.current.uploadedBy}`}
                      </span>
                    </>
                  )}
                </span>
                {mayWrite && (
                  <label className="btn btn--small plp__dokknopf">
                    {laeuft === g.group ? t("pod.uploading")
                      : t(g.current === null ? "pod.upload" : "pod.replace")}
                    <input type="file" hidden accept="application/pdf"
                      data-testid={`plp-dokupload-${g.group}`}
                      disabled={laeuft !== null}
                      aria-label={t(g.current === null ? "pod.upload" : "pod.replace")}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f !== undefined) void ablegen(g.group, f, g.current?.id ?? null);
                      }} />
                  </label>
                )}
                {g.superseded.length > 0 && (
                  <details className="plp__dokalt" data-testid="plp-dokalt">
                    <summary>{t("pod.versions", { n: String(g.superseded.length) })}</summary>
                    <ul>
                      {g.superseded.map((v) => (
                        <li key={v.id} data-testid="plp-dokaltfassung">
                          <a href={dateiAdresse(v.id)} target="_blank" rel="noreferrer">
                            {v.fileName}</a>
                          <span className="plp__dokmeta">
                            {t("pod.uploadedOn", {
                              datum: kurzDatum(v.uploadedAt.slice(0, 10), i18n.lang) })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
          <p className="log__hint">{t("pod.supersededNote")}</p>
        </>
      )}
    </section>
  );
}

/**
 * ── DIESELBEN DOKUMENTE AN DER SENDUNG -- REFERENZIERT ──────────────
 *
 * Die Sendung zeigt, was an ihren Bestellungen haengt. Sie legt nichts
 * ab und kopiert nichts: es gibt hier keinen Hochladeknopf, und die
 * Schranke `pd_subject_chk` liesse eine zweite Ablage ohnehin nicht zu
 * -- ein Dokument hat genau einen Bezug.
 */
function Sendungsdokumente({ cnId, i18n }: {
  cnId: number; i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const [stand, setStand] = useState<ConsolidationDocumentsResponse | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  useEffect(() => {
    let lebt = true;
    api.consolidationDocuments(cnId)
      .then((d) => { if (lebt) setStand(d); })
      .catch((e) => { if (lebt) setFehler(e); });
    return () => { lebt = false; };
  }, [cnId]);

  return (
    <section className="log__group plp__dok" data-testid="plp-cndokumente">
      <h4 className="log__grouptitle">{t("pod.ofOrders")}</h4>
      {fehler !== null ? <p className="log__error">{errorText(i18n, fehler)}</p>
        : stand === null ? <p className="log__hint">{t("doc.loading")}</p>
        : stand.orders.length === 0
          ? <p className="log__hint">{t("pod.noOrders")}</p>
          : (
        <>
          <ul className="plp__dokpos">
            {stand.orders.map((o) => (
              <li key={o.purchaseOrderId} data-testid="plp-cndokpo" data-po={o.dteNumber}>
                <span className="plp__dokponummer">{o.dteNumber}</span>
                <span className="plp__dokfaecher">
                  {o.groups.map((g) => (
                    <span key={g.group} className={dokStand(g) === "fehlt"
                      ? "plp__dokmarke plp__dokmarke--fehlt"
                      : dokStand(g) === "nochnicht"
                        ? "plp__dokmarke plp__dokmarke--nochnicht" : "plp__dokmarke"}
                      data-testid="plp-cndokmarke" data-gruppe={g.group}
                      data-stand={dokStand(g)} title={t(DOK_SATZ[dokStand(g)])}>
                      <span aria-hidden="true">{DOK_ZEICHEN[dokStand(g)]}</span>
                      {g.current === null ? t(FACHNAME[g.group]) : (
                        <a className="plp__dokziel" href={dateiAdresse(g.current.id)}
                          target="_blank" rel="noreferrer">{t(FACHNAME[g.group])}</a>
                      )}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
          <p className="log__hint">{t("pod.cnReadOnly")}</p>
        </>
      )}
    </section>
  );
}

/* ── Arbeitsliste und Fenster ───────────────────────────────────────────── */

function Arbeitsliste({ daten, i18n, onSubject }: {
  daten: TimelineResponse; i18n: I18n;
  onSubject: (kind: "purchase_order" | "consolidation", id: number) => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  /*
   * ── OHNE PUNKTE KEINE KARTE ────────────────────────────────────
   *
   * UMGEDREHT GEGENUEBER DER BROWSER-ABNAHME 002. Dort hiess es: „der
   * leere Fall ist eine Auskunft, keine fehlende Liste." Das klang
   * richtig und war es im Einzelfall auch -- nur steht die Karte auf
   * JEDER Ansicht der Uebersicht, und in der Praxis ist sie meistens
   * leer.
   *
   * EINE UEBERSCHRIFT „Was braucht Aufmerksamkeit?" mit der Antwort
   * „Keine offenen Punkte." verbraucht genau so viel Platz und
   * Aufmerksamkeit wie eine mit echten Punkten. Wer sie zehnmal leer
   * gesehen hat, liest sie beim elften Mal nicht mehr -- und dann
   * steht dort etwas.
   *
   * WAS KEINE AUFMERKSAMKEIT BRAUCHT, BEKOMMT KEINE.
   */
  if (daten.attention.length === 0) return null;
  return (
    <section className="log__group plp__attention" data-testid="plp-attention">
      <h4 className="log__grouptitle">{t("plp.attention")}</h4>
      {/*
        * WAS HIER STEHT, IST ZU TUN -- und was nicht zu tun ist, steht nicht hier.
        *
        * DER BEFUND DER BROWSER-ABNAHME 002. Die Liste markierte ihre
        * Zeilen mit einem roten Dreieck und einer konturierten Raute.
        * Beides sagt nichts: ein Benutzer kann daraus weder lesen, ob
        * etwas gut oder schlecht ist, noch ob er handeln muss. Farbe und
        * Form allein sind keine Auskunft -- und wer farbenblind ist,
        * bekommt gar keine.
        *
        * Ab hier traegt jede Zeile ein Wort: DRINGEND oder OFFEN. Das
        * Zeichen daneben ist Schmuck und ausdruecklich `aria-hidden`;
        * die Bedeutung steckt im Text, nicht in der Form.
        *
        * UND DER LEERE FALL IST EINE AUSKUNFT, keine fehlende Liste.
        * „Nichts offen." las sich wie ein Achselzucken; „Keine offenen
        * Punkte" mit Haken ist die Antwort auf die Frage, die in der
        * Ueberschrift steht.
        */}
      {(
        <ul className="plp__punkte">
          {daten.attention.map((a, i) => (
            <li key={`${a.code}${a.subjectId}${i}`} data-stufe={a.severity}
                className={`plp__punkt plp__punkt--${a.severity}`}>
              <span className="plp__punktmarke" aria-hidden="true">
                {a.severity === "warnung" ? "⚠" : "ⓘ"}
              </span>
              <span className="plp__punktstufe">
                {t(a.severity === "warnung" ? "plp.severityUrgent" : "plp.severityOpen")}
              </span>
              <button type="button" className="plp__punktziel" data-testid="plp-punkt"
                data-code={a.code}
                onClick={() => onSubject(a.subject, a.subjectId)}>
                {a.subjectLabel}
              </button>
              <span className="plp__punkttext">
                {i18n.tCode("plpAttn", a.code, a.code)}
                {a.detail !== null && (
                  <span className="plp__punktdetail">{detailText(a.code, a.detail, i18n)}</span>
                )}
                {/*
                  * WAS IST, UND WAS ZU TUN IST -- in dieser Reihenfolge.
                  *
                  * Die Zeile sagte bisher nur den Befund: „Artwork
                  * gesendet, aber nicht freigegeben". Richtig, und
                  * trotzdem muss der Benutzer selbst herausfinden,
                  * wodurch die Bestellung wieder gruen wird. Der zweite
                  * Satz nennt die Handlung; der Klick auf die Bestellung
                  * daneben fuehrt dorthin, wo sie moeglich ist -- die
                  * Arbeitszone oben in der Akte hat sie dann bereits
                  * vorgewaehlt.
                  */}
                <span className="plp__punkttun" data-testid="plp-punkttun">
                  {i18n.tCode("plpAttnDo", a.code, "")}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function detailText(code: string, detail: string, i18n: I18n): string {
  if (code === "po_forecast_basis_missing") {
    return detail === "rule" ? i18n.t("plp.missingRule")
      : i18n.t("plp.missingAnchor", { anker: i18n.tCode("ms", detail, detail) });
  }
  if (code === "milestone_overdue") {
    const [c, tag] = detail.split("|");
    return `${i18n.tCode("ms", c ?? "", c ?? "")} · ${kurzDatum(tag ?? "", i18n.lang)}`;
  }
  if (code === "cn_po_after_cutoff") {
    const [po, ready, cutoff] = detail.split("|");
    return `${po} · ${kurzDatum(ready ?? "", i18n.lang)} > ${kurzDatum(cutoff ?? "", i18n.lang)}`;
  }
  if (code === "cn_window_open") {
    const [po, , puffer] = detail.split("|");
    return `${po} · ${i18n.t("plp.buffer", { n: puffer ?? "?" })}`;
  }
  if (code === "po_below_moq") return i18n.t("plp.moqLines", { n: detail });
  if (/^\d{4}-\d{2}-\d{2}$/.test(detail)) return kurzDatum(detail, i18n.lang);
  /*
   * ── KEIN DATENBANKCODE AUF DEM BILDSCHIRM ──────────────────────
   *
   * DER BEFUND DER BROWSER-ABNAHME. In der Arbeitsliste stand
   * „eine erforderliche Prozessangabe fehlt noch  packing_list_received".
   * Der letzte Teil ist der Schluessel aus `milestone_codes` -- fuer
   * HOMEBASE eine Kennung, fuer den Leser ein Stolperstein.
   *
   * DIE URSACHE war der Rueckfall am Ende dieser Funktion: was keinen
   * eigenen Zweig hatte, wurde unveraendert durchgereicht. Das ist
   * bequem und genau einmal richtig -- naemlich wenn der Rohwert
   * schon ein Satz ist.
   *
   * GEPRUEFT WIRD DESHALB ZUERST DER TERMINKATALOG. Steht der Wert
   * dort, bekommt er seinen Namen; steht er nicht dort, ist er kein
   * Code und darf bleiben. Das gilt fuer JEDEN Aufmerksamkeitspunkt,
   * nicht nur fuer den einen, der aufgefallen ist.
   */
  const ausKatalog = i18n.tCode("ms", detail, "");
  if (ausKatalog !== "") return ausKatalog;
  return detail;
}

/**
 * DAS KONSOLIDIERUNGSFENSTER -- ein Planungshinweis, keine Entscheidung.
 *
 * Gefuehrt wird der KONSERVATIVE Puffer: wer mit dem optimistischen
 * plant, plant mit dem besten Fall, und der ist bei einer Spanne von 30
 * bis 60 Tagen die Ausnahme. Der letzte Satz jeder Zeile ist keine
 * Hoeflichkeit -- er ist die Zusage, dass HOMEBASE nichts zuordnet.
 */
function Fenster({ daten, i18n, mayWrite, onZuordnen }: {
  daten: TimelineResponse; i18n: I18n; mayWrite: boolean;
  onZuordnen: (w: ConsolidationWindow) => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  const passend = daten.windows.filter((w) => w.state !== "passt_nicht");
  if (passend.length === 0) return null;
  return (
    <section className="log__group plp__fenster" data-testid="plp-fenster">
      <h4 className="log__grouptitle">{t("plp.window")}</h4>
      <ul className="plp__fensterliste">
        {passend.map((w) => (
          <li key={`${w.consolidationId}-${w.purchaseOrderId}`} className="plp__fensterzeile"
              data-testid="plp-fensterzeile" data-state={w.state}>
            <span className="plp__fensterkopf">
              {w.dteNumber} · {w.supplierName}
              <span className="plp__tag">{i18n.tCode("plpWindow", w.state)}</span>
            </span>
            <span className="plp__fensterwerte">
              <span>{t("plp.expectedReady")}: {w.readyFrom === null ? <Unknown />
                : w.readyFrom === w.readyTo ? kurzDatum(w.readyFrom, i18n.lang)
                : `${kurzDatum(w.readyFrom, i18n.lang)} – ${kurzDatum(w.readyTo!, i18n.lang)}`}
                {w.readyCertainty !== null && ` · ${i18n.tCode("cert", w.readyCertainty)}`}</span>
              <span>{t("plp.cutoff")} {w.consolidationNumber}: {kurzDatum(w.cutoffOn!, i18n.lang)}</span>
              <span>{t("plp.bufferLabel")}: {t("plp.buffer", { n: String(w.bufferConservative) })}</span>
            </span>
            <span className="plp__fenstersatz">
              {t("plp.windowSentence", { po: w.dteNumber, cn: w.consolidationNumber })}
            </span>
            {mayWrite && (
              <button type="button" className="btn btn--small" data-testid="plp-zuordnen"
                onClick={() => onZuordnen(w)}>{t("plp.assign")}</button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Termine erfassen ───────────────────────────────────────────────────── */

/*
 * EINE ZAHLUNG IST KEINE TERMINART -- und sie stand trotzdem in der
 * Terminauswahl.
 *
 * DER BEFUND DER BROWSER-ABNAHME 002. Im Auswahlfeld „Termin waehlen"
 * erschien „Zahlung" neben „Packing List erhalten" und „Versandbereit".
 * Technisch war das nie falsch -- gespeichert wurde immer eine
 * `purchase_order_payments`-Zeile und nie ein Meilenstein. Aber die
 * Maske behauptete etwas anderes, als sie tat, und wer sie liest, lernt
 * das Falsche: dass es einen Termincode „Zahlung" gaebe.
 *
 * Erfasst wird eine Zahlung ab hier ausschliesslich unter FINANZEN. Dort
 * steht sie seit jeher, mit Art, Betrag, Beleg und Notiz -- also mit
 * allem, was eine Terminmaske gar nicht erfragen kann.
 *
 * ANGEZEIGT wird sie weiterhin in der Chronologie, denn zeitlich gehoert
 * sie zum Vorgang. Eine Zeile, die man lesen, aber nicht hier anlegen
 * kann, ist kein Widerspruch: sie ist der Unterschied zwischen Verlauf
 * und Erfassung.
 */

/**
 * RAUM UNTER DER LETZTEN KARTE -- fuer den nativen Kalender.
 *
 * DER BEFUND DER ABNAHME 002: steht die Terminmaske weit unten auf einer
 * langen Bestellseite, laesst sich das Datumsfeld in Safari nicht
 * zuverlaessig bedienen; der Kalender erscheint am unteren Rand.
 *
 * DIE URSACHE SIND ZWEI DINGE ZUSAMMEN. `.app__main` ist genau
 * fensterhoch, und `.plp` scrollt in sich -- nicht die Seite. Und die
 * Terminmaske ist das LETZTE Element dieses Bereichs: unter dem Feld
 * liegt nichts, `scrollTop` steht am Anschlag (gemessen: 493 von 493),
 * und ein Browser kann einen Scrollbereich nicht aufscrollen, in dem
 * nichts mehr kommt.
 *
 * WAS NICHT GEHOLFEN HAT, und warum es hier nicht steht: beim Fokus zu
 * scrollen. Es setzt voraus, dass ein Fokusereignis ankommt und dass
 * der Browser den Kalender erst danach platziert -- beides konnte ich
 * nicht belegen. Ein Mechanismus, dessen Wirkung ich nicht nachweisen
 * kann, gehoert nicht in eine Korrektur.
 *
 * DIESER PLATZHALTER BRAUCHT WEDER EREIGNIS NOCH ZEITPUNKT. Er ist
 * einfach da, und damit ist unter dem Feld Raum, in den der Browser
 * scrollen kann.
 *
 * `min(24rem, 50vh)` ist aus dem bemessen, was hineinpassen muss: ein
 * nativer Kalender ist rund 24rem hoch. Auf kleinen Bildschirmen wird
 * daraus nie mehr als die halbe Ansicht -- sonst waere die Reserve
 * groesser als das, wofuer sie da ist.
 */
function Kalenderraum(): React.JSX.Element {
  return <div className="plp__kalenderraum" aria-hidden="true" />;
}

/**
 * PLATZ FUER DEN KALENDER SCHAFFEN, BEVOR ER AUFGEHT.
 *
 * DER BEFUND AUS DER PRODUKTIVEN ABNAHME 002: steht die Terminmaske weit
 * unten auf einer langen Bestellseite, laesst sich das Datumsfeld in
 * Safari nicht zuverlaessig bedienen. Der native Kalender erscheint am
 * unteren Rand und ist dort nicht mehr sicher zu treffen.
 *
 * DIE URSACHE IST DER AUFBAU DER SEITE, NICHT DAS FELD.
 *
 * `.app__main` ist genau fensterhoch, und `.plp` traegt seit dem
 * Scroll-Befund `overflow-y: auto` -- die SEITE scrollt also nicht,
 * sondern der Portalbereich in ihr. Die Terminmaske ist dessen letztes
 * Element: ihre Unterkante faellt mit der Unterkante des Scrollbereichs
 * zusammen, und die wiederum mit dem Fensterrand.
 *
 * Unter dem Feld ist damit NULL Platz. Safari oeffnet den Kalender nach
 * unten, findet dort nichts und scrollt einen VERSCHACHTELTEN
 * Scrollbereich dafuer nicht auf -- die Seite selbst koennte es, dieser
 * Bereich wird nicht gefragt.
 *
 * AUSGESCHLOSSEN, weil geprueft: `overflow: hidden` an `.log__group`
 * schneidet nichts ab -- ein nativer Kalender ist Browseroberflaeche und
 * wird von CSS-Overflow nicht beschnitten. Es ist auch keine
 * Stapelreihenfolge: das Feld ist anklickbar, nur der Kalender hat
 * keinen Raum.
 *
 * DIE REGEL, DIE DARAUS FOLGT -- und sie ist keine geratene Pixelzahl:
 * ein Datumsfeld, dessen Kalender nach unten aufgeht, gehoert in die
 * OBERE HAELFTE seines Scrollbereichs. Liegt es tiefer, wird genau so
 * weit gescrollt, dass es dort ankommt. Liegt es ohnehin oben, geschieht
 * nichts.
 */


function Termine({ subject, subjectId, milestones, codes, prozess, i18n, mayWrite,
                   onChanged, onError, payments = [], vorwahl = null, onVorwahl }: {
  subject: "purchase_order" | "consolidation"; subjectId: number;
  milestones: readonly Milestone[]; codes: readonly MilestoneCodeInfo[];
  /** Altvorgaenge und gesperrte Schritte -- aus dem Fachkern, nicht von hier. */
  prozess: ProcessGuidance;
  i18n: I18n; mayWrite: boolean; onChanged: () => void; onError: (e: unknown) => void;
  /** Die tatsaechlich geleisteten Zahlungen -- dieselben Saetze wie unter
      Finanzen, hier nur ANGEZEIGT. Erfasst werden sie dort. */
  payments?: readonly PurchaseOrderPayment[];
  /**
   * Die Terminart, die der „Naechster Schritt"-Knopf oben gewaehlt hat.
   *
   * KEINE ZWEITE MASKE UND KEIN ZWEITER SCHREIBWEG -- der Knopf oben
   * stellt genau dieses Feld ein und holt die bestehende Maske in den
   * Blick. Alles danach ist derselbe Weg wie immer.
   */
  vorwahl?: MilestoneCode | null;
  /** Meldet zurueck, dass die Vorwahl angekommen ist -- sonst setzt sie
      sich bei jedem Neuaufbau erneut und ueberschriebe die Eingabe. */
  onVorwahl?: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [code, setCode] = useState<MilestoneCode | "">("");
  const [tag, setTag] = useState("");
  const [wunsch, setWunsch] = useState<StoredCertainty>("confirmed");
  const [grund, setGrund] = useState("");
  const [speichert, setSpeichert] = useState(false);
  /*
   * BEARBEITEN IST KEINE ZWEITE MASKE.
   *
   * Der Befund aus der Abnahme: wer sich im Datum vertippt hatte, musste
   * den Termin ENTFERNEN und neu anlegen. Das ist nicht nur umstaendlich
   * -- es verliert die Geschichte, und bei einem Stand, der schon etwas
   * abgeloest hat, verweigert die API das Entfernen zu Recht.
   *
   * Der Weg, den es dafuer laengst gibt, ist die ABLOESUNG: derselbe
   * Code, derselbe Terminstatus, ein anderer Tag. Genau das tut
   * `setMilestone`. Bearbeiten heisst hier deshalb: dieselbe Maske, mit
   * den Werten der Zeile vorbelegt. Kein neuer Schreibweg, keine zweite
   * Logik, keine umgangene Historisierung.
   */
  const [bearbeitet, setBearbeitet] = useState<Milestone | null>(null);
  const maske = useRef<HTMLDivElement | null>(null);

  /* Die Vorwahl von oben stellt die Maske ein und holt sie in den Blick.
     Mehr tut sie nicht -- gespeichert wird weiterhin von Hand. */
  useEffect(() => {
    if (vorwahl === null || vorwahl === undefined) return;
    setBearbeitet(null);
    setCode(vorwahl);
    setTag("");
    setGrund("");
    maske.current?.scrollIntoView({ block: "center" });
    onVorwahl?.();
  }, [vorwahl, onVorwahl]);

  /*
   * ABGELOESTE TERMINARTEN STEHEN NICHT MEHR ZUR WAHL -- aber sie bleiben
   * bearbeitbar. Wer eine bestehende Zeile ueber den Stift oeffnet,
   * bekommt ihre Terminart weiterhin angezeigt; nur NEU anlegen laesst
   * sie sich nicht mehr. Die Liste dafuer steht im Katalog und nicht
   * hier -- eine Aufzaehlung an dieser Stelle waere die zweite Wahrheit.
   */
  /*
   * ABGEKUENDIGTE UND NICHT MEHR MANUELLE TERMINARTEN STEHEN NICHT MEHR
   * ZUR WAHL -- aber sie bleiben BEARBEITBAR.
   *
   * Seit 0084 gibt es beide Faelle: abgekuendigt heisst „wird kuenftig
   * gar nicht mehr erfasst", nicht manuell heisst „wird woanders
   * erfasst". Wer eine bestehende Zeile ueber den Stift oeffnet,
   * bekommt ihre Terminart in beiden Faellen weiterhin angezeigt --
   * sonst stuende die Maske beim Bearbeiten auf einem leeren Feld und
   * schriebe beim Speichern etwas anderes, als dort stand.
   */
  /*
   * ── UND DIE TERMINPFLEGE ARBEITET NICHT AM PROZESSMODELL VORBEI ──
   *
   * DER BEFUND DER BROWSER-ABNAHME: unter „Alle Termine dieser
   * Sendung" liess sich „Departed" waehlen, obwohl CN-01 es als
   * Altvorgang fuehrte -- und das Speichern endete mit
   * `cn_not_handed_over`. Die Datenbank hat richtig gehalten; die
   * Oberflaeche hatte es vorher wissen koennen.
   *
   * AB HIER WEISS SIE ES. Zwei Gruppen verschwinden aus der Auswahl:
   *
   *   ALTVORGAENGE     Terminarten, die es bei Anlage dieses Vorgangs
   *                    nicht gab. Sie werden nicht nachgepflegt; die
   *                    Akte zeigt sie als „nicht dokumentiert".
   *   GESPERRTE        Terminarten, deren Voraussetzung fehlt. Eine
   *                    Abfahrt ohne Carrier-Uebergabe gibt es nicht.
   *   AUSGESCHLOSSENE  Terminarten, die woanders entstehen -- der
   *                    Wareneingang im Wareneingang, die
   *                    Konsolidierungsbereitschaft durch Rechnung.
   *
   * Beide Listen kommen aus `akte.process` -- also aus derselben
   * Ableitung wie die Arbeitszone oben und die Uebersicht. Hier steht
   * keine zweite Regel.
   *
   * WAS BEARBEITBAR BLEIBT, BLEIBT BEARBEITBAR. Die gerade geoeffnete
   * Terminart ist immer dabei: sonst stuende die Maske beim Bearbeiten
   * auf einem leeren Feld und schriebe beim Speichern etwas anderes,
   * als dort stand. Das gilt auch fuer abgekuendigte und nicht mehr
   * manuelle Arten.
   */
  const moeglich = codes.filter((c) =>
    c.subject === subject && (c.manual || c.code === code)
    && (!c.deprecated || c.code === code)
    && (!istAlt(prozess, c.code) || c.code === code)
    && (!(prozess.excluded as readonly string[]).includes(c.code) || c.code === code)
    && (fehltVoraussetzung(prozess, c.code) === null || c.code === code));

  /*
   * ── PLAN UND IST STEHEN GETRENNT ──
   *
   * Der Katalog traegt die Unterscheidung bereits: was kein Ist sein
   * darf, ist eine Erwartung. Eine zweite Liste im Quelltext waere die
   * zweite Wahrheit -- und sie waere beim naechsten Katalogschritt
   * falsch.
   *
   *   ERWARTUNG   Cut-off, ETD, ETA Hamburg, ETA Lueneburg
   *   TATSACHE    alles, was eintreten kann
   */
  const plangruppe = moeglich.filter((c) => !c.allowsActual);
  const istgruppe = moeglich.filter((c) => c.allowsActual);

  const info = moeglich.find((c) => c.code === code) ?? null;

  /*
   * DIE ZULAESSIGKEITSMATRIX HAT GENAU EINE QUELLE: `milestone_codes`.
   *
   * Sie kommt als `allowsConfirmed`/`allowsActual` aus demselben Katalog,
   * den die API prueft. Hier steht keine zweite Liste, die auseinander-
   * laufen koennte.
   *
   * UND SIE BESTIMMT DEN WERT, NICHT NUR DIE AUSWAHL. Vorher hielt diese
   * Maske den Terminstatus in einem eigenen Zustand und blendete nur die
   * unzulaessige OPTION aus. Bei „PI erhalten" verschwand damit die
   * Zusage aus der Liste, der Zustand blieb aber auf `confirmed` stehen:
   * der Browser zeigte „Ist", gesendet wurde „Zugesagt", und die API
   * antwortete zu Recht mit `certainty_not_allowed`. Was angezeigt wird,
   * ist ab jetzt dasselbe wie das, was gesendet wird -- der Wunsch des
   * Benutzers wird gemerkt, aber nie gegen die Matrix durchgesetzt.
   */
  const erlaubt: StoredCertainty[] = info === null
    ? ["confirmed", "actual"]
    : ([["confirmed", info.allowsConfirmed], ["actual", info.allowsActual]] as const)
        .filter(([, ja]) => ja).map(([c]) => c);
  const sicherheit: StoredCertainty = erlaubt.includes(wunsch) ? wunsch : erlaubt[0]!;

  const aktuell = milestones.filter((m) => m.supersededAt === null);
  /* Loest die Eingabe einen bestehenden Stand ab? Dann wird nach dem
     Grund gefragt -- und nur dann. */
  const loestAb = info !== null && aktuell.some(
    (m) => m.code === code && m.certainty === sicherheit && m.happensOn !== tag);

  async function speichern(): Promise<void> {
    if (code === "" || tag === "") return;
    setSpeichert(true);
    try {
      await api.setMilestone({
        subject, subjectId, code, happensOn: tag, certainty: sicherheit,
        reasonNote: grund.trim() === "" ? null : grund.trim(),
      });
      /*
       * EIN GEAENDERTER TERMINSTATUS IST KEINE ABLOESUNG.
       *
       * Eindeutig ist je (Objekt, Code, TERMINSTATUS) -- eine Zusage und
       * ein Ist stehen nebeneinander, und das ist richtig so. Wer beim
       * Erfassen den falschen Status gewaehlt hat, erzeugt mit der
       * Korrektur deshalb eine ZWEITE Zeile; die falsche bliebe stehen.
       *
       * Sie wird darum entfernt -- aber ERST NACH dem Schreiben des
       * richtigen Standes. Scheitert das Entfernen, stehen beide da und
       * man sieht es; in der anderen Reihenfolge waere der Termin bei
       * einem Fehlschlag ganz fort.
       *
       * Und es wird NICHT erzwungen: hat die alte Zeile bereits etwas
       * abgeloest, weist die API das Entfernen mit
       * `milestone_has_history` ab. Das ist keine Panne, sondern die
       * Historisierung, die hier ausdruecklich nicht umgangen wird --
       * dann bleiben beide Staende lesbar.
       */
      if (bearbeitet !== null && bearbeitet.certainty !== sicherheit) {
        try { await api.deleteMilestone(bearbeitet.id); }
        catch { /* Geschichte bleibt Geschichte -- beide Staende bleiben sichtbar. */ }
      }
      setCode(""); setTag(""); setGrund(""); setBearbeitet(null); onChanged();
    } catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  /** Die Zeile in die Maske holen -- mit ihren Werten, nicht mit leeren. */
  function bearbeiten(m: Milestone): void {
    setBearbeitet(m);
    setCode(m.code);
    setTag(m.happensOn);
    setWunsch(m.certainty);
    setGrund("");
    onError(null);
  }
  function abbrechen(): void {
    setBearbeitet(null); setCode(""); setTag(""); setGrund("");
  }
  async function entfernen(id: number): Promise<void> {
    setSpeichert(true);
    try { await api.deleteMilestone(id); onChanged(); }
    catch (e) { onError(e); } finally { setSpeichert(false); }
  }

  /*
   * DER VERLAUF FOLGT DEM, WAS GESCHEHEN IST -- nicht dem, was erfasst wurde.
   *
   * DER BEFUND DER BROWSER-ABNAHME 002. „Versandbereit" war auf den 25.08.
   * erfasst und danach ueber den Stift auf den 24.08. berichtigt worden.
   * Die Liste zeigte daraufhin:
   *
   *     Versandbereit   25.08.   abgeloest
   *     Packing List    18.08.
   *     Versandbereit   24.08.
   *
   * Dreimal irrefuehrend auf einmal: die Reihenfolge stimmte nicht, der
   * abgeloeste Stand stand VOR dem gueltigen, und beide sahen gleich
   * wichtig aus. Wer das liest, zaehlt zwei Versandbereitschaften.
   *
   * ZWEI REGELN RAEUMEN DAS AUF
   *
   *   1. Sortiert wird nach dem FACHLICHEN Ereignisdatum, aufsteigend.
   *      Bei gleichem Tag entscheidet die Prozessreihenfolge aus dem
   *      Katalog -- `codes` kommt bereits nach `sort_order` sortiert aus
   *      der API, der Rang ist also der Platz in dieser Liste und keine
   *      zweite Ordnung. Zahlungen reihen sich ueber ihren Zahltag ein
   *      und stehen bei Gleichstand hinten: sie begleiten den Prozess,
   *      sie treiben ihn nicht.
   *
   *   2. Ein abgeloester Stand ist KEINE eigene Zeile mehr. Er haengt
   *      unter dem gueltigen Stand derselben Kette -- und eine Kette ist
   *      (Code, Terminstatus), genau wie in der Datenbank. Eine Zusage
   *      und ein Ist stehen deshalb weiterhin nebeneinander; nur die
   *      BERICHTIGUNG rutscht nach unten.
   *
   * NICHTS VERSCHWINDET. Ein abgeloester Stand ohne gueltigen Stand --
   * etwa weil die Zeile mit dem anderen Terminstatus entfernt wurde --
   * bleibt als eigene Zeile stehen und traegt weiter seine Marke. Lieber
   * eine Zeile, die erklaert werden muss, als eine, die fehlt.
   */
  const rang = (code: string): number => {
    const i = codes.findIndex((c) => c.code === code);
    return i < 0 ? 900 : i;
  };
  const kette = (m: Milestone): string => `${m.code}|${m.certainty}`;
  const historieZu = new Map<string, Milestone[]>();
  for (const m of milestones) {
    if (m.supersededAt === null) continue;
    const liste = historieZu.get(kette(m));
    if (liste === undefined) historieZu.set(kette(m), [m]); else liste.push(m);
  }
  type Verlaufszeile =
    | { art: "termin"; datum: string; rang: number; m: Milestone; historie: readonly Milestone[] }
    | { art: "zahlung"; datum: string; rang: number; z: PurchaseOrderPayment };
  const verlauf: Verlaufszeile[] = [];
  for (const m of milestones) {
    if (m.supersededAt !== null) continue;
    const h = [...(historieZu.get(kette(m)) ?? [])]
      .sort((a, b) => (b.supersededAt ?? "").localeCompare(a.supersededAt ?? ""));
    historieZu.delete(kette(m));
    verlauf.push({ art: "termin", datum: m.happensOn, rang: rang(m.code), m, historie: h });
  }
  for (const liste of historieZu.values()) {
    for (const m of liste) {
      verlauf.push({ art: "termin", datum: m.happensOn, rang: rang(m.code), m, historie: [] });
    }
  }
  for (const z of payments) {
    verlauf.push({ art: "zahlung", datum: z.paidOn, rang: 950, z });
  }
  verlauf.sort((a, b) => a.datum.localeCompare(b.datum) || a.rang - b.rang);

  return (
    <section className="log__group plp__termine" data-testid="plp-termine">
      <h4 className="log__grouptitle">{t("plp.milestones")}</h4>
      {milestones.length === 0 && payments.length === 0 ? (
        <p className="log__hint">{t("plp.noMilestones")}</p>
      ) : (
        <ul className="plp__terminliste">
          {verlauf.map((v) => v.art === "zahlung" ? (
            /*
              * DIESELBEN SAETZE WIE UNTER FINANZEN, keine Kopie -- und
              * hier nur zu lesen. Eine Zahlung traegt keinen
              * Terminstatus und keine Abloesung: sie ist geleistet oder
              * sie ist nicht erfasst. Geaendert wird sie dort, wo sie
              * gefuehrt wird.
              */
            <li key={`z${v.z.id}`} className="plp__termin plp__termin--zahlung"
                data-testid="plp-zahlungzeile">
              <span className="plp__terminname">{t("plp.payment")}</span>
              <span className="plp__termindatum">{kurzDatum(v.z.paidOn, i18n.lang)}</span>
              <span className="plp__zahlbetrag" data-betrag={v.z.amount}>
                {`${Number(v.z.amount).toLocaleString(i18n.lang, {
                  minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${v.z.currency}`}
              </span>
              <span className="plp__terminherkunft">
                {t("plp.recordedBy", {
                  wer: v.z.recordedBy ?? "—",
                  wann: kurzDatum(v.z.recordedAt.slice(0, 10), i18n.lang),
                })}
              </span>
            </li>
          ) : (
            <li key={v.m.id} data-testid="plp-termin" data-code={v.m.code}
                data-datum={v.m.happensOn}
                className={v.m.supersededAt === null ? "plp__termin" : "plp__termin plp__termin--alt"}>
              <span className="plp__terminname">{i18n.tCode("ms", v.m.code, v.m.code)}</span>
              <span className="plp__termindatum">{kurzDatum(v.m.happensOn, i18n.lang)}</span>
              <span className="plp__tag">{i18n.tCode("cert", v.m.certainty)}</span>
              {v.m.supersededAt !== null && (
                <span className="plp__tag plp__tag--alt">{t("plp.superseded")}</span>
              )}
              <span className="plp__terminherkunft">
                {t("plp.recordedBy", {
                  wer: v.m.recordedBy ?? "—",
                  wann: kurzDatum(v.m.recordedAt.slice(0, 10), i18n.lang),
                })}
                {v.m.reasonNote !== null && <em className="plp__terminGrund">{v.m.reasonNote}</em>}
              </span>
              {/*
                * ZWEI GETRENNTE HANDLUNGEN, und sie sehen auch so aus.
                * Bearbeiten korrigiert, Entfernen nimmt zurueck -- wer
                * ein Datum richtigstellen will, soll nicht loeschen
                * muessen.
                */}
              {mayWrite && v.m.supersededAt === null && (
                <button type="button" className="plp__terminstift"
                  data-testid="plp-terminstift" disabled={speichert}
                  title={t("plp.editMilestone")} aria-label={t("plp.editMilestone")}
                  onClick={() => bearbeiten(v.m)}>✎</button>
              )}
              {mayWrite && v.m.supersededAt === null && (
                <button type="button" className="bew__weg" data-testid="plp-terminweg"
                  disabled={speichert} onClick={() => void entfernen(v.m.id)}>
                  {t("plp.removeMilestone")}
                </button>
              )}
              {/*
                * DIE BERICHTIGUNG STEHT UNTER DEM GUELTIGEN STAND -- leise,
                * vollstaendig und ohne eigene Zeile in der Chronologie.
                */}
              {v.historie.length > 0 && (
                <ul className="plp__terminhistorie" data-testid="plp-terminhistorie">
                  {v.historie.map((h) => (
                    <li key={h.id} data-testid="plp-terminzuvor" data-datum={h.happensOn}>
                      {t("plp.previously", { wann: kurzDatum(h.happensOn, i18n.lang) })}
                      {h.reasonNote !== null && (
                        <em className="plp__terminGrund">{h.reasonNote}</em>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {mayWrite && (
        <div className="sup__kform" data-testid="plp-terminmaske" ref={maske}
             data-modus={bearbeitet === null ? "neu" : "bearbeiten"}>
          {/* Wer bearbeitet, soll sehen, WAS er bearbeitet -- und nicht
              raten muessen, ob die Maske noch anlegt. */}
          {bearbeitet !== null && (
            <p className="sup__kfeld--breit log__hint" data-testid="plp-terminbearbeitet">
              {t("plp.editingMilestone", {
                was: i18n.tCode("ms", bearbeitet.code, bearbeitet.code),
                wann: kurzDatum(bearbeitet.happensOn, i18n.lang),
              })}
            </p>
          )}
          <label className="sup__kfeld">
            <span className="sup__klabel">{t("plp.milestone")}</span>
            {/* Beim Bearbeiten steht die Terminart fest: eine andere waere
                ein anderer Termin und keine Korrektur. */}
            <select className="log__input" value={code}
              disabled={speichert || bearbeitet !== null}
              aria-label={t("plp.milestone")} data-feld="code"
              onChange={(e) => setCode(e.target.value as MilestoneCode | "")}>
              <option value="">{t("plp.chooseMilestone")}</option>
              {plangruppe.length > 0 && (
                <optgroup label={t("plp.groupPlanned")} data-gruppe="plan">
                  {plangruppe.map((c) => (
                    <option key={c.code} value={c.code}>
                      {i18n.tCode("ms", c.code, c.code)}</option>
                  ))}
                </optgroup>
              )}
              {istgruppe.length > 0 && (
                <optgroup label={t("plp.groupActual")} data-gruppe="ist">
                  {istgruppe.map((c) => (
                    <option key={c.code} value={c.code}>
                      {i18n.tCode("ms", c.code, c.code)}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {/*
              * WAS GERADE NICHT GEHT, STEHT DA -- samt Grund.
              *
              * Ein Wert, der ohne Erklaerung fehlt, sieht aus wie ein
              * Fehler. Dieser Satz nennt das Ereignis, das vorher
              * erfasst sein muss, und zwar BEVOR jemand speichert.
              */}
            {prozess.blocked.length > 0 && (
              <span className="log__hint" data-testid="plp-termingesperrt">
                {prozess.blocked.map((b: { code: string; needs: string }) => t("plp.stepNeedsFirst", {
                  was: i18n.tCode("ms", b.code, b.code),
                  erst: vorbedingungText(b.needs, i18n),
                })).join(" ")}
              </span>
            )}
          </label>
          <label className="sup__kfeld">
            <span className="sup__klabel">{t("plp.date")}</span>
            <input className="log__input log__input--text" type="date" value={tag}
              aria-label={t("plp.date")} data-feld="happensOn" disabled={speichert}
              onChange={(e) => setTag(e.target.value)} />
          </label>
          <label className="sup__kfeld">
            <span className="sup__klabel">{t("plp.certainty")}</span>
            {/* GESPEICHERT WIRD NUR, WAS JEMAND WEISS. Ein Forecast steht
                hier nicht zur Wahl -- er wird gerechnet. */}
            <select className="log__input" value={sicherheit} disabled={speichert || erlaubt.length < 2}
              aria-label={t("plp.certainty")} data-feld="certainty"
              onChange={(e) => setWunsch(e.target.value as StoredCertainty)}>
              {erlaubt.map((c) => (
                <option key={c} value={c}>{i18n.tCode("cert", c)}</option>
              ))}
            </select>
            {/* Warum es hier nichts zu waehlen gibt -- statt eines Feldes,
                das grundlos feststeht. */}
            {erlaubt.length < 2 && (
              <span className="log__hint" data-testid="plp-certainty-fest">
                {t(sicherheit === "actual" ? "plp.certaintyOnlyActual" : "plp.certaintyOnlyConfirmed")}
              </span>
            )}
          </label>
          {loestAb && (
            <label className="sup__kfeld sup__kfeld--breit">
              <span className="sup__klabel">{t("plp.whyMoved")}</span>
              <input className="log__input log__input--text" value={grund} data-feld="reasonNote"
                aria-label={t("plp.whyMoved")} placeholder={t("plp.whyMovedHint")}
                disabled={speichert} onChange={(e) => setGrund(e.target.value)} />
            </label>
          )}
          <span className="sup__kformaktionen">
            <button className="btn btn--primary btn--small" data-testid="plp-terminspeichern"
              disabled={speichert || code === "" || tag === ""}
              onClick={() => void speichern()}>
              {speichert ? t("props.saving") : loestAb ? t("plp.moveMilestone") : t("log.save")}
            </button>
            {bearbeitet !== null && (
              <button className="btn btn--small" data-testid="plp-terminabbrechen"
                disabled={speichert} onClick={abbrechen}>{t("common.cancel")}</button>
            )}
          </span>
        </div>
      )}
    </section>
  );
}


/**
 * ── DIE REGELN STEHEN NICHT MEHR HIER ──
 *
 * Bis zum Korrekturlauf 005 fuehrte diese Datei drei eigene Listen:
 * welche Terminart nicht angeboten wird, welche Tatsache welchen
 * Schritt erledigt, und welche Schritte eine Bestellung wirklich
 * braucht. Daneben fuehrte `zeitachse.ts` eine vierte fuer dieselbe
 * Frage.
 *
 * DER BEFUND DER BROWSER-ABNAHME: die Uebersicht meldete „Keine
 * offenen Punkte", und die Akte zeigte daneben eine offene
 * Prozessangabe. Beide hatten nach ihren eigenen Regeln recht.
 *
 * AB HIER KOMMT DIE ANTWORT AUS DEM FACHKERN -- `akte.process` --, und
 * zwar dieselbe, die auch die Arbeitsliste der Uebersicht speist. Was
 * offen ist, was ein Altvorgang ist und was fachlich noch nicht geht,
 * entscheidet `prozessfuehrung.ts` und sonst niemand.
 */

/** Ist diese Terminart bei diesem Vorgang ein Altvorgang? */
function istAlt(p: ProcessGuidance, code: string): boolean {
  return (p.legacy as readonly string[]).includes(code);
}

/** Fehlt dieser Terminart noch ihre Voraussetzung -- und welche? */
function fehltVoraussetzung(p: ProcessGuidance, code: string): string | null {
  return p.blocked.find((b: { code: string }) => b.code === code)?.needs ?? null;
}

/**
 * Die Voraussetzung in Worten.
 *
 * Fast immer ist sie eine Terminart und kommt aus dem Katalog.
 * `forwarder_intake` ist keine -- es ist der Zustand „alle
 * Bestellungen sind beim Logistikpartner". Ohne diesen Zweig stuende
 * dort der nackte Code.
 */
function vorbedingungText(needs: string, i18n: I18n): string {
  return needs === "forwarder_intake"
    ? i18n.t("plp.needsIntake")
    : i18n.tCode("ms", needs, needs);
}

/**
 * DIE ARBEITSZONE -- oben, klein, und in zwei Staerken.
 *
 * NOCH ERFORDERLICH steht zuerst: ohne Packliste keine Frachtanfrage,
 * ohne Versandbereitschaft keine Sendung. MOEGLICHE WEITERE ANGABEN
 * stehen daneben und leiser -- sie sind Angebote, keine Forderungen.
 *
 * Innerhalb einer Gruppe gibt es keine Rangfolge. Die Reihenfolge im
 * Katalog ist die Prozessreihenfolge und keine Rangliste.
 */
function Arbeitszone({ prozess, titel, i18n, mayWrite, onErfassen, testid,
                      zeigen = "alle", eingaenge = [], onEingang, reihenfolge = [] }: {
  /** Die Ableitung aus dem Fachkern -- dieselbe wie in der Uebersicht. */
  prozess: ProcessGuidance; titel: string;
  i18n: I18n; mayWrite: boolean;
  onErfassen: (code: MilestoneCode) => void; testid: string;
  /**
   * WIE VIEL DIE ZONE ZEIGT.
   *
   *   alle        jede offene Angabe, nach erforderlich und moeglich
   *               getrennt -- so arbeitet die BESTELLUNG. Dort gibt es
   *               zwei Pflichtschritte und mehrere Angebote, und
   *               welches davon drankommt, entscheidet der Fall.
   *   naechster   NUR der naechste Schritt -- so arbeitet die SENDUNG.
   *               Ihr Weg ist eine Kette: Frachtanfrage, Angebot,
   *               Auftrag, Papiere, Uebergabe, Abfahrt, Hamburg, Zoll,
   *               Weitergabe, Anlieferung. Dreizehn Angebote
   *               nebeneinander sind dort keine Fuehrung, sondern eine
   *               Auswahl.
   *
   * ES IST DIESELBE ABLEITUNG UND DIESELBE KOMPONENTE. Ein zweites
   * CN-Frontend daneben waere genau die Doppelung, die der
   * Korrekturlauf 005 gerade abgeschafft hat.
   */
  zeigen?: "alle" | "naechster";
  /**
   * Bestellungen dieser Sendung, die noch nicht beim Logistikpartner
   * eingetroffen sind.
   *
   * Sie stehen VOR dem naechsten Transportschritt, weil der Weg dort
   * beginnt: solange etwas fehlt, ist die Uebergabe an den Carrier
   * nicht die naechste Handlung, sondern das Warten darauf.
   */
  eingaenge?: ReadonlyArray<{ poId: number; dteNumber: string; supplierName: string }>;
  onEingang?: (poId: number) => void;
  /**
   * Die Terminarten dieses Vorgangs in Katalogreihenfolge.
   *
   * NUR FUER „naechster" -- und nur, um einen GESPERRTEN Schritt nicht
   * zu ueberspringen. Ohne sie waere der naechste Schritt schlicht die
   * erste offene Angabe, und das fuehrt in die Irre: solange die
   * Uebergabe an den Carrier auf die Bestellungen wartet, ist
   * „Hamburg angekommen" nicht das Naechste, sondern das
   * Uebernaechste.
   *
   * ES ENTSTEHT KEINE NEUE ABHAENGIGKEIT. Welcher Schritt gesperrt ist
   * und warum, sagt `prozessfuehrung.ts`; hier wird nur nicht an ihm
   * vorbeigezeigt.
   */
  reihenfolge?: readonly string[];
}): React.JSX.Element {
  const t = i18n.t;
  const offen = prozess.open;
  const gesperrt = reihenfolge.find((c) =>
    prozess.blocked.some((b: { code: string }) => b.code === c)
    || offen.some((o) => o.code === c));
  const wartet = gesperrt !== undefined
    && !offen.some((o) => o.code === gesperrt)
      ? prozess.blocked.find((b: { code: string }) => b.code === gesperrt)!
      : null;
  const naechster = wartet !== null ? null : offen.length === 0 ? null : offen[0]!;
  const nichtsZuTun = offen.length === 0 && eingaenge.length === 0 && wartet === null;

  if (nichtsZuTun) {
    /*
     * „KEINE OFFENE AKTION." -- und nicht „jede moegliche Angabe ist
     * erfasst".
     *
     * Der zweite Satz ist nicht wahr: ein Altvorgang traegt Angaben,
     * die niemand mehr erfasst, und ein gesperrter Schritt ist noch
     * nicht an der Reihe. Gemeint und richtig ist die schwaechere
     * Aussage: hier ist gerade nichts zu tun.
     */
    return (
      <section className="plp__zone plp__zone--fertig" data-testid={testid}>
        <span className="plp__zonemarke" aria-hidden="true">✓</span>
        <span className="plp__zonetext">
          {zeigen === "naechster" ? t("plp.noOpenAction") : t("plp.noOpenProcessData")}
        </span>
      </section>
    );
  }

  const knopf = (c: OpenProcessStep, stark: boolean): React.JSX.Element =>
    mayWrite ? (
      <button key={c.code} type="button"
        className={stark ? "btn btn--primary btn--small" : "btn btn--small"}
        data-testid={`${testid}-knopf`} data-code={c.code}
        data-art={stark ? "pflicht" : "moeglich"}
        onClick={() => onErfassen(c.code)}>
        {zeigen === "naechster" ? t("plp.recordNow") : i18n.tCode("ms", c.code, c.code)}
      </button>
    ) : (
      <span key={c.code} className="sup__tag" data-code={c.code}>
        {i18n.tCode("ms", c.code, c.code)}
      </span>
    );

  if (zeigen === "naechster") {
    return (
      <section className="plp__zone plp__zone--fuehrung" data-testid={testid}>
        {eingaenge.length > 0 && (
          <div className="plp__zoneeingaenge" data-testid={`${testid}-eingaenge`}>
            <p className="plp__zonesatz">
              {eingaenge.length === 1
                ? t("plp.intakeMissingOne")
                : t("plp.intakeMissing", { n: String(eingaenge.length) })}
            </p>
            <ul className="plp__zoneliste">
              {eingaenge.map((e) => (
                <li key={e.poId} className="plp__zoneeingang" data-testid={`${testid}-eingang`}
                  data-po={e.dteNumber}>
                  <span className="plp__zonepo">{e.dteNumber}</span>
                  <span className="plp__zonelieferant">{e.supplierName}</span>
                  {mayWrite && onEingang !== undefined && (
                    <button type="button" className="btn btn--small"
                      data-testid={`${testid}-eingangknopf`} data-po-id={e.poId}
                      onClick={() => onEingang(e.poId)}>{t("plp.recordIntake")}</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {naechster !== null && (
          <div className="plp__zonenaechster" data-testid={`${testid}-naechster`}
            data-code={naechster.code}>
            <span className="plp__zonetitel">{t("plp.nextStep")}</span>
            <span className="plp__zoneschritt">
              {i18n.tCode("ms", naechster.code, naechster.code)}
            </span>
            {knopf(naechster, true)}
          </div>
        )}
        {/*
          * WARTET DER WEG, STEHT DA, WORAUF -- ABER NICHT NEBEN DER
          * AUFGABE, DIE GERADE ANSTEHT.
          *
          * KORREKTURLAUF 002, Abschnitt 9. „An Carrier uebergeben --
          * erst moeglich nach Eingang aller Bestellungen" neben den
          * zwei Eingangszeilen sagt dasselbe zweimal: die Liste IST
          * der Grund. Zwei Zeilen, die aufeinander zeigen, sind keine
          * Fuehrung, sondern Rauschen.
          *
          * Stehen die Eingaenge dagegen NICHT in der Zone -- beim
          * Altvorgang, wo es die Spalte nie gab --, bleibt der Satz:
          * dort erklaert er etwas, das sonst niemand erklaert.
          *
          * Kein Knopf in beiden Faellen: ein Knopf, der in eine Absage
          * laeuft, ist schlimmer als keiner.
          */}
        {wartet !== null && eingaenge.length === 0 && (
          <div className="plp__zonewartet" data-testid={`${testid}-wartet`}
            data-code={wartet.code} data-braucht={wartet.needs}>
            <span className="plp__zonetitel">{t("plp.nextStep")}</span>
            <span className="plp__zoneschritt">
              {i18n.tCode("ms", wartet.code, wartet.code)}
            </span>
            <span className="plp__zonewarum">
              {t("plp.trailBlocked", { erst: vorbedingungText(wartet.needs, i18n) })}
            </span>
          </div>
        )}
      </section>
    );
  }

  const noetig = offen.filter((c) => c.required);
  const moeglich = offen.filter((c) => !c.required);
  return (
    <section className="plp__zone" data-testid={testid}>
      {noetig.length > 0 && (
        <span className="plp__zonegruppe" data-testid={`${testid}-pflicht`}>
          <span className="plp__zonetitel">{t("plp.stillRequired")}</span>
          <span className="plp__zoneknoepfe">{noetig.map((c) => knopf(c, true))}</span>
        </span>
      )}
      {moeglich.length > 0 && (
        <span className="plp__zonegruppe" data-testid={`${testid}-moeglich`}>
          <span className="plp__zonetitel">{titel}</span>
          <span className="plp__zoneknoepfe">{moeglich.map((c) => knopf(c, false))}</span>
        </span>
      )}
      <span className="plp__zoneoffen" data-testid={`${testid}-hinweis`}>
        {t("plp.notEveryStepRequired")}
      </span>
    </section>
  );
}

/**
 * ── DER PROZESSVERLAUF -- VOLLSTAENDIG, ABER EINGEKLAPPT ────────────
 *
 * DER BEFUND DER ABNAHME: die Sendungsakte legte das ganze
 * Prozessmodell auf den Bildschirm. Zehn Tatsachen, drei Erwartungen,
 * ein Katalog zur Auswahl -- und dazwischen die eine Frage, die
 * jemanden hergefuehrt hat: was ist jetzt zu tun?
 *
 * DER VERLAUF VERSCHWINDET DESHALB NICHT, ER TRITT ZURUECK. Eine Zeile
 * sagt, wie weit der Vorgang ist; wer mehr will, klappt auf.
 *
 * WAS DRIN STEHT, KOMMT AUS DEM KATALOG UND AUS `process` -- also aus
 * derselben Ableitung wie die Arbeitszone oben. Hier wird nichts
 * zweites entschieden, nur anders angeordnet.
 *
 * GESPERRTE SCHRITTE BLEIBEN SICHTBAR, und das ist Absicht: „Departed,
 * erst moeglich nach An Carrier uebergeben" erklaert den Weg. Ein
 * Schritt, der einfach fehlt, erklaert nichts.
 */
function Prozessverlauf({ codes, milestones, prozess, i18n, testid }: {
  codes: readonly MilestoneCodeInfo[]; milestones: readonly Milestone[];
  prozess: ProcessGuidance; i18n: I18n; testid: string;
}): React.JSX.Element {
  const t = i18n.t;
  /* Die Tatsachen des Weges, in der Reihenfolge des Katalogs. Nur was
     eintreten kann -- eine Erwartung ist kein Schritt, sie steht oben
     im Transport. */
  const schritte = codes.filter((c) =>
    c.subject === "consolidation" && c.manual && !c.deprecated && c.allowsActual
    && !(prozess.excluded as readonly string[]).includes(c.code));
  const naechster = prozess.open.length === 0 ? null : prozess.open[0]!.code;
  const erledigt = schritte.filter((c) => aktuellerTermin(milestones, c.code) !== null).length;

  return (
    <details className="log__group plp__verlauf" data-testid={testid}>
      <summary className="plp__verlauftitel">
        <span className="plp__verlaufname">{t("plp.processTrail")}</span>
        <span className="plp__verlaufzahl" data-testid={`${testid}-zahl`}>
          {t("plp.trailDone", { n: String(erledigt), alle: String(schritte.length) })}
        </span>
      </summary>
      <ol className="plp__verlaufliste">
        {schritte.map((c) => {
          const tag = aktuellerTermin(milestones, c.code);
          const alt = istAlt(prozess, c.code);
          const braucht = fehltVoraussetzung(prozess, c.code);
          const art = tag !== null ? "erledigt"
            : alt ? "altvorgang"
            : braucht !== null ? "gesperrt"
            : c.code === naechster ? "naechster" : "spaeter";
          return (
            <li key={c.code} className={`plp__verlaufschritt plp__verlaufschritt--${art}`}
              data-testid={`${testid}-schritt`} data-code={c.code} data-art={art}>
              <span className="plp__verlaufmarke" aria-hidden="true">
                {tag !== null ? "✓" : "·"}
              </span>
              <span className="plp__verlaufwas">{i18n.tCode("ms", c.code, c.code)}</span>
              <span className="plp__verlaufwann">
                {tag !== null ? kurzDatum(tag, i18n.lang)
                  : alt ? t("plp.legacyUndocumented")
                  : braucht !== null
                    ? t("plp.trailBlocked", { erst: vorbedingungText(braucht, i18n) })
                  : art === "naechster" ? t("plp.trailNext") : ""}
              </span>
            </li>
          );
        })}
      </ol>
    </details>
  );
}

/* ── Abschluss: Bewertung und Rueckblick ─────────────────────────────────── */

/**
 * WIE IST ES GELAUFEN -- von Hand, und ohne Rechnerei.
 *
 * DER BEFUND AUS DER ABNAHME. HOMEBASE kennt jeden Termin eines
 * Vorgangs, aber nicht, wie er gelaufen ist. Drei Monate spaeter steht
 * da eine Kette von Daten, und niemand weiss mehr, dass der Lieferant
 * zweimal nachbessern musste oder dass der Zoll ungewoehnlich lange
 * brauchte. Genau das zaehlt beim naechsten Mal.
 *
 * DIE STERNE RECHNET NIEMAND AUS. Keine Punkteformel, keine Schulnote,
 * keine Lieferantenbewertungsmaschine. Laufzeiten sagen, WAS war -- sie
 * sagen nicht, wer schuld ist. Wer bewertet, hat den Vorgang gesehen;
 * eine Zahl, die sich selbst errechnet, hat ihn nicht gesehen.
 *
 * GESPEICHERT WIRD ERST AUF KNOPFDRUCK. Ein Stern, der beim Klicken
 * schon schreibt, erzeugt Bewertungen, die niemand abgeben wollte.
 */
function Abschluss({ sterne, rueckblick, i18n, mayWrite, speichert, onSpeichern, testid }: {
  sterne: number | null; rueckblick: string | null; i18n: I18n; mayWrite: boolean;
  speichert: boolean;
  onSpeichern: (sterne: number | null, rueckblick: string | null) => Promise<void>;
  testid: string;
}): React.JSX.Element {
  const t = i18n.t;
  const [entwurfSterne, setEntwurfSterne] = useState<number | null>(null);
  const [entwurfText, setEntwurfText] = useState<string | null>(null);
  const zeigtSterne = entwurfSterne !== null ? (entwurfSterne === 0 ? null : entwurfSterne) : sterne;
  const zeigtText = entwurfText !== null ? entwurfText : (rueckblick ?? "");
  const geaendert = (entwurfSterne !== null && (entwurfSterne === 0 ? null : entwurfSterne) !== sterne)
    || (entwurfText !== null && entwurfText !== (rueckblick ?? ""));

  const inhalt = (
    <>
      <div className="log__rows">
        <div className="log__item">
          <span className="log__label">{t("plp.reviewStars")}</span>
          <span className="log__vals plp__sterne" data-testid={`${testid}-sterne`}
            data-wert={zeigtSterne === null ? "" : String(zeigtSterne)}>
            {[1, 2, 3, 4, 5].map((n) => mayWrite ? (
              <button key={n} type="button" className="plp__stern"
                data-testid={`${testid}-stern-${n}`} disabled={speichert}
                aria-pressed={zeigtSterne !== null && n <= zeigtSterne}
                aria-label={t("plp.reviewStarsOf", { n: String(n) })}
                onClick={() => setEntwurfSterne(n)}>
                {zeigtSterne !== null && n <= zeigtSterne ? "\u2605" : "\u2606"}
              </button>
            ) : (
              <span key={n} aria-hidden="true">
                {zeigtSterne !== null && n <= zeigtSterne ? "\u2605" : "\u2606"}
              </span>
            ))}
            {zeigtSterne === null && (
              <span className="log__hint" data-testid={`${testid}-ohne`}>
                {t("plp.reviewNone")}
              </span>
            )}
            {mayWrite && zeigtSterne !== null && (
              <button type="button" className="bew__weg" disabled={speichert}
                data-testid={`${testid}-zuruecknehmen`}
                onClick={() => setEntwurfSterne(0)}>{t("plp.reviewClear")}</button>
            )}
          </span>
        </div>
        <div className="log__item">
          <span className="log__label">{t("plp.reviewNote")}</span>
          {mayWrite ? (
            <span className="log__vals">
              <textarea className="log__input log__input--text plp__rueckblick"
                rows={3} value={zeigtText} disabled={speichert}
                data-testid={`${testid}-text`} aria-label={t("plp.reviewNote")}
                placeholder={t("plp.reviewNoteHint")}
                onChange={(e) => setEntwurfText(e.target.value)} />
            </span>
          ) : (
            <span className="log__vals" data-testid={`${testid}-textlesen`}>
              {rueckblick ?? <Unknown />}
            </span>
          )}
        </div>
      </div>
      {mayWrite && geaendert && (
        <span className="sup__kformaktionen">
          <button className="btn btn--primary btn--small" disabled={speichert}
            data-testid={`${testid}-speichern`}
            onClick={() => void (async () => {
              const neueSterne = entwurfSterne === null ? sterne
                : (entwurfSterne === 0 ? null : entwurfSterne);
              const neuerText = entwurfText === null ? (rueckblick ?? null)
                : (entwurfText.trim() === "" ? null : entwurfText.trim());
              await onSpeichern(neueSterne, neuerText);
              setEntwurfSterne(null); setEntwurfText(null);
            })()}>{speichert ? t("props.saving") : t("log.save")}</button>
          <button className="btn btn--small" disabled={speichert}
            data-testid={`${testid}-abbrechen`}
            onClick={() => { setEntwurfSterne(null); setEntwurfText(null); }}>
            {t("common.cancel")}
          </button>
        </span>
      )}
    </>
  );

  /*
   * ── UND SIE STEHT NUR DA, WENN SIE AUFGERUFEN WIRD ──────────────
   *
   * BERICHTIGT GEGENUEBER KORREKTURLAUF 002. Dort trat die Bewertung
   * in eine Klappe zurueck, solange der Vorgang lief -- „erreichbar,
   * aber nicht beherrschend". Die Browser-Abnahme hat den
   * Kompromiss verworfen (Abschnitt 20): auch eine Klappe ist eine
   * dauerhafte Karte, die nach etwas fragt, was noch nicht
   * beantwortbar ist, und sie nimmt eine Zeile der Akte dafuer ein.
   *
   * WER ENTSCHEIDET, OB AUFGERUFEN WIRD, steht nicht hier, sondern
   * an den beiden Akten -- und dort je Vorgang GETRENNT
   * (Abschnitt 20.2). Diese Komponente stellt die Maske; wann der
   * Vorgang beurteilbar ist, weiss der Vorgang.
   */
  return (
    <section className="log__group plp__abschluss" data-testid={testid}>
      <h4 className="log__grouptitle">{t("plp.review")}</h4>
      {inhalt}
    </section>
  );
}

/** Der gueltige eingetretene Stand eines Termins -- oder null. */
function istTag(ms: readonly Milestone[], code: string): string | null {
  return ms.find((m) => m.code === code && m.certainty === "actual"
    && m.supersededAt === null)?.happensOn ?? null;
}
/** Tage zwischen zwei Tagen -- oder null, wenn einer fehlt. */
function tageZw(von: string | null, bis: string | null): number | null {
  if (von === null || bis === null) return null;
  return Math.round((Date.parse(`${bis}T00:00:00Z`) - Date.parse(`${von}T00:00:00Z`))
    / (24 * 60 * 60 * 1000));
}

/**
 * PROZESSDATEN -- gerechnet aus dem, was dasteht, und sonst nichts.
 *
 * Fehlt die Grundlage, steht „nicht bewertbar". Das ist keine Floskel:
 * eine Dauer aus einem fehlenden Datum zu schaetzen waere eine Zahl, die
 * aussieht wie eine Messung.
 *
 * UND KEINE SCHULDZUWEISUNG. Hier stehen Tage, keine Urteile. Ob eine
 * lange Laufzeit am Lieferanten lag, am Zoll oder am Wetter, sagt diese
 * Liste nicht -- das sagt der Rueckblick daneben, und den schreibt ein
 * Mensch.
 */
function Prozessdaten({ zeilen, i18n, testid, titel, raster = false }: {
  zeilen: readonly { label: string; wert: string | null }[];
  i18n: I18n; testid: string;
  /**
   * Die Angaben nebeneinander statt untereinander.
   *
   * Das HOMEBASE-Informationsraster (`styles/informationsraster.css`).
   * Es steht hier als EIGENSCHAFT und nicht fest im Baustein, weil
   * die Bestellakte in diesem Block unveraendert bleibt -- sie
   * bekommt es, wenn ihr eigener Block sie erreicht.
   */
  raster?: boolean;
  /**
   * Eine Ueberschrift -- optional, und erst seit die Sendung ZWEI
   * dieser Bloecke untereinander stellt.
   *
   * DER BEFUND AM BILDSCHIRM: „Abfahrt Fernost +3 Tage" stand ohne
   * jede Ueberschrift unter der Bewertung. Die Zahl stimmte; wofuer
   * sie steht, sagte sie nicht. Solange es EIN Block war, trug ihn
   * der Abschluss darueber -- bei zweien tut er das nicht mehr.
   */
  titel?: string;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className={raster ? "log__rows plp__prozessdaten infoRaster"
                           : "log__rows plp__prozessdaten"}
      data-testid={testid} data-raster={raster ? "ja" : "nein"}>
      {titel !== undefined && (
        <p className="plp__prozessdatentitel" data-testid={`${testid}-titel`}>{titel}</p>
      )}
      {zeilen.map((z) => (
        <div key={z.label} className="log__item">
          <span className="log__label">{z.label}</span>
          <span className="log__vals" data-testid={`${testid}-wert`}>
            {z.wert ?? <em className="log__hint">{t("plp.notMeasurable")}</em>}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Die Bestellakte ────────────────────────────────────────────────────── */

function Bestellakte({ id, meta, codes, i18n, mayWrite, onClose, onChanged }: {
  id: number; meta: MetaResponse; codes: readonly MilestoneCodeInfo[];
  i18n: I18n; mayWrite: boolean; onClose: () => void; onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [d, setD] = useState<PurchaseOrderDetail | null>(null);
  const [ladeFehler, setLadeFehler] = useState<unknown>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  /*
   * ── ZWEI BEREICHE, ZWEI ZUSTAENDE ──────────────────────────────
   *
   * DER BEFUND DER BROWSER-ABNAHME (§2). Ein Stift oben schaltete die
   * GANZE Akte: Kopfdaten und Positionen zugleich. Bei einer langen
   * Bestellung liegen die Positionen weit unten, der Stift weit oben
   * -- wer eine Menge aendern will, sucht erst den Schalter und dann
   * den Weg zurueck.
   *
   * JEDER BEREICH SCHALTET SICH SELBST, und jeder hat seinen eigenen
   * Speichern-Bereich an Ort und Stelle. Finanzen und Termine haben
   * ihre eigenen Masken ohnehin schon; neu getrennt sind Kopf und
   * Positionen.
   *
   * KEINE NEUE BEARBEITUNGSARCHITEKTUR. Es ist derselbe Entwurf,
   * dieselbe Speicherung, dieselbe Abbruchlogik -- nur zweimal statt
   * einmal, und jeweils dort, wo gearbeitet wird.
   */
  const [kopfBearbeitet, setKopfBearbeitet] = useState(false);
  const [posBearbeitet, setPosBearbeitet] = useState(false);
  const [kopf, setKopf] = useState<Kopfentwurf | null>(null);
  const [zeilen, setZeilen] = useState<Record<number, Zeilenentwurf>>({});
  /*
   * ── ZWEI ARTEN VON POSITION ────────────────────────────────────
   *
   * DER BEFUND AUS DER PRAXIS (§10). Es gibt regelmaessig Muster,
   * Ersatzteile, Komponenten und Einmalbeschaffungen -- Dinge, die
   * nie in den Artikelstamm gehoeren, weil sie nie verkauft werden.
   * Die Maske bot dafuer nur eine Auswahl aus dem Artikelstamm an.
   *
   * SIE SIND KEINE NOTIZ UND KEIN BLINDARTIKEL. Eine freie Position
   * ist eine vollwertige Bestellzeile: sie zaehlt zum Bestellwert,
   * faehrt in der Sendung mit und steht in der ankommenden Ware.
   *
   * DAS DATENMODELL TRUG SIE SCHON. `supplier_article_id` ist
   * nullbar, `description`, `unit` und `supplier_article_number_raw`
   * stehen seit der Belegerfassung da, und `pol_gegenstand_chk`
   * verlangt Artikel ODER Bezeichnung. Auch der Fachkern nahm sie
   * entgegen. Es fehlte ausschliesslich der Weg dorthin.
   */
  const [neu, setNeu] = useState<{
    art: "artikel" | "frei";
    sa: string; menge: string; preis: string;
    bezeichnung: string; einheit: string; liefernummer: string; notiz: string;
  } | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [regel, setRegel] = useState<{ min: string; max: string; anker: string; quelle: string; wortlaut: string } | null>(null);
  const [artikel, setArtikel] = useState<readonly { id: number; label: string }[]>([]);
  /* Was der Knopf oben gewaehlt hat. Er stellt die Maske unten ein --
     mehr passiert hier nicht. */
  const [vorwahl, setVorwahl] = useState<MilestoneCode | null>(null);
  const vorwahlVerbraucht = useCallback(() => setVorwahl(null), []);

  /*
   * ── DER STAND DER AKTE ────────────────────────────────────────
   *
   * DER BEFUND AUS DER PRAXIS (PO70305, §13). Die Positionen zeigten
   * 45.508,64 USD, die Finanzen darunter 43.208,64 USD -- genau die
   * zwei nachtraeglich ergaenzten Positionen fehlten. Nach einem
   * Neuladen der Seite stimmte es.
   *
   * DIE URSACHE war kein Rechenfehler, sondern ein zweites Gedaechtnis:
   * der Finanzbereich holt seine Zahlen selbst und hing am Schluessel
   * `fin-${payments.length}`. Eine neue POSITION aendert die Zahl der
   * ZAHLUNGEN nicht -- also lud er nicht nach und zeigte weiter, was er
   * beim Einhaengen gelesen hatte.
   *
   * Ein Zaehler, den die Akte bei JEDEM Nachladen erhoeht, bindet die
   * beiden aneinander: was die Akte neu liest, liest der Finanzbereich
   * mit. Nicht der Betrag ist das Signal, sondern das Ereignis --
   * sonst bliebe eine Preisaenderung unsichtbar, die zufaellig
   * dieselbe Summe ergibt.
   */
  const [stand, setStand] = useState(0);
  const laden = useCallback(async () => {
    try { setD(await api.purchaseOrder(id)); setLadeFehler(null); }
    catch (e) { setLadeFehler(e); }
    finally { setStand((n) => n + 1); }
  }, [id]);
  useEffect(() => { void laden(); }, [laden]);

  /* Die waehlbaren Beziehungen sind die des LIEFERANTEN dieser
     Bestellung -- eine Bestellung traegt nur Positionen ihres eigenen
     Partners, und die Maske bietet gar nichts anderes an. */
  useEffect(() => {
    if (d === null) return;
    let aktuell = true;
    void api.supplierArticles(d.supplierId).then((r) => {
      if (!aktuell) return;
      setArtikel(r.items.map((a) => ({
        id: a.relationId,
        label: `${a.articleNumber} · ${a.shortDescription ?? ""}`.trim(),
      })));
    }).catch(() => undefined);
    return () => { aktuell = false; };
  }, [d]);

  if (ladeFehler !== null) return <p className="log__error">{errorText(i18n, ladeFehler)}</p>;
  if (d === null) return <p className="work__empty">{t("common.loading")}</p>;

  const akte = d;
  const melden = (e: unknown): void => setFehler(e);
  const nachher = (): void => { setFehler(null); void laden(); onChanged(); };

  /*
   * ── IST DIESE BESTELLUNG SO WEIT, DASS MAN SIE BEURTEILEN KANN? ──
   *
   * DER BEFUND DER BROWSER-ABNAHME (Abschnitt 20.1). „Abschluss"
   * stand an jeder Bestellung vom ersten Tag an -- mit fuenf leeren
   * Sternen und der Frage, wie es gelaufen ist. An einer Bestellung,
   * die vorgestern angelegt wurde, ist das keine Frage, sondern eine
   * leere Karte, die man jedes Mal ueberliest.
   *
   * ARNES OPERATIVE DEFINITION: versendet heisst bewertbar. Der
   * Zeitpunkt dafuer ist der Eingang beim Logistikpartner -- da hat
   * die Ware das Haus des Lieferanten verlassen, und genau seine
   * Leistung wird hier beurteilt. Danach macht die Sendung weiter,
   * aber nicht mehr er (siehe die Begruendung oben am Aufruf).
   *
   * UND DER ADMINISTRATIVE ABSCHLUSS zaehlt daneben: eine
   * GESCHLOSSENE Bestellung ist fertig, auch wenn nie eine Zuordnung
   * ihren Eingang getragen hat -- Altvorgaenge und Direktlieferungen
   * gibt es. STORNIERT zaehlt NICHT: an einer zurueckgenommenen
   * Bestellung gibt es keine Leistung zu beurteilen.
   *
   * WAS SCHON BEWERTET IST, BLEIBT SICHTBAR. Sonst verschwaende eine
   * Regel die Arbeit, die jemand geleistet hat -- und niemand koennte
   * einen Irrtum zurueckziehen.
   *
   * GESPERRT STATT FORT waere die Ausweichloesung gewesen; Abschnitt
   * 20.1 nennt sie ausdruecklich die zweite Wahl. Es gibt hier keinen
   * technischen Grund fuer Sichtbarkeit: was fehlt, sagt die
   * Prozessfuehrung oben in der Akte ohnehin.
   */
  const bewertbar = akte.forwarderReceivedOn !== null
    || akte.status === "geschlossen"
    || akte.reviewStars !== null
    || (akte.reviewNote ?? "") !== "";

  /*
   * DER STIFT SCHALTET DIE GANZE AKTE -- Kopf UND Positionen.
   *
   * Vorher war der Kopf gar nicht aenderbar und die Positionen trugen
   * eigene kleine Formulare: zwei Bedienmuster in einer Maske. Jetzt
   * gilt hier dasselbe wie in der Lieferantenakte -- ein Stift oben
   * rechts, und was bearbeitbar ist, wird zum Feld.
   */
  function kopfStarten(): void {
    setKopf({
      nummer: akte.dteNumber, supplier: String(akte.supplierId), datum: akte.orderedAt,
      incoterm: akte.incoterm ?? "", waehrung: akte.currency ?? "", notiz: akte.note ?? "",
    });
    setKopfBearbeitet(true); setFehler(null);
  }
  function kopfAbbrechen(): void {
    setKopfBearbeitet(false); setKopf(null); setFehler(null);
  }
  function positionenStarten(): void {
    setZeilen(Object.fromEntries(akte.lines.map((l) => [l.id, {
      menge: String(l.quantity), preis: l.unitPriceAmount ?? "", notiz: l.note ?? "",
    }])));
    setPosBearbeitet(true); setFehler(null);
  }
  function positionenAbbrechen(): void {
    setPosBearbeitet(false); setZeilen({}); setNeu(null); setFehler(null);
  }

  /*
   * GESPEICHERT WIRD, WAS SICH GEAENDERT HAT.
   *
   * Nur geaenderte Felder gehen mit -- und nur geaenderte Positionen.
   * Wer die Akte oeffnet, etwas anderes ansieht und wieder schliesst,
   * hinterlaesst keine Spur im Protokoll. Ein „alles mitschicken"
   * erzeugte Protokollzeilen fuer Aenderungen, die niemand gemacht hat.
   */
  async function kopfSpeichern(): Promise<void> {
    if (kopf === null) return;
    setSpeichert(true);
    try {
      const aenderung: Record<string, unknown> = {};
      if (kopf.nummer.trim() !== akte.dteNumber) aenderung["dteNumber"] = kopf.nummer.trim();
      if (Number(kopf.supplier) !== akte.supplierId) aenderung["supplierId"] = Number(kopf.supplier);
      if (kopf.datum !== akte.orderedAt) aenderung["orderedAt"] = kopf.datum;
      if ((kopf.incoterm || null) !== akte.incoterm) aenderung["incoterm"] = kopf.incoterm || null;
      if ((kopf.waehrung || null) !== akte.currency) aenderung["currency"] = kopf.waehrung || null;
      if ((kopf.notiz.trim() || null) !== akte.note) aenderung["note"] = kopf.notiz.trim() || null;
      if (Object.keys(aenderung).length > 0) {
        await api.updatePurchaseOrder(id, aenderung as never);
      }
      kopfAbbrechen(); nachher();
    } catch (e) { melden(e); } finally { setSpeichert(false); }
  }

  /*
   * DIE POSITIONEN SPEICHERN SICH SELBST.
   *
   * Vorher ging beides in einem Aufruf -- Kopfaenderungen und
   * Positionsaenderungen. Das war bequem, solange es EINEN Schalter
   * gab; mit zwei Bereichen waere es die Vermischung, die §2
   * abschafft.
   *
   * GESPEICHERT WIRD WEITERHIN NUR, WAS SICH GEAENDERT HAT. Wer die
   * Akte oeffnet, etwas ansieht und wieder schliesst, hinterlaesst
   * keine Protokollzeile.
   */
  async function positionenSpeichern(): Promise<void> {
    setSpeichert(true);
    try {
      for (const l of akte.lines) {
        const z = zeilen[l.id];
        if (z === undefined) continue;
        if (Number(z.menge) === l.quantity
            && (z.preis.trim() || null) === l.unitPriceAmount
            && (z.notiz.trim() || null) === l.note) continue;
        await api.updatePurchaseOrderLine(id, l.id, {
          supplierArticleId: l.supplierArticleId, quantity: Number(z.menge),
          unitPriceAmount: z.preis.trim() === "" ? null : z.preis.trim(),
          note: z.notiz.trim() === "" ? null : z.notiz.trim(),
        });
      }
      positionenAbbrechen(); nachher();
    } catch (e) { melden(e); } finally { setSpeichert(false); }
  }

  async function positionSpeichern(): Promise<void> {
    if (neu === null || neu.menge === "") return;
    /* Jede Art hat ihre eigene Mindestangabe: der Artikel braucht den
       Artikel, die freie Position eine Bezeichnung. Das ist dieselbe
       Bedingung, die `pol_gegenstand_chk` in der Datenbank haelt --
       hier steht sie, damit niemand gegen eine Schranke laeuft. */
    if (neu.art === "artikel" && neu.sa === "") return;
    if (neu.art === "frei" && neu.bezeichnung.trim() === "") return;
    setSpeichert(true);
    try {
      const preis = neu.preis.trim() === "" ? null : neu.preis.trim();
      await api.addPurchaseOrderLine(id, neu.art === "artikel"
        ? { supplierArticleId: Number(neu.sa), quantity: Number(neu.menge),
            unitPriceAmount: preis }
        : { supplierArticleId: null, quantity: Number(neu.menge),
            unitPriceAmount: preis,
            description: neu.bezeichnung.trim(),
            unit: neu.einheit.trim() === "" ? null : neu.einheit.trim(),
            supplierArticleNumberRaw:
              neu.liefernummer.trim() === "" ? null : neu.liefernummer.trim(),
            note: neu.notiz.trim() === "" ? null : neu.notiz.trim() });
      setNeu(null); nachher();
    } catch (e) { melden(e); } finally { setSpeichert(false); }
  }
  async function regelSpeichern(): Promise<void> {
    if (regel === null) return;
    setSpeichert(true);
    try {
      /* Der gewaehlte Wert traegt beide Raeume: „ms:pi_approved" oder
         „pay:deposit_paid". Getrennt wird hier und nicht im Fachkern --
         dort stehen zwei Felder, und zwei Felder sind die Wahrheit. */
      const [raum, code] = regel.anker.split(":") as [string, string];
      await api.setLeadTimeRule({
        purchaseOrderId: id, daysMin: Number(regel.min), daysMax: Number(regel.max),
        anchorKind: raum === "pay" ? "payment" : "milestone",
        anchorCode: raum === "pay" ? null : (code as MilestoneCode),
        paymentAnchor: raum === "pay" ? (code as never) : null,
        source: regel.quelle as never,
        quotedAs: regel.wortlaut.trim() === "" ? null : regel.wortlaut.trim(),
      });
      setRegel(null); nachher();
    } catch (e) { melden(e); } finally { setSpeichert(false); }
  }

  /*
   * ══════════════════════════════════════════════════════════════════
   * DREI BEZUGSPUNKTE, UND NUR DIESE DREI (Abschnitt 7 bis 10)
   * ══════════════════════════════════════════════════════════════════
   *
   * Die Lieferzeitregel beantwortet: ab welchem Ereignis beginnt die
   * Lead Time? Arnes Entscheidung:
   *
   *   PI freigegeben       ein Termin (pi_approved)
   *   Anzahlung geleistet  eine ZAHLUNG
   *   Vollstaendig bezahlt eine ZAHLUNG
   *
   * BESTELLT FAELLT FORT. Ohne Bestellung gibt es keine PO, und eine PI
   * gibt es in diesen Prozessen immer -- „Bestellt" liefert damit keinen
   * zusaetzlichen fachlichen Startpunkt. Es war bis hierher der
   * Vorgabewert; neuer Vorgabewert ist „PI freigegeben".
   *
   * WARENEINGANG FAELLT FORT. Er ist das ERGEBNIS eines
   * Beschaffungslaufs und kein Startpunkt; eine Lieferzeit, die darauf
   * ankert, misst von ihrem eigenen Ziel aus.
   *
   * BESTEHENDE REGELN WERDEN NICHT ZERSTOERT. Steht an dieser
   * Bestellung eine Regel auf einem Punkt, der nicht mehr angeboten
   * wird, steht er in der Auswahl trotzdem da -- als ihr aktueller
   * Wert. Sonst setzte das blosse Oeffnen der Maske stillschweigend
   * einen anderen Bezugspunkt. Dieselbe Begruendung wie in 0081: die
   * Unterscheidung zwischen BESTEHEND und NEU gehoert in die Maske.
   */
  const ankerWert = (k: string, c: string): string => `${k}:${c}`;
  const ANKER_VORGABE = ankerWert("ms", "pi_approved");
  const ankerName = (wert: string): string => {
    const [raum, code] = wert.split(":") as [string, string];
    return raum === "pay" ? i18n.tCode("payAnchor", code, code)
      : i18n.tCode("ms", code, code);
  };
  const regulaere = [ANKER_VORGABE, ankerWert("pay", "deposit_paid"),
                     ankerWert("pay", "paid_in_full")];
  const bestehend = akte.leadTimeRule === null ? null
    : akte.leadTimeRule.rule.anchorKind === "payment"
      ? ankerWert("pay", akte.leadTimeRule.rule.paymentAnchor!)
      : ankerWert("ms", akte.leadTimeRule.rule.anchorCode!);
  const anker = [...regulaere,
    ...(bestehend !== null && !regulaere.includes(bestehend) ? [bestehend] : [])];
  /*
   * WORAN ETWAS HAENGT, DAS SAGT DIE MASKE VORHER.
   *
   * Der Lieferant laesst sich nur wechseln, solange keine Position an
   * ihm haengt; die Waehrung nur, solange kein Preis in ihr steht. Beide
   * Regeln haelt der Fachkern -- hier stehen sie nochmal, damit niemand
   * erst durch eine Absage davon erfaehrt.
   */
  const supplierFest = akte.lines.length > 0;
  const waehrungFest = akte.lines.some((l) => l.unitPriceAmount !== null);

  return (
    <HilfeRahmen>
    <div className="plp">
      <header className="sup__akte">
        <button className="btn sup__zurueck" onClick={onClose}>{t("plp.back")}</button>
        <div className="sup__ident">
          <h2 className="sup__kurzname">{akte.dteNumber}</h2>
          <span className="sup__firma">{akte.supplierName}</span>
        </div>
        <span className="sup__tag">{i18n.tCode("poStatus", akte.status)}</span>
        {/*
          * DIE AKTIVE SENDUNG -- oder ausdruecklich keine.
          *
          * „keine aktive Consolidation" ist eine Aussage und kein
          * fehlender Wert: die Bestellung ist zurueckgestellt, nicht
          * unbearbeitet. Beides als leere Stelle zu zeigen hiesse, den
          * haeufigsten Fall unsichtbar zu machen.
          */}
        {akte.consolidationNumber !== null ? (
          <span className="sup__tag sup__tag--standard"
            data-testid="plp-aktivecn">{akte.consolidationNumber}</span>
        ) : akte.releasedAssignments.length > 0 && (
          <span className="sup__tag" data-testid="plp-keinecn">{t("plp.noActiveCn")}</span>
        )}
        <HilfeSchalter />
        {/*
          * ── DER GLOBALE STIFT IST FORT ────────────────────────────
          *
          * DER BEFUND DER BROWSER-ABNAHME (§2). Er schaltete die GANZE
          * Akte -- Kopf und Positionen zugleich -- und sass oben,
          * waehrend die Positionen weit unten liegen. Wer eine Menge
          * aendern wollte, suchte erst den Schalter und dann den Weg
          * zurueck.
          *
          * JEDER BEREICH HAT JETZT SEINEN EIGENEN ZUGANG, dort, wo
          * gearbeitet wird. Der Auftrag laesst einen globalen Stift
          * ausdruecklich nur fuer „kurze, klar abgegrenzte Kopfdaten"
          * zu -- und die Bestellakte ist das Gegenteil davon.
          */}
      </header>
      {fehler !== null && (
        <p className="log__error" role="alert" data-testid="plp-fehler">{errorText(i18n, fehler)}</p>
      )}

      {/* BEDIENUNG NACH OBEN, VERLAUF NACH UNTEN. Siehe `Arbeitszone`. */}
      <Arbeitszone prozess={akte.process} titel={t("plp.openProcessData")}
        i18n={i18n} mayWrite={mayWrite} testid="plp-zone"
        onErfassen={(c) => setVorwahl(c)} />

      <div className="log__cluster sup__cluster">
        <section className="log__group" data-testid="plp-kopf">
          <h4 className="log__grouptitle">
            {t("plp.order")}
            {/*
              * DER ZUGANG STEHT IM BEREICHSKOPF -- dort, wo das steht,
              * was er bearbeitbar macht.
              *
              * UND NUR DER ZUGANG (Abschnitt 22). Vorher stand hier im
              * Bearbeitungsmodus „Abbrechen" -- und in der Maske
              * darunter ein zweites „Abbrechen". Zwei gleich
              * benannte Knoepfe fuer eine Handlung, und beim Lesen
              * die Frage, ob sie dasselbe tun.
              *
              * JETZT TRAEGT DIE MASKE IHRE AKTIONEN. Speichern und
              * Abbrechen stehen dort, wo die Bearbeitung stattfindet;
              * der Kopf fuehrt hinein und nicht auch heraus. Dass man
              * im Bearbeitungsmodus ist, sagt die Maske selbst --
              * dafuer braucht es keinen gedrueckten Knopf daneben.
              */}
            {mayWrite && !kopfBearbeitet && (
            <button type="button" data-testid="plp-kopfstift"
              className="subnav__pencil plp__stift"
              title={t("props.edit")} aria-label={t("plp.editAria")}
              onClick={kopfStarten}>
              <span aria-hidden="true">{PENCIL}</span>
              <span className="plp__stifttext">{t("props.edit")}</span>
            </button>
          )}
          </h4>
          <HilfeHinweis bereich="po.incoterm" />
          {kopfBearbeitet && kopf !== null ? (
            <div className="sup__kform" data-testid="plp-kopfmaske">
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.dteNumber")}</span>
                <input className="log__input log__input--text" value={kopf.nummer} data-feld="dteNumber"
                  aria-label={t("plp.dteNumber")} placeholder={t("plp.numberExample")}
                  onChange={(e) => setKopf({ ...kopf, nummer: e.target.value.toUpperCase() })} /></label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("sup.title")}</span>
                <select className="log__input" value={kopf.supplier} data-feld="supplierId"
                  aria-label={t("sup.title")} disabled={supplierFest}
                  onChange={(e) => setKopf({ ...kopf, supplier: e.target.value })}>
                  {meta.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {supplierFest && (
                  <span className="log__hint" data-testid="plp-supplierfest">{t("plp.supplierLocked")}</span>
                )}
              </label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.orderedAt")}</span>
                <input className="log__input log__input--text" type="date" value={kopf.datum}
                  data-feld="orderedAt" aria-label={t("plp.orderedAt")}
                  onChange={(e) => setKopf({ ...kopf, datum: e.target.value })} /></label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("sup.incoterm")}</span>
                <select className="log__input" value={kopf.incoterm} data-feld="incoterm"
                  aria-label={t("sup.incoterm")}
                  onChange={(e) => setKopf({ ...kopf, incoterm: e.target.value })}>
                  <option value="">{t("sup.optional")}</option>
                  {meta.incoterms.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
                </select></label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.currency")}</span>
                <select className="log__input" value={kopf.waehrung} data-feld="currency"
                  aria-label={t("plp.currency")} disabled={waehrungFest}
                  onChange={(e) => setKopf({ ...kopf, waehrung: e.target.value })}>
                  <option value="">{t("sup.optional")}</option>
                  {meta.currencies.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
                </select>
                {waehrungFest && (
                  <span className="log__hint" data-testid="plp-waehrungfest">{t("plp.currencyLocked")}</span>
                )}
              </label>
              <label className="sup__kfeld sup__kfeld--breit">
                <span className="sup__klabel">{t("plp.note")}</span>
                <input className="log__input log__input--text" value={kopf.notiz} data-feld="note"
                  aria-label={t("plp.note")} placeholder={t("sup.optional")}
                  onChange={(e) => setKopf({ ...kopf, notiz: e.target.value })} /></label>
              <span className="sup__kformaktionen">
                <button className="btn btn--primary btn--small" data-testid="plp-kopfspeichern"
                  disabled={speichert} onClick={() => void kopfSpeichern()}>{t("log.save")}</button>
                <button className="btn btn--small" disabled={speichert}
                  data-testid="plp-kopfabbrechen"
                  onClick={kopfAbbrechen}>{t("common.cancel")}</button>
              </span>
            </div>
          ) : (
            <div className="log__rows">
              <div className="log__item"><span className="log__label">{t("plp.dteNumber")}</span>
                <span className="log__vals">{akte.dteNumber}</span></div>
              {/*
                * ── DIE BESCHRIFTUNG SAGT DIE QUELLE SCHON (MICRO 002)
                *
                * HIER STANDEN ZWEI ZEILEN, und beide sind fort:
                *
                *     Quelle              DTE
                *                         In HOMEBASE übernommen:
                *                         manuell eingegeben
                *
                * Die erste war in FOLLOW-UP 002 §11 entstanden, um
                * Quelle und Erfassungsweg zu TRENNEN -- und das war
                * richtig. Sie war aber eine Antwort auf eine Frage,
                * die niemand stellt: „DTE-Bestellnummer" sagt die
                * Quelle bereits, und eine Zeile „Quelle: DTE"
                * darunter legt nahe, es koennte auch andere geben.
                * Es gibt keine. HOMEBASE erzeugt keine PO-Nummer
                * (0069), besitzt kein zweites Nummernsystem und legt
                * ohne DTE-formatierte Nummer keine Bestellung an --
                * `dte_number` ist NOT NULL mit `^PO7[0-9]{4,}$`.
                *
                * DER ERFASSUNGSWEG IST MIT IHR GEGANGEN. Er beschreibt
                * nicht die Quelle, sondern wie die Nummer nach
                * HOMEBASE kam, und er ist heute ueberall `manual`. Als
                * gleichrangige Fachzeile stand er neben Angaben, die
                * taeglich gebraucht werden; als Unterzeile einer
                * Quellenzeile, die es nicht mehr gibt, haette er
                * keinen Ort.
                *
                * ER BLEIBT TECHNISCH DA: `number_origin` und
                * `capture_mode` stehen unveraendert in der Datenbank
                * und kommen weiter in der API-Antwort
                * (`numberOrigin`, `captureMode`). Wer sie fuer ein
                * Audit braucht, liest sie dort. Eine eigene
                * Hinweisstruktur in der Karte dafuer zu bauen waere
                * mehr Oberflaeche fuer eine Angabe, die im
                * Arbeitsalltag nicht gebraucht wird.
                */}
              <div className="log__item"><span className="log__label">{t("plp.orderedAt")}</span>
                <span className="log__vals">{kurzDatum(akte.orderedAt, i18n.lang)}</span></div>
              <div className="log__item"><span className="log__label">{t("sup.incoterm")}</span>
                <span className="log__vals">{akte.incoterm ?? <Unknown />}</span></div>
              <div className="log__item"><span className="log__label">{t("plp.currency")}</span>
                <span className="log__vals">{akte.currency ?? <Unknown />}</span></div>
              {/*
                * ── DER KOPF SAGT ES AUCH ──────────────────────────
                *
                * DER BEFUND DER BROWSER-ABNAHME. Positionen und
                * Finanzen kennzeichneten den unvollstaendigen
                * Bestellwert bereits; der KOPF nannte eine Zahl --
                * und er ist die Stelle, auf die man zuerst schaut.
                *
                * KEINE ZWEITE RECHNUNG. Es ist dieselbe Zaehlung
                * (`linesWithoutPrice`), die auch die anderen beiden
                * Stellen lesen. Drei Anzeigen, eine Wahrheit.
                *
                * DER TEILWERT BLEIBT STEHEN. Er ist richtig fuer
                * das, was er enthaelt -- er ist nur nicht der ganze
                * Auftrag, und das steht jetzt daneben.
                */}
              <div className="log__item"><span className="log__label">{t("plp.orderValue")}</span>
                <span className="log__vals" data-testid="plp-bestellwert"
                  data-vollstaendig={akte.linesWithoutPrice === 0 ? "ja" : "nein"}>
                  {wertText(akte, i18n)}
                  {akte.linesWithoutPrice > 0 && akte.totalAmount !== null && (
                    <em className="plp__wertoffen" data-testid="plp-kopfunvollstaendig">
                      {t("plp.valueIncomplete")}
                    </em>
                  )}
                </span></div>
              <div className="log__item"><span className="log__label">{t("plp.note")}</span>
                <span className="log__vals">{akte.note ?? <Unknown />}</span></div>
            </div>
          )}
          {/* Die Nummer ist abgetippt, nicht abgeglichen -- und das steht
              da, statt es zu verschweigen. */}
          <p className="log__hint">{t("plp.numberHint")}</p>
        </section>

        {/*
          * DIE TRANSPORTGESCHICHTE -- getrennt vom Beschaffungsstand.
          *
          * Ein Release aendert am Prozess der Bestellung nichts: PI,
          * Artwork, QC, Versandbereitschaft und Zahlungen stehen
          * unveraendert in ihren eigenen Abschnitten. Was hier steht,
          * ist eine Logistikauskunft und kein Bestellstatus.
          */}
        {akte.releasedAssignments.length > 0 && (
          <section className="log__group" data-testid="plp-cnhistorie">
            <h4 className="log__grouptitle">{t("plp.releasedOrders")}</h4>
            {akte.consolidationNumber === null && (
              <p className="log__hint" data-testid="plp-zurueckgestellt">{t("plp.heldBack")}</p>
            )}
            {/*
              * ── EINE HISTORIENZEILE LIEST SICH VON LINKS ──────────
              *
              * DER BEFUND DER ABNAHME: die Nummer stand links, alles
              * andere rechtsbuendig am Kartenrand -- Datum, Person und
              * Grund in einem Block, der am rechten Rand umbrach. Was
              * zusammengehoert, stand am weitesten auseinander.
              *
              * JETZT GRUPPIERT UND LINKSBUENDIG: Nummer prominent,
              * darunter der Grund, darunter Datum und Person als
              * Beiwerk. Dieselben Angaben, dieselbe Reihenfolge,
              * dieselben Uebersetzungen.
              *
              * NUR FORMATIERUNG. Weder Revisions-, Freigabe-,
              * Nummern- noch Historienlogik ist angefasst -- und der
              * GRUND bleibt ausdruecklich Freitext und wird nicht als
              * Status ausgezeichnet. Siehe Rueckgabe, Abschnitt 4.
              */}
            <ul className="plp__historie" data-testid="plp-cnhistorie-liste">
              {akte.releasedAssignments.map((r) => (
                <li key={r.id} className="plp__historiezeile" data-testid="plp-historiezeile">
                  <span className="plp__historienummer">{r.consolidationNumber}</span>
                  <span className="plp__historiegrund">{r.releaseReason}</span>
                  <span className="plp__historiemeta">
                    {t("plp.releasedOn", { date: kurzDatum(r.releasedAt.slice(0, 10), i18n.lang) })}
                    {r.releasedBy !== null && ` · ${r.releasedBy.name}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/*
          * DIE DOKUMENTE DER BESTELLUNG.
          *
          * Sie stehen NACH dem Prozessstand und VOR der Lieferzeitregel:
          * sie sind die dokumentarische Wahrheit zu dem, was oben als
          * strukturierte Wahrheit steht -- und beantworten die Frage
          * „worauf beruht das eigentlich?".
          */}
        <Bestelldokumente poId={id} i18n={i18n} mayWrite={mayWrite} />

        <section className="log__group">
          <h4 className="log__grouptitle">
            {t("plp.leadTime")}
            {/*
              * ── DASSELBE MUSTER WIE IN DER BESTELLUNG ───────────
              *
              * DER BEFUND DER BROWSER-ABNAHME (§6). „Bestellung"
              * trug „Bearbeiten" im Kopf, die Lieferzeitregel einen
              * grossen Knopf im Fuss -- „Lieferzeitregel fuer diese
              * Bestellung". Beides heisst dasselbe: diesen Bereich
              * bearbeiten.
              *
              * Ein Bereich, der bearbeitbar ist, traegt die Aktion im
              * Kopf. Einer, der es nicht ist, traegt keine. Der
              * Kartenkopf-Baustein aus dem Vorblock haelt sie auf
              * einer Linie mit den uebrigen.
              */}
            {/* Und auch hier nur der Zugang -- siehe die Begruendung
                am Kopf der Bestellung (Abschnitt 22). */}
            {mayWrite && regel === null && (
              <button type="button" data-testid="plp-regelstift"
                className="subnav__pencil plp__stift"
                title={t("props.edit")} aria-label={t("plp.editAria")}
                onClick={() => setRegel({ min: "", max: "", anker: bestehend ?? ANKER_VORGABE,
                                          quelle: "supplier_pi", wortlaut: "" })}>
                <span aria-hidden="true">{PENCIL}</span>
                <span className="plp__stifttext">{t("props.edit")}</span>
              </button>
            )}
          </h4>
          <HilfeHinweis bereich="po.leadtime" />
          {akte.leadTimeRule === null ? (
            <p className="log__hint">{t("plp.noRule")}</p>
          ) : (
            <div className="log__rows">
              <div className="log__item"><span className="log__label">{t("plp.duration")}</span>
                <span className="log__vals">
                  {t("plp.days", { min: String(akte.leadTimeRule.rule.daysMin),
                                   max: String(akte.leadTimeRule.rule.daysMax) })}</span></div>
              <div className="log__item"><span className="log__label">{t("plp.anchor")}</span>
                <span className="log__vals" data-testid="plp-regelanker">
                  {ankerName(bestehend!)}
                  {/* FEHLT DAS DATUM, STEHT DA WARUM. „Kein Forecast"
                      ohne Grund liest sich wie ein Fehler; mit Grund
                      ist es der Hinweis, was zu tun ist -- siehe
                      `LeadTimeRuleUse.anchorGap`. */}
                  {akte.leadTimeRule.anchorOn === null
                    ? ` · ${akte.leadTimeRule.anchorGap === "bestellwert_unvollstaendig"
                        ? t("plp.anchorValueIncomplete") : t("plp.anchorMissing")}`
                    : ` · ${kurzDatum(akte.leadTimeRule.anchorOn, i18n.lang)}`}</span></div>
              <div className="log__item"><span className="log__label">{t("plp.scope")}</span>
                <span className="log__vals">{i18n.tCode("plpScope", akte.leadTimeRule.rule.scope)}</span></div>
              <div className="log__item"><span className="log__label">{t("plp.source")}</span>
                <span className="log__vals">{i18n.tCode("plpSource", akte.leadTimeRule.rule.source)}</span></div>
              {akte.leadTimeRule.rule.quotedAs !== null && (
                <div className="log__item"><span className="log__label">{t("plp.quoted")}</span>
                  <span className="log__vals">{akte.leadTimeRule.rule.quotedAs}</span></div>
              )}
            </div>
          )}
          {/* DER ZWEITE KNOPF IM FUSS IST FORT. Er tat dasselbe wie der
              Stift im Kopf -- zwei Wege zu derselben Handlung sind
              einer zu viel, und der untere trug dazu eine andere
              Beschriftung fuer dieselbe Sache. */}
          {mayWrite && regel !== null && (
            <div className="sup__kform" data-testid="plp-regelmaske">
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.daysMin")}</span>
                <input className="log__input log__input--text" type="number" value={regel.min}
                  data-feld="daysMin" aria-label={t("plp.daysMin")}
                  onChange={(e) => setRegel({ ...regel, min: e.target.value })} /></label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.daysMax")}</span>
                <input className="log__input log__input--text" type="number" value={regel.max}
                  data-feld="daysMax" aria-label={t("plp.daysMax")}
                  onChange={(e) => setRegel({ ...regel, max: e.target.value })} /></label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.anchor")}</span>
                <select className="log__input" value={regel.anker} data-feld="anchorCode"
                  aria-label={t("plp.anchor")}
                  onChange={(e) => setRegel({ ...regel, anker: e.target.value })}>
                  {anker.map((w) => (
                    <option key={w} value={w}>{ankerName(w)}</option>
                  ))}
                </select></label>
              <label className="sup__kfeld"><span className="sup__klabel">{t("plp.source")}</span>
                <select className="log__input" value={regel.quelle} data-feld="source"
                  aria-label={t("plp.source")}
                  onChange={(e) => setRegel({ ...regel, quelle: e.target.value })}>
                  {LEAD_TIME_SOURCES.map((q) => (
                    <option key={q} value={q}>{i18n.tCode("plpSource", q)}</option>
                  ))}
                </select></label>
              <label className="sup__kfeld sup__kfeld--breit">
                <span className="sup__klabel">{t("plp.quoted")}</span>
                <input className="log__input log__input--text" value={regel.wortlaut}
                  data-feld="quotedAs" aria-label={t("plp.quoted")}
                  placeholder={t("plp.quotedHint")}
                  onChange={(e) => setRegel({ ...regel, wortlaut: e.target.value })} /></label>
              <span className="sup__kformaktionen">
                <button className="btn btn--primary btn--small" data-testid="plp-regelspeichern"
                  disabled={speichert} onClick={() => void regelSpeichern()}>{t("log.save")}</button>
                <button className="btn btn--small" disabled={speichert}
                  data-testid="plp-regelabbrechen"
                  onClick={() => setRegel(null)}>{t("common.cancel")}</button>
              </span>
            </div>
          )}
        </section>
      </div>

      <section className="log__group plp__positionen">
        <h4 className="log__grouptitle">
          {t("plp.lines")}
          {/*
            * ── DER BEREICH SCHALTET SICH SELBST ──────────────────
            *
            * Stift und „+ Position" stehen im Kopf DIESES Bereichs --
            * nicht oben an der Akte. Wer eine Menge aendern will,
            * findet den Schalter dort, wo die Mengen stehen.
            */}
          {mayWrite && (
            <button className="btn btn--small sup__plus" data-testid="plp-positionneu"
              disabled={neu !== null}
              onClick={() => setNeu({ art: "artikel", sa: "", menge: "", preis: "",
                bezeichnung: "", einheit: "", liefernummer: "", notiz: "" })}>
              {t("plp.addLine")}
            </button>
          )}
          {/* Und auch hier nur der Zugang -- siehe die Begruendung am
              Kopf der Bestellung (Abschnitt 22). */}
          {mayWrite && !posBearbeitet && (
            <button type="button" data-testid="plp-posstift"
              className="subnav__pencil plp__stift"
              title={t("props.edit")} aria-label={t("plp.editAria")}
              onClick={positionenStarten}>
              <span aria-hidden="true">{PENCIL}</span>
              <span className="plp__stifttext">{t("props.edit")}</span>
            </button>
          )}
        </h4>
        {akte.lines.length === 0 ? <p className="log__hint">{t("plp.noLines")}</p> : (
          <div className="sup__tablewrap">
            <table className="sup__table">
              <thead><tr>
                <th>{t("plp.article")}</th>
                <th>{t("log.supplierArticleNumber")}</th>
                <th className="sup__num">{t("plp.quantity")}</th>
                <th className="sup__num">{t("plp.unitPrice")}</th>
                <th className="sup__num">{t("plp.lineTotal")}</th>
                <th />
              </tr></thead>
              <tbody>
                {akte.lines.map((l) => {
                  const z = zeilen[l.id];
                  const offen = posBearbeitet && z !== undefined;
                  return (
                    <tr key={l.id} data-testid="plp-position" data-position={l.position}>
                      {/*
                        * EINE FREIE POSITION STEHT HIER WIE JEDE ANDERE.
                        *
                        * Sie hat keine greenius-Artikelnummer -- also
                        * traegt ihre Bezeichnung die erste Zeile.
                        * Darunter steht, dass sie keinem Artikel
                        * zugeordnet ist: das ist eine Tatsache ueber
                        * die Position und kein Mangel an ihr.
                        */}
                      <td className="sup__bez" data-frei={l.articleNumber === null ? "ja" : "nein"}>
                        {l.articleNumber !== null ? (
                          <>
                            <span className="plp__poartikel">{l.articleNumber}</span>
                            {l.shortDescription !== null && (
                              <span className="plp__pobez">{l.shortDescription}</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="plp__poartikel" data-testid="plp-posfrei">
                              {l.description ?? <Unknown />}
                            </span>
                            <span className="plp__pobez">
                              {t("plp.lineFreeHint")}
                              {l.unit !== null && ` · ${l.unit}`}
                            </span>
                          </>
                        )}
                      </td>
                      {/* Die Nummer des Lieferanten -- aus der Beziehung,
                          sonst die vom Beleg. Eine freie Position hat
                          keine Beziehung und trotzdem oft eine Nummer. */}
                      <td className="sup__supnr">
                        {l.supplierArticleNumber ?? l.supplierArticleNumberRaw ?? <Unknown />}
                      </td>
                      <td className="sup__num">
                        {offen ? (
                          <input className="log__input log__input--text plp__pofeld" type="number"
                            value={z.menge} data-feld="quantity" aria-label={t("plp.quantity")}
                            onChange={(e) => setZeilen({ ...zeilen, [l.id]: { ...z, menge: e.target.value } })} />
                        ) : (
                          <>
                            {l.quantity}
                            {l.moq !== null && l.quantity < l.moq && (
                              <span className="sup__tag sup__tag--aus"
                                title={t("plp.belowMoq", { n: String(l.moq) })}>{t("plp.moq")}</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="sup__num">
                        {offen ? (
                          <input className="log__input log__input--text plp__pofeld"
                            value={z.preis} data-feld="unitPriceAmount" aria-label={t("plp.unitPrice")}
                            placeholder={t("sup.optional")}
                            onChange={(e) => setZeilen({ ...zeilen, [l.id]: { ...z, preis: e.target.value } })} />
                        ) : l.unitPriceAmount === null ? <Unknown />
                          : `${betragText(l.unitPriceAmount, i18n.lang)} ${akte.currency ?? ""}`}
                      </td>
                      <td className="sup__num" data-testid="plp-zeilensumme">
                        {l.lineTotal === null ? <Unknown />
                          : `${betragText(l.lineTotal, i18n.lang)} ${akte.currency ?? ""}`}
                      </td>
                      <td>
                        {mayWrite && posBearbeitet && (
                          <button type="button" className="bew__weg" data-testid="plp-positionweg"
                            disabled={speichert}
                            onClick={() => { setSpeichert(true);
                              void api.deletePurchaseOrderLine(id, l.id)
                                .then(() => { positionenAbbrechen(); nachher(); })
                                .catch(melden)
                                .finally(() => setSpeichert(false)); }}>
                            {t("plp.removeLine")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/*
                * DER BESTELLWERT STEHT UNTER DEN POSITIONEN, WEIL ER AUS
                * IHNEN KOMMT. Er ist kein Feld, das jemand pflegt --
                * Menge mal Preis, aufsummiert, in der Waehrung der
                * Bestellung.
                */}
              <tfoot><tr data-testid="plp-posumme">
                <td colSpan={4}>{t("plp.orderValue")}</td>
                <td className="sup__num">{wertText(akte, i18n)}</td>
                <td />
              </tr></tfoot>
            </table>
          </div>
        )}
        {akte.linesWithoutPrice > 0 && akte.totalAmount !== null && (
          <p className="log__hint" data-testid="plp-wertunvollstaendig">
            {t("plp.valuePartial", { n: String(akte.linesWithoutPrice) })}
          </p>
        )}
        {/*
          * SPEICHERN STEHT IM BEREICH, NICHT IN DER AKTE.
          *
          * Wer eine Menge weit unten aendert, soll nicht nach oben
          * scrollen muessen, um sie zu behalten. Dieselbe Regel wie
          * beim Zugang: die Aktion liegt dort, wo gearbeitet wird.
          */}
        {mayWrite && posBearbeitet && (
          <div className="sup__kformaktionen plp__posaktionen"
            data-testid="plp-positionaktionen">
            <button className="btn btn--primary btn--small"
              data-testid="plp-positionenspeichern" disabled={speichert}
              onClick={() => void positionenSpeichern()}>{t("log.save")}</button>
            <button className="btn btn--small" disabled={speichert}
              data-testid="plp-posabbrechen"
              onClick={positionenAbbrechen}>{t("common.cancel")}</button>
          </div>
        )}
        {mayWrite && neu !== null && (
          <div className="sup__kform" data-testid="plp-positionmaske"
            data-art={neu.art}>
            {/*
              * DIE WAHL STEHT OBEN, WEIL SIE ALLES DARUNTER BESTIMMT.
              *
              * Nicht ein Haken „kein Artikel" neben dem Auswahlfeld:
              * das waere eine Verneinung, und der Benutzer muesste
              * erst das eine verstehen, um das andere zu waehlen. Zwei
              * Knoepfe sagen, dass es ZWEI Wege gibt.
              */}
            <div className="sup__kfeld sup__kfeld--breit plp__posart" role="group"
              aria-label={t("plp.lineKind")}>
              {([["artikel", "plp.lineFromCatalog"], ["frei", "plp.lineFree"]] as const)
                .map(([a2, k]) => (
                <button key={a2} type="button"
                  className={neu.art === a2 ? "btn btn--small btn--primary" : "btn btn--small"}
                  data-testid={`plp-posart-${a2}`} aria-pressed={neu.art === a2}
                  onClick={() => setNeu({ ...neu, art: a2 })}>{t(k)}</button>
              ))}
            </div>
            {neu.art === "artikel" ? (
              <label className="sup__kfeld sup__kfeld--breit">
                <span className="sup__klabel">{t("plp.article")}</span>
                <select className="log__input" value={neu.sa} data-feld="supplierArticleId"
                  aria-label={t("plp.article")}
                  onChange={(e) => setNeu({ ...neu, sa: e.target.value })}>
                  <option value="">{t("plp.chooseArticle")}</option>
                  {artikel.map((a2) => <option key={a2.id} value={a2.id}>{a2.label}</option>)}
                </select>
              </label>
            ) : (
              <>
                <label className="sup__kfeld sup__kfeld--breit">
                  <span className="sup__klabel">{t("plp.lineDescription")}</span>
                  <input className="log__input log__input--text" value={neu.bezeichnung}
                    data-feld="description" aria-label={t("plp.lineDescription")}
                    onChange={(e) => setNeu({ ...neu, bezeichnung: e.target.value })} />
                </label>
                <label className="sup__kfeld">
                  <span className="sup__klabel">{t("log.supplierArticleNumber")}</span>
                  <input className="log__input log__input--text" value={neu.liefernummer}
                    data-feld="supplierArticleNumberRaw" placeholder={t("sup.optional")}
                    aria-label={t("log.supplierArticleNumber")}
                    onChange={(e) => setNeu({ ...neu, liefernummer: e.target.value })} />
                </label>
                <label className="sup__kfeld">
                  <span className="sup__klabel">{t("plp.unit")}</span>
                  <input className="log__input log__input--text" value={neu.einheit}
                    data-feld="unit" placeholder={t("sup.optional")}
                    aria-label={t("plp.unit")}
                    onChange={(e) => setNeu({ ...neu, einheit: e.target.value })} />
                </label>
              </>
            )}
            <label className="sup__kfeld"><span className="sup__klabel">{t("plp.quantity")}</span>
              <input className="log__input log__input--text" type="number" value={neu.menge}
                data-feld="quantity" aria-label={t("plp.quantity")}
                onChange={(e) => setNeu({ ...neu, menge: e.target.value })} /></label>
            <label className="sup__kfeld">
              <span className="sup__klabel">
                {akte.currency === null ? t("plp.unitPrice")
                  : t("plp.unitPriceIn", { w: akte.currency })}
              </span>
              <input className="log__input log__input--text" value={neu.preis}
                data-feld="unitPriceAmount" aria-label={t("plp.unitPrice")}
                placeholder={t("sup.optional")}
                onChange={(e) => setNeu({ ...neu, preis: e.target.value })} />
              {/* Ein Preis ohne Waehrung ist eine Zahl ohne Aussage --
                  und das steht hier, bevor die Absage kommt. */}
              {akte.currency === null && (
                <span className="log__hint" data-testid="plp-keinewaehrung">{t("plp.currencyMissing")}</span>
              )}
            </label>
            {neu.art === "frei" && (
              <label className="sup__kfeld sup__kfeld--breit">
                <span className="sup__klabel">{t("plp.note")}</span>
                <input className="log__input log__input--text" value={neu.notiz}
                  data-feld="note" placeholder={t("sup.optional")} aria-label={t("plp.note")}
                  onChange={(e) => setNeu({ ...neu, notiz: e.target.value })} />
              </label>
            )}
            <span className="sup__kformaktionen">
              <button className="btn btn--primary btn--small" data-testid="plp-positionspeichern"
                disabled={speichert} onClick={() => void positionSpeichern()}>{t("log.save")}</button>
              <button className="btn btn--small" disabled={speichert}
                onClick={() => setNeu(null)}>{t("common.cancel")}</button>
            </span>
          </div>
        )}
        {/*
          * ── EIN ZUGANG, NICHT ZWEI ────────────────────────────────
          *
          * DER BEFUND DER BROWSER-ABNAHME (§3). „+ Position" stand
          * oben im Bereichskopf UND unten unter der Tabelle. Die
          * Begruendung von damals war: bei zwanzig Positionen steht
          * der obere Knopf ausser Sicht.
          *
          * ZWEI IDENTISCHE AKTIONEN SIND ABER KEINE HILFE, SONDERN
          * EINE FRAGE -- naemlich, worin sie sich unterscheiden. Und
          * die Antwort „in nichts" muss der Benutzer erst selbst
          * finden.
          *
          * DER ZUGANG STEHT IM BEREICHSKOPF, dort, wo auch der Stift
          * dieses Bereichs sitzt. Wer scrollt, scrollt zurueck --
          * das ist eine Bewegung, keine Suche.
          */}
      </section>

      {/*
        * DIE KAUFMAENNISCHE SICHT steht NACH den Positionen und VOR den
        * Terminen: der Bestellwert kommt aus den Positionen, und was
        * bezahlt wurde, ist eine andere Frage als, wo die Ware steht.
        */}
      {/*
        * `key` haengt an der Zahl der Zahlungen: wird eine im Terminbereich
        * erfasst, baut sich der Finanzbereich neu auf und zeigt sie sofort.
        * Ohne das stuende dieselbe Zahlung oben und unten verschieden da --
        * nicht, weil es zwei Haltungen gaebe, sondern weil die eine Ansicht
        * ihren Stand nur beim Einhaengen liest.
        */}
      <HilfeHinweis bereich="po.payments" />
      <OrderFinance poId={id} stand={stand} i18n={i18n}
        mayWrite={mayWrite} onChanged={nachher} />

      {/*
        * ABSCHLUSS DER BESTELLUNG -- getrennt von dem der Sendung.
        *
        * Was hier bewertet wird, ist die LEISTUNG DIESER BESTELLUNG:
        * war der Lieferant rechtzeitig fertig, stimmte die Ware, kam
        * die Packliste. Ob die Sendung danach auf eine zweite
        * Bestellung wartete, gehoert nicht hierher -- sonst stuende
        * ein puenktlicher Lieferant schlechter da, weil ein anderer
        * zu spaet war.
        *
        * UND ERST, WENN DIE WARE RAUS IST (Abschnitt 20.1). Siehe
        * `bewertbar` weiter oben.
        */}
      {bewertbar && <Abschluss sterne={akte.reviewStars} rueckblick={akte.reviewNote}
        i18n={i18n} mayWrite={mayWrite} speichert={speichert} testid="plp-abschluss"
        onSpeichern={async (st, rb) => {
          setSpeichert(true);
          try {
            await api.updatePurchaseOrder(id, { reviewStars: st, reviewNote: rb } as never);
            setFehler(null); await laden(); onChanged();
          } catch (e) { melden(e); } finally { setSpeichert(false); }
        }} />}
      {/*
        * DER ABSCHNITT HEISST DURCHLAUFZEIT.
        *
        * DER BEFUND DER BILDABNAHME: drei Zeilen -- Bestellt,
        * Versandbereit, Bestellung bis Versandbereitschaft -- standen
        * ohne Ueberschrift da. Drei Werte ohne Aussage darueber, wofuer
        * sie zusammen stehen.
        *
        * NUR DER NAME IST NEU. Die drei Zeilen, ihre Werte und die
        * Rechnung dahinter sind unveraendert; der Abschnitt bekommt
        * ausschliesslich die Beschriftung, die er immer gebraucht haette.
        * Die Sendungsakte hat ihre Karten seit CN UX SIMPLIFICATION 001
        * benannt -- die Bestellakte zieht hier nach.
        */}
      <Prozessdaten testid="plp-prozessdaten" i18n={i18n} titel={t("plp.leadTimeSection")}
        zeilen={(() => {
        const bereit = istTag(akte.milestones, "ready_for_shipment");
        const d = tageZw(akte.orderedAt, bereit);
        return [
          { label: t("plp.dOrdered"), wert: kurzDatum(akte.orderedAt, i18n.lang) },
          { label: t("plp.dReady"),
            wert: bereit === null ? null : kurzDatum(bereit, i18n.lang) },
          { label: t("plp.dOrderToReady"),
            wert: d === null ? null : t("plp.dDays", { n: String(d) }) },
        ];
      })()} />
      <Termine subject="purchase_order" subjectId={id} milestones={akte.milestones}
        codes={codes} prozess={akte.process}
        i18n={i18n} mayWrite={mayWrite} onChanged={nachher} onError={melden}
        payments={akte.payments} vorwahl={vorwahl}
        onVorwahl={vorwahlVerbraucht} />

      <Kalenderraum />
    </div>
    </HilfeRahmen>
  );
}

/* ── Die Consolidation-Akte ─────────────────────────────────────────────── */

/**
 * Der GELTENDE Stand eines Sendungstermins -- oder nichts.
 *
 * Die Liste traegt die ganze Geschichte, aufsteigend sortiert; der
 * aktuelle Stand ist der, den nichts abgeloest hat. Blind das erste
 * Element zu nehmen zeigte die aelteste ETA -- genau der Fehler, den der
 * Pruefstand beim Schreiben dieses Blocks aufgedeckt hat.
 */
function aktuellerTermin(
  milestones: readonly Milestone[], code: string,
): string | null {
  const m = milestones.find((x) => x.code === code && x.supersededAt === null);
  return m?.happensOn ?? null;
}

/**
 * Derselbe Wert als Anzeige -- oder der Satz, dass es ihn noch nicht
 * gibt. UND BEI EINEM ALTVORGANG EIN ANDERER SATZ.
 *
 * „offen" heisst: das muss noch jemand tun. Bei einer Sendung, die
 * gefahren ist, bevor es dieses Feld gab, ist das die falsche Auskunft
 * -- dort heisst es „nicht dokumentiert (Altvorgang)". Beide Saetze
 * sagen, dass nichts dasteht; nur einer davon behauptet eine Aufgabe.
 */
function terminText(
  milestones: readonly Milestone[], code: string, i18n: I18n,
  altvorgang = false,
): React.JSX.Element {
  const tag = aktuellerTermin(milestones, code);
  if (tag !== null) return <>{kurzDatum(tag, i18n.lang)}</>;
  return altvorgang
    ? <span className="log__leer plp__altvorgang" data-altvorgang="ja">
        {i18n.t("plp.legacyUndocumented")}</span>
    : <span className="log__leer">{i18n.t("plp.stillOpen")}</span>;
}

function CnAkte({ id, meta, codes, i18n, mayWrite, onClose, onChanged, onOpenPo,
                 onOpenPartners }: {
  id: number; meta: MetaResponse; codes: readonly MilestoneCodeInfo[];
  i18n: I18n; mayWrite: boolean;
  onClose: () => void; onChanged: () => void; onOpenPo: (poId: number) => void;
  onOpenPartners?: (() => void) | undefined;
}): React.JSX.Element {
  const t = i18n.t;
  const [d, setD] = useState<ConsolidationDetail | null>(null);
  const [offen, setOffen] = useState<readonly PurchaseOrderListItem[]>([]);
  const [ladeFehler, setLadeFehler] = useState<unknown>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [speichert, setSpeichert] = useState(false);
  /*
   * DERSELBE STIFT WIE IN DER BESTELLAKTE.
   *
   * `bearbeitet` plus ein Entwurf, der beim Start aus dem Stand
   * gefuellt und beim Abbrechen verworfen wird -- Zeile fuer Zeile das
   * Muster aus `Bestellakte`. Eine eigene CN-Logik waere ein zweites
   * Bedienmuster in derselben Anwendung.
   */
  const [bearbeitet, setBearbeitet] = useState(false);
  const [entwurf, setEntwurf] = useState<
    { forwarder: string; zielforwarder: string; transport: string; notiz: string }
    | null>(null);
  const [cnVorwahl, setCnVorwahl] = useState<MilestoneCode | null>(null);
  /* Die Terminpflege steht hinter dem Stift -- oder wird von der
     Arbeitszone geholt. Siehe `cnErfassen`. */
  const [terminePflege, setTerminePflege] = useState(false);
  /*
   * ── DER TOTE KNOPF ──
   *
   * DER BEFUND AUS DER ABNAHME: in „Noch nicht erfasste
   * Transportangaben" waren die Knoepfe sichtbar und reagierten nicht.
   *
   * DIE URSACHE WAR NICHT DER KNOPF. `onClick` lief, die Vorwahl kam
   * an, die Maske stellte sich richtig ein -- nur steht die
   * Terminpflege der SENDUNG seit dem Kartenumbau in einem
   * zugeklappten `details`. Ein zugeklapptes `details` verbirgt seinen
   * Inhalt; `scrollIntoView` auf etwas Verborgenes tut nichts.
   * Sichtbar passierte also: nichts. Und weil die Maske die Vorwahl
   * ordnungsgemaess quittierte, war sie danach auch wieder fort.
   *
   * In der BESTELLAKTE gab es den Fehler nie -- dort steht dieselbe
   * Maske frei. Deshalb traf es genau die vier Transportangaben, die
   * Arne genannt hat, und keine einzige Bestellangabe.
   *
   * DIE LOESUNG OEFFNET DIE KLAPPE, mehr nicht. Kein zweiter
   * Schreibweg, kein Dialog, keine zweite Maske: derselbe Weg wie
   * immer, nur sichtbar.
   */
  const alleTermine = useRef<HTMLDetailsElement | null>(null);
  /*
   * ── DER KNOPF, DER DIE MASKE ERST HOLT ──────────────────────────
   *
   * DER EIGENE BEFUND DIESES BLOCKS -- und der fuenfte toter Knopf
   * derselben Familie. Die Terminpflege steht seit der Vereinfachung
   * hinter dem Stift. `scrollIntoView` und `open = true` auf einem
   * Element, das gar nicht im Baum haengt, tun nichts: „Jetzt
   * erfassen" klappte ins Leere.
   *
   * DIE MASKE KOMMT DESHALB AUF DIESEN KNOPF HIN -- und NICHT der
   * ganze Bearbeitungsmodus. Wer ein Datum festhalten will, bekommt
   * die Terminmaske; die Stammdatenfelder gehen ihn dabei nichts an.
   */
  const cnErfassen = useCallback((c: MilestoneCode) => {
    setTerminePflege(true);
    setCnVorwahl(c);
  }, []);
  useEffect(() => {
    if (cnVorwahl !== null && alleTermine.current !== null) {
      alleTermine.current.open = true;
    }
  }, [cnVorwahl]);
  const [cutoffEntwurf, setCutoffEntwurf] = useState<string | null>(null);
  const cnVorwahlVerbraucht = useCallback(() => setCnVorwahl(null), []);
  /* Die offene Loesemaske: welche Bestellung, und der Grund im Entwurf. */
  const [loesen, setLoesen] = useState<
    { poId: number; dteNumber: string; grund: string } | null>(null);
  /*
   * IST DIE WARE UEBERGEBEN? Dann wird nichts mehr herausgenommen und
   * nichts mehr hineingegeben -- die Oberflaeche zeigt es, statt es erst
   * durch eine Absage mitzuteilen.
   *
   * GEFRAGT WIRD DIE UEBERGABE UND NICHT DER ZUSTAND. Bis 0086 war das
   * dasselbe: `in_transport` kam aus `carrier_handover`. Seit die
   * ABFAHRT den Zustand traegt, ist es das nicht mehr -- zwischen
   * Uebergabe und Ablegen stuende die Sendung wieder auf `gebucht`, und
   * die Oberflaeche boete das Loesen an, das die Datenbank dann abweist.
   * Dieselbe Korrektur steht im Fachkern (`istUebergeben`).
   */
  const gefahren = d !== null && d.milestones.some(
    (m) => m.code === "carrier_handover" && m.certainty === "actual"
        && m.supersededAt === null);

  /*
   * ── PLAN GEGEN IST UND WARTEZEITEN -- ABGELEITET, UND NUR DANN DA ──
   *
   * KORREKTURLAUF 002, Abschnitt 11. Eine Karte, die dreimal „nicht
   * bewertbar" sagt, traegt nichts bei und kostet eine Seitenlaenge.
   * Sie steht deshalb erst da, wenn mindestens EINE Zeile einen Wert
   * hat.
   *
   * GERECHNET WIRD TROTZDEM IMMER -- die Sichtbarkeit faellt aus dem
   * Ergebnis, nicht umgekehrt. Eine Bedingung, die VOR der Rechnung
   * entscheidet, waere eine zweite Regel darueber, wann etwas
   * auswertbar ist.
   */
  const planIst = d === null ? [] : ([
    ["plp.legStart", "planned_etd", "departed"],
    ["plp.legHamburg", "eta_hamburg", "hh_landed"],
    ["plp.legLueneburg", "eta_lueneburg", "goods_delivered"]] as const)
    .map(([etappe, plan, ist]) => {
      const tage = tageZw(aktuellerTermin(d.milestones, plan),
                          aktuellerTermin(d.milestones, ist));
      return {
        label: t(etappe),
        wert: tage === null ? null
          : tage === 0 ? t("plp.deltaOnTime")
          : tage > 0 ? `+${t("plp.deltaDays", { n: String(tage) })}`
          : t("plp.deltaEarly", { n: String(-tage) }),
      };
    });
  const wartezeiten = d === null ? [] : (() => {
    const ein = d.orders.map((o) => o.forwarderReceivedOn)
      .filter((x): x is string => x !== null).sort();
    const alle = ein.length === d.orders.length && d.orders.length > 0;
    const erster = ein[0] ?? null;
    const letzter = alle ? (ein.at(-1) ?? null) : null;
    const warten = tageZw(erster, letzter);
    return [
      { label: t("plp.dFirstAtForwarder"),
        wert: erster === null ? null : kurzDatum(erster, i18n.lang) },
      { label: t("plp.dLastAtForwarder"),
        wert: letzter === null ? null : kurzDatum(letzter, i18n.lang) },
      { label: t("plp.dWaitForOthers"),
        wert: warten === null ? null : t("plp.dDays", { n: String(warten) }) },
    ];
  })();
  /*
   * HAT UEBERHAUPT JEMAND GEWARTET?
   *
   * Nicht „gibt es Werte", sondern „ist eine Wartezeit entstanden".
   * Die beiden Daten allein beantworten die Frage nicht: wenn erste
   * und letzte Bestellung am selben Tag eintrafen, hat niemand
   * gewartet, und drei Zeilen darueber sind keine Auskunft.
   */
  const wartezeitEcht = d !== null && (() => {
    const ein = d.orders.map((o) => o.forwarderReceivedOn)
      .filter((x): x is string => x !== null).sort();
    if (ein.length < 2 || ein.length !== d.orders.length) return false;
    const tage = tageZw(ein[0]!, ein.at(-1)!);
    return tage !== null && tage > 0;
  })();

  /*
   * ── IST DIESE SENDUNG SO WEIT, DASS MAN SIE BEURTEILEN KANN? ──
   *
   * Abschnitt 20.2: die Bewertung einer Sendung beginnt, wenn die
   * SENDUNG abgeschlossen ist -- nicht wenn die Ware da ist.
   *
   * ENGER ALS BISHER, UND ABSICHTLICH. Vorher zaehlte schon die
   * Anlieferung (`goods_delivered`, Status ZUGESTELLT). Zwischen
   * Anlieferung und Abschluss liegt aber noch Arbeit, die zur
   * Beurteilung gehoert: Wareneingang, Mengen, Schaeden,
   * Frachtabrechnung. Wer am Anliefertag bewertet, bewertet den
   * Transport und nicht den Vorgang -- und ueberschreibt danach
   * nichts mehr, weil die Karte schon ausgefuellt dasteht. Der
   * Abschluss ist eine bewusste Entscheidung (siehe
   * `CONSOLIDATION_STATUSES`) und genau deshalb der richtige
   * Zeitpunkt: er sagt, dass jemand hingesehen hat.
   *
   * NICHT VON DER PO-BEWERTUNG ABGELEITET. PO und CN sind zwei
   * Vorgaenge mit zwei Leistungen und zwei Verantwortlichen.
   *
   * UND EINE ABGEGEBENE BEWERTUNG BLEIBT SICHTBAR -- auch an einer
   * verworfenen Sendung. Eine Regel, die einen vorhandenen Satz
   * ausblendet, laesst Arbeit verschwinden, die jemand geleistet hat.
   */
  const beurteilbar = d !== null
    && (d.status === "geschlossen"
        || d.reviewStars !== null
        || (d.reviewNote ?? "") !== "");
  /* Wie viele der aktiven Bestellungen sind beim Logistikpartner? Reine
     Zaehlung dessen, was die Liste ohnehin zeigt. */
  const eingetroffen = (d?.orders ?? [])
    .filter((o) => o.forwarderReceivedOn !== null).length;

  const laden = useCallback(async () => {
    try {
      setD(await api.consolidation(id));
      setOffen((await api.purchaseOrders()).items.filter(
        (p) => p.consolidationId === null && p.status === "offen"));
      setLadeFehler(null);
    } catch (e) { setLadeFehler(e); }
  }, [id]);
  useEffect(() => { void laden(); }, [laden]);

  if (ladeFehler !== null) return <p className="log__error">{errorText(i18n, ladeFehler)}</p>;
  if (d === null) return <p className="work__empty">{t("common.loading")}</p>;

  const melden = (e: unknown): void => setFehler(e);
  const nachher = (): void => { setFehler(null); void laden(); onChanged(); };
  const tun = (p: Promise<unknown>): void => {
    setSpeichert(true);
    void p.then(nachher).catch(melden).finally(() => setSpeichert(false));
  };

  /* Zur Wahl stehen die Organisationen MIT der Rolle Forwarder -- die
     Liste kommt aus dem zentralen Geschaeftspartnerbestand und wird hier
     nicht gefuehrt. */
  const forwarder = meta.partners.filter((p) => p.roles.includes("forwarder"));

  const akte = d;
  function bearbeitenStarten(): void {
    setEntwurf({
      forwarder: akte.forwarderId === null ? "" : String(akte.forwarderId),
      zielforwarder: akte.destinationForwarderId === null
        ? "" : String(akte.destinationForwarderId),
      transport: akte.transportMode ?? "",
      notiz: akte.note ?? "",
    });
    setBearbeitet(true);
  }
  function abbrechen(): void {
    setBearbeitet(false); setEntwurf(null); setFehler(null);
  }
  /*
   * GESPEICHERT WIRD, WAS SICH GEAENDERT HAT -- wie in der Bestellakte.
   *
   * Alles mitzuschicken erzeugte Protokollzeilen fuer Aenderungen, die
   * niemand gemacht hat.
   */
  /** Der gueltige Cut-off dieser Sendung -- oder keiner. */
  function cutoffTag(ms: readonly Milestone[]): string | null {
    return ms.find((m) => m.code === "consolidation_cutoff" && m.supersededAt === null)
      ?.happensOn ?? null;
  }
  /*
   * DERSELBE SCHREIBWEG WIE JEDER TERMIN -- `setMilestone`, mit der
   * Zusage als Terminstatus. Der Cut-off kennt kein Ist: er ist eine
   * Planungsgroesse und tritt nicht ein.
   */
  async function cutoffSpeichern(): Promise<void> {
    if (cutoffEntwurf === null || cutoffEntwurf === "") return;
    setSpeichert(true);
    try {
      await api.setMilestone({
        subject: "consolidation", subjectId: id, code: "consolidation_cutoff",
        happensOn: cutoffEntwurf, certainty: "confirmed", reasonNote: null,
      });
      setCutoffEntwurf(null); setFehler(null); await laden(); onChanged();
    } catch (e) { melden(e); } finally { setSpeichert(false); }
  }

  async function stammSpeichern(): Promise<void> {
    if (entwurf === null) return;
    const aenderung: Record<string, unknown> = {};
    const fw = entwurf.forwarder === "" ? null : Number(entwurf.forwarder);
    if (fw !== akte.forwarderId) aenderung["forwarderId"] = fw;
    const zfw = entwurf.zielforwarder === "" ? null : Number(entwurf.zielforwarder);
    if (zfw !== akte.destinationForwarderId) aenderung["destinationForwarderId"] = zfw;
    const tm = entwurf.transport === "" ? null : entwurf.transport;
    if (tm !== akte.transportMode) aenderung["transportMode"] = tm;
    const nz = entwurf.notiz.trim() === "" ? null : entwurf.notiz.trim();
    if (nz !== akte.note) aenderung["note"] = nz;
    if (Object.keys(aenderung).length === 0) { abbrechen(); return; }
    setSpeichert(true);
    try {
      setD(await api.updateConsolidation(id, aenderung as never));
      setBearbeitet(false); setEntwurf(null); setFehler(null); onChanged();
    } catch (e) { melden(e); } finally { setSpeichert(false); }
  }

  return (
    /*
     * `plp--ruhig` IST DER GANZE VISUELLE KORREKTURLAUF 002.
     *
     * Eine Klasse am Wurzelelement, und alles Weitere steht im
     * Stylesheet darunter. Zwei Gruende:
     *
     *   1  Die Bausteine `log__group`, `log__item`, `log__label` und
     *      `log__vals` gehoeren der ganzen Anwendung. Sie hier
     *      umzubauen hiesse, die Produktakte und die Lieferantenakte
     *      mit umzubauen -- ungefragt.
     *   2  Die SENDUNG ist der Pilot. Faellt die Abnahme gut aus,
     *      bekommt die Bestellakte dieselbe Klasse und sonst nichts.
     */
    <HilfeRahmen>
    <div className="plp plp--ruhig">
      {/*
        * DER KOPF BEANTWORTET DREI FRAGEN AUF EINEN BLICK: welche
        * Sendung, in welchem Zustand, wie transportiert. Alles andere
        * steht darunter in eigenen Bereichen -- bis hierher standen
        * Termine, Bestellungen, Aktionen und Historie gleichgewichtig
        * nebeneinander, und man musste suchen.
        */}
      {/*
        * DER KOPF NENNT DIE SENDUNG, IHREN ZUSTAND UND IHREN WEG --
        * nicht ihre Lieferanten.
        *
        * Eine Sendung kann viele tragen; eine Aufzaehlung im Kopf
        * skaliert nicht und verdraengt das Wesentliche. Welcher
        * Lieferant zu welcher Bestellung gehoert, steht dort, wo die
        * Bestellungen stehen.
        */}
      <header className="sup__akte">
        <button className="btn sup__zurueck" onClick={onClose}>{t("plp.back")}</button>
        <div className="sup__ident">
          <h2 className="sup__kurzname">{d.number}</h2>
          {/*
            * EINE ZEILE, DREI ANGABEN: wie sie reist, wer sie fährt,
            * wie viel sie trägt. Mehr gehört nicht in den Kopf -- die
            * Frage, mit der jemand herkommt, steht direkt darunter.
            */}
          <span className="sup__firma" data-testid="plp-cnkopfzeile">
            {[d.transportMode === null ? null : i18n.tCode("cnTransport", d.transportMode),
              d.forwarderName,
              d.orders.length === 0 ? null
                : t("plp.ordersCount", { n: String(d.orders.length) })]
              .filter((x) => x !== null && x !== "").join(" · ")
              || t("plp.transportUnknown")}
          </span>
          {/*
            * „0/2 BEIM LOGISTIKPARTNER" BLEIBT IM KOPF.
            *
            * Es ist der eine Stand, der ueber den naechsten Schritt
            * entscheidet, und er passt in vier Zeichen. Die
            * Lieferantenzahl daneben ist dagegen fort: sie beantwortet
            * keine Frage, die jemand beim Oeffnen hat, und steht
            * ohnehin an jeder Bestellzeile.
            */}
          {d.orders.length > 0 && !d.intakeLegacy && (
            <span className="plp__kopfzaehler" data-testid="plp-cneingang">
              {t("plp.atForwarderCount", {
                da: String(eingetroffen), von: String(d.orders.length) })}
            </span>
          )}
        </div>
        <span className={`sup__tag plp__zustand plp__zustand--${d.status}`}
          data-testid="plp-cnstatus">{i18n.tCode("cnStatus", d.status)}</span>
        <HilfeSchalter />
        {mayWrite && (
          <button type="button" data-testid="plp-cnstift"
            className={bearbeitet ? "subnav__pencil subnav__pencil--on plp__stift" : "subnav__pencil plp__stift"}
            aria-pressed={bearbeitet} title={t("props.edit")} aria-label={t("plp.editAria")}
            onClick={() => (bearbeitet ? abbrechen() : bearbeitenStarten())}>
            {/*
              * EIN SYMBOL ALLEIN IST KEINE BESCHRIFTUNG.
              *
              * DER BEFUND DER BROWSER-ABNAHME 002: der Stift war zu
              * versteckt -- wer die Akte zum ersten Mal sieht, sucht
              * „Bearbeiten" und findet ein Zeichen. Das Zeichen bleibt,
              * weil es wiedererkannt wird; daneben steht ab jetzt das
              * Wort. Beim Umschalten sagt es, was der naechste Klick
              * tut, und nicht, in welchem Zustand man gerade ist.
              */}
            <span aria-hidden="true">{PENCIL}</span>
            <span className="plp__stifttext">
              {bearbeitet ? t("common.cancel") : t("props.edit")}
            </span>
          </button>
        )}
      </header>
      {fehler !== null && (
        <p className="log__error" role="alert" data-testid="plp-fehler">{errorText(i18n, fehler)}</p>
      )}
      {/*
        * ── DIE ZENTRALE PROZESSFUEHRUNG STEHT UNMITTELBAR UNTER DEM
        *    KOPF -- genau wie in der Bestellakte ──
        *
        * DER BEFUND DER BROWSER-ABNAHME: bei der Bestellung lag sie
        * oben, bei der Sendung erschien sie erst unter den
        * Stammdaten. Zwei Bedienmuster in derselben Anwendung, und
        * das haeufigere war das versteckte.
        *
        * DIESELBE KOMPONENTE, DIESELBE ABLEITUNG. Es gibt hier keine
        * eigene CN-Fassung, die auseinanderlaufen koennte.
        */}
      <h4 className="plp__zonefrage">{t("plp.whatNow")}</h4>
      <HilfeHinweis bereich="cn.arbeitszone" />
      <Arbeitszone prozess={d.process} titel={t("plp.openTransportData")}
        i18n={i18n} mayWrite={mayWrite} testid="plp-cnzone"
        zeigen="naechster"
      reihenfolge={codes.filter((c) => c.subject === "consolidation" && c.manual
        && !c.deprecated && c.allowsActual).map((c) => c.code)}
        eingaenge={d.orders
          .filter((o) => o.forwarderReceivedOn === null && !d.intakeLegacy)
          .map((o) => ({ poId: o.id, dteNumber: o.dteNumber,
                         supplierName: o.supplierName }))}
        /* DERSELBE SCHREIBWEG WIE AN DER BESTELLZEILE UNTEN --
           `setForwarderReceipt`, derselbe Tag, dieselbe Historie. Der
           Knopf hier ist eine zweite STELLE, kein zweiter Weg. */
        onEingang={(poId) => tun(api.setForwarderReceipt(id, poId,
          { receivedOn: heuteLokal() }))}
        onErfassen={cnErfassen} />

      {/*
        * DER LOGISTIKPARTNER -- EINE REFERENZ, KEINE ZWEITE ADRESSE.
        *
        * Die Consolidation speichert die Kennung der Organisation.
        * Firmierung, Anschrift und Ansprechpartner stehen in der
        * Geschaeftspartnerakte; haette die CN eine eigene Kopie, waere
        * jeder Umzug eine Korrektur an jeder einzelnen Consolidation --
        * und die aelteste bliebe falsch.
        */}
      {/*
        * ── TRANSPORT ──
        *
        * Planwerte und Tatsachen stehen getrennt. „ETA HH 05.11." und
        * „Carrier uebergeben 18.10." sehen sonst gleich aus, und das
        * eine ist eine Erwartung, das andere ein Ereignis -- die
        * Verwechslung ist genau die, die 0083 im Datenmodell
        * aufgeraeumt hat.
        *
        * Gelesen wird aus den vorhandenen Terminen. Keine neue Wahrheit.
        */}
      {/*
        * ── STAMMDATEN ──
        *
        * Lesen ist der Normalzustand. Der Stift oben rechts macht
        * daraus Felder -- dieselbe Mechanik wie in der Bestellakte und
        * in der Lieferantenakte. Ein dauerhaft offenes Auswahlfeld sieht
        * aus wie eine unerledigte Eingabe.
        */}
      <section className="log__group plp__sendungsdaten" data-testid="plp-cnstamm">
        <h4 className="log__grouptitle">{t("plp.cnMaster")}</h4>
        {bearbeitet && entwurf !== null ? (
          <div className="sup__kform" data-testid="plp-cnstammmaske">
            <label className="sup__kfeld sup__kfeld--breit">
              <span className="sup__klabel">{t("plp.chooseForwarder")}</span>
              <select className="log__input" value={entwurf.forwarder} data-feld="forwarderId"
                aria-label={t("plp.chooseForwarder")} disabled={speichert}
                onChange={(e) => setEntwurf({ ...entwurf, forwarder: e.target.value })}>
                <option value="">{t("plp.noForwarder")}</option>
                {forwarder.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select></label>
            {/*
              * DER ZWEITE PARTNER -- dieselbe Liste, dieselbe Rolle.
              *
              * Eine Sendung hat fachlich zwei Logistikpartner: einen in
              * Fernost, der sammelt und an den Carrier uebergibt, und
              * einen in Hamburg, der nach Ankunft und Verzollung
              * weiterfuehrt. Beide stehen im Geschaeftspartnerstamm und
              * tragen die Rolle Forwarder -- hier entsteht keine zweite
              * Adressverwaltung, nur eine zweite Auswahl aus derselben.
              */}
            <label className="sup__kfeld sup__kfeld--breit">
              <span className="sup__klabel">{t("plp.chooseDestForwarder")}</span>
              <select className="log__input" value={entwurf.zielforwarder}
                data-feld="destinationForwarderId"
                aria-label={t("plp.chooseDestForwarder")} disabled={speichert}
                onChange={(e) => setEntwurf({ ...entwurf, zielforwarder: e.target.value })}>
                <option value="">{t("plp.noDestForwarder")}</option>
                {forwarder.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select></label>
            <label className="sup__kfeld">
              <span className="sup__klabel">{t("plp.transportMode")}</span>
              <select className="log__input" value={entwurf.transport} data-feld="transportMode"
                aria-label={t("plp.transportMode")} disabled={speichert}
                onChange={(e) => setEntwurf({ ...entwurf, transport: e.target.value })}>
                <option value="">{t("plp.transportUnknown")}</option>
                {CONSOLIDATION_TRANSPORT_MODES.map((m) => (
                  <option key={m} value={m}>{i18n.tCode("cnTransport", m, m)}</option>
                ))}
              </select></label>
            <label className="sup__kfeld sup__kfeld--breit">
              <span className="sup__klabel">{t("plp.note")}</span>
              <input className="log__input log__input--text" value={entwurf.notiz} data-feld="note"
                aria-label={t("plp.note")} placeholder={t("sup.optional")} disabled={speichert}
                onChange={(e) => setEntwurf({ ...entwurf, notiz: e.target.value })} /></label>
            <span className="sup__kformaktionen">
              <button className="btn btn--primary btn--small" data-testid="plp-cnspeichern"
                disabled={speichert} onClick={() => void stammSpeichern()}>{t("log.save")}</button>
              <button className="btn btn--small" disabled={speichert}
                data-testid="plp-cnabbrechen" onClick={abbrechen}>{t("common.cancel")}</button>
            </span>
          </div>
        ) : (
          /*
            * ZWEI ANGABEN NEBENEINANDER -- DIE REIHENFOLGE BLEIBT.
            *
            * Das Raster fuellt zeilenweise, und die vorhandene
            * Reihenfolge ergibt dabei von selbst die sinnvolle
            * Paarung: die beiden Logistikpartner stehen nebeneinander,
            * darunter Transportart und Notiz. Die Angaben umzusortieren,
            * nur damit das Raster anders fuellt, waere eine
            * Inhaltsaenderung fuer einen Layoutzweck.
            */
          <div className="log__rows infoRaster">
            <div className="log__item">
              <span className="log__label">{t("plp.originForwarder")}</span>
              <span className="log__vals" data-testid="plp-forwardername">
                {d.forwarderName ?? <Unknown />}</span></div>
            <div className="log__item">
              <span className="log__label">{t("plp.destForwarder")}</span>
              <span className="log__vals" data-testid="plp-zielforwardername">
                {d.destinationForwarderName ?? <Unknown />}</span></div>
            <div className="log__item"><span className="log__label">{t("plp.transportMode")}</span>
              <span className="log__vals" data-testid="plp-transportart">
                {d.transportMode === null
                  ? <Unknown /> : i18n.tCode("cnTransport", d.transportMode)}</span></div>
            <div className="log__item"><span className="log__label">{t("plp.note")}</span>
              <span className="log__vals">{d.note ?? <Unknown />}</span></div>
          </div>
        )}
        {/*
          * EIN HINWEIS, DER NICHT WEITERFUEHRT, IST EINE SACKGASSE.
          *
          * DER BEFUND DER BROWSER-ABNAHME 002. Im Bearbeitungsmodus stand
          * „kein Logistikpartner" und darunter der Satz, dass noch keine
          * Organisation die Rolle Forwarder traegt. Richtig -- und ohne
          * jeden Weg dorthin.
          *
          * GESCHAEFTSPARTNER BLEIBT DIE STAMMDATENQUELLE. Hier entsteht
          * kein Freitextfeld und keine zweite Adressverwaltung: greenius
          * arbeitet mit wechselnden Partnern -- dem Fernost-Dienstleister,
          * DHL Express, FedEx --, und die gehoeren einmal angelegt und
          * danach ausgewaehlt. Die Sendung fragt nur: WER bewegt sie?
          *
          * Der Knopf wechselt deshalb den Bereich, statt hier ein
          * Formular aufzumachen.
          */}
        {bearbeitet && forwarder.length === 0 && (
          <p className="log__hint plp__sackgasse" data-testid="plp-keinforwarder">
            <span>{t("plp.noForwarderYet")}</span>
            {onOpenPartners !== undefined && (
              <button type="button" className="btn btn--small"
                data-testid="plp-forwarderverwalten"
                onClick={onOpenPartners}>{t("plp.manageForwarders")}</button>
            )}
          </p>
        )}
        {bearbeitet && forwarder.length > 0 && (
          <p className="log__hint" data-testid="plp-forwarderquelle">
            {t("plp.forwarderFromPartners")}
          </p>
        )}
      </section>

      {/*
        * ── TRANSPORT ──
        *
        * Reine Anzeige. Plan und Tatsache getrennt: „ETA HH 05.11." und
        * „Carrier uebergeben 18.10." sehen sonst gleich aus, und das
        * eine ist eine Erwartung, das andere ein Ereignis.
        *
        * Gepflegt werden die Termine ueber die Terminliste weiter
        * unten -- ein zweiter Schreibweg fuer dieselben Daten waere
        * genau die Doppelung, die das Datenmodell vermeidet.
        */}
      {/*
        * ── TRANSPORT: DREI ETAPPEN, ERWARTUNG GEGEN TATSACHE ──
        *
        * DER BEFUND DER ABNAHME: hier standen dreizehn Zeilen -- vier
        * Planwerte und sieben Tatsachen in zwei Spalten, dazu der
        * Cut-off als Eingabefeld. Das ist das Datenmodell, nicht die
        * Managementsicht.
        *
        * DIE MANAGEMENTSICHT SIND DREI PAARE, und zwar genau die, bei
        * denen eine Erwartung und eine Tatsache dasselbe Ereignis
        * meinen:
        *
        *     Abfahrt Fernost   ETD            <-> Departed
        *     Hamburg           ETA Hamburg    <-> HH landed
        *     Lueneburg         ETA Lueneburg  <-> LG landed
        *
        * Alles Uebrige -- Frachtanfrage, Angebot, Auftrag, Papiere,
        * Uebergabe, Verzollung, Weitergabe -- hat keine Erwartung
        * daneben und gehoert in den Prozessverlauf. Es ist nicht
        * verschwunden, es steht nur nicht mehr dauerhaft hier.
        *
        * DER CUT-OFF IST AUS DIESER ANSICHT FORT. Er schliesst nichts
        * -- das tut die Carrier-Uebergabe --, und ein Planwert ohne
        * Wirkung ist in der Hauptbedienung eine Frage, die niemand
        * beantworten muss. Das FELD bleibt, die Historie bleibt, der
        * Weg ueber die Terminpflege bleibt.
        *
        * EIN FEHLENDER PLANWERT HEISST „noch nicht geplant" UND NICHT
        * „noch offen". „Offen" klingt nach Aufgabe; eine Erwartung,
        * die niemand abgegeben hat, ist keine.
        */}
      <section className="log__group" data-testid="plp-transport">
        <h4 className="log__grouptitle">{t("plp.transportTitle")}</h4>
        <HilfeHinweis bereich="cn.transport" />
        <div className="plp__etappen" role="table" aria-label={t("plp.transportTitle")}>
          <div className="plp__etappenkopf" role="row">
            <span role="columnheader" />
            <span role="columnheader" className="plp__etappenspalte">
              {t("plp.colExpected")}</span>
            <span role="columnheader" className="plp__etappenspalte">
              {t("plp.colActual")}</span>
          </div>
          {([["plp.legStart", "planned_etd", "departed"],
             ["plp.legHamburg", "eta_hamburg", "hh_landed"],
             ["plp.legLueneburg", "eta_lueneburg", "goods_delivered"]] as const)
            .map(([etappe, plan, ist]) => (
            <div key={plan} className="plp__etappe" role="row"
              data-testid="plp-etappe" data-plan={plan} data-ist={ist}>
              <span className="plp__etappenname" role="rowheader">{t(etappe)}</span>
              <span className="plp__etappenwert" role="cell"
                data-testid={`plp-plan-${plan}`}>
                <span className="plp__etappenlabel">{i18n.tCode("ms", plan, plan)}</span>
                {/*
                  * DER WERT STEHT IN EINEM EIGENEN ELEMENT.
                  *
                  * KORREKTURLAUF 002. Als blosser Textknoten war er fuer
                  * das Stylesheet nicht erreichbar -- die Regel fuer den
                  * grossen Wert griff auf die Beschriftung darueber, und
                  * am Bildschirm stand „ETD" gross und „30.09.26" klein.
                  */}
                <span className="plp__etappenzahl">
                  {aktuellerTermin(d.milestones, plan) !== null
                    ? kurzDatum(aktuellerTermin(d.milestones, plan)!, i18n.lang)
                    : <span className="log__leer">{t("plp.notPlanned")}</span>}
                </span>
              </span>
              <span className="plp__etappenwert" role="cell"
                data-testid={`plp-ist-${ist}`}>
                <span className="plp__etappenlabel">{i18n.tCode("ms", ist, ist)}</span>
                <span className="plp__etappenzahl">
                  {terminText(d.milestones, ist, i18n, istAlt(d.process, ist))}
                </span>
              </span>
            </div>
          ))}
        </div>
        {/*
          * ── DIE KONSOLIDIERUNG, KOMPAKT ──
          *
          * Eine Zeile mit einem Satz dahinter. Kein Bedienblock: es
          * gibt nichts zu druecken, der Zustand folgt aus der
          * Carrier-Uebergabe.
          */}
        <p className={d.intakeWindow === "offen"
          ? "plp__cnfenster plp__cnfenster--offen" : "plp__cnfenster"}
          data-testid="plp-cnfenster" data-zustand={d.intakeWindow}>
          <span className="plp__cnfenstertitel">{t("plp.consolidation")}</span>
          <span className="plp__cnfensterwert">
            {t(d.intakeWindow === "offen" ? "plp.intakeWindowOpen"
              : d.intakeWindow === "geschlossen_altvorgang"
                ? "plp.intakeWindowClosedLegacy" : "plp.intakeWindowClosed")}
          </span>
          <span className="plp__cnfenstersatz">
            {d.intakeWindow === "offen" ? t("plp.consolidationOpenHint")
              : d.intakeWindow === "geschlossen_altvorgang"
                ? t("plp.consolidationClosedLegacyHint")
              : t("plp.consolidationClosedHint", {
                  tag: kurzDatum(aktuellerTermin(d.milestones, "carrier_handover") ?? "",
                    i18n.lang) })}
          </span>
        </p>
      </section>

      <section className="log__group">
        <h4 className="log__grouptitle">{t("plp.orders")}</h4>
        {/*
          * WORAUF WARTET DIESE SENDUNG NOCH? -- eine Zeile, abgeleitet.
          *
          * Kein gespeicherter Fortschritt, kein Balken, kein Kennwert:
          * gezaehlt wird, was ohnehin in der Liste darunter steht.
          */}
        {/*
          * UND DASSELBE FUER DEN EINGANG BEIM LOGISTIKPARTNER.
          *
          * „0/2 beim Logistikpartner" liest sich wie ein Versaeumnis.
          * Bei CN-01 ist es keins: die Spalte kam mit 0082, die Sendung
          * ist aelter. Sie hat den Eingang nicht vergessen -- es gab
          * dafuer kein Feld. `intakeLegacy` beantwortet das aus der
          * Datenbank und nicht aus einem Stichtag im Quelltext.
          */}
        {d.orders.length > 0 && (d.intakeLegacy ? (
          <p className="plp__fortschritt plp__fortschritt--alt"
            data-testid="plp-fortschritt" data-altvorgang="ja">
            {t("plp.intakeLegacyHint")}
          </p>
        ) : (
          <p className={eingetroffen === d.orders.length
            ? "plp__fortschritt plp__fortschritt--voll" : "plp__fortschritt"}
            data-testid="plp-fortschritt">
            {eingetroffen === d.orders.length
              ? t("plp.allArrived")
              : t("plp.arrivedOf", { da: String(eingetroffen),
                                     alle: String(d.orders.length) })}
          </p>
        ))}
        {d.orders.length === 0 ? <p className="log__hint">{t("plp.cnEmpty")}</p> : (
          <ul className="plp__poliste">
            {d.orders.map((p) => (
              <li key={p.id} className="plp__pozeile" data-testid="plp-cnorder">
                <button type="button" className="plp__punktziel plp__ponummer"
                  onClick={() => onOpenPo(p.id)}>{p.dteNumber}</button>
                <span className="plp__lanesupplier">{p.supplierName}</span>
                {/*
                  * WO DIE WARE STEHT -- je Bestellung.
                  *
                  * Das beantwortet die Frage, mit der jemand herkommt:
                  * worauf wartet diese Sendung noch? Ein CN-Status dafuer
                  * waere zu grob -- er sagte nur, DASS gewartet wird.
                  */}
                <span className={p.forwarderReceivedOn !== null
                  ? "sup__tag plp__eingang plp__eingang--da" : "sup__tag plp__eingang"}
                  data-testid={`plp-eingang-${p.id}`}>
                  {p.forwarderReceivedOn !== null
                    ? t("plp.atForwarderOn",
                        { date: kurzDatum(p.forwarderReceivedOn, i18n.lang) })
                    : t("plp.notAtForwarder")}
                </span>
                <span className="plp__poaktionen">
                {mayWrite && !gefahren && (
                  <button type="button" className="btn btn--small"
                    data-testid={`plp-eingang-setzen-${p.id}`} disabled={speichert}
                    onClick={() => tun(api.setForwarderReceipt(id, p.id, {
                      receivedOn: p.forwarderReceivedOn === null ? heuteLokal() : null }))}>
                    {t(p.forwarderReceivedOn === null
                      ? "plp.markAtForwarder" : "plp.unmarkAtForwarder")}
                  </button>
                )}
                {mayWrite && !gefahren && (
                  <button type="button" className="btn btn--small" data-testid="plp-loesen"
                    disabled={speichert}
                    onClick={() => setLoesen(loesen?.poId === p.id
                      ? null : { poId: p.id, dteNumber: p.dteNumber, grund: "" })}>
                    {t("plp.unassign")}
                  </button>
                )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/*
          * AUS DER SENDUNG LOESEN -- eine bewusste Handlung mit Grund.
          *
          * Kein Loeschsymbol in der Zeile: hier wird nichts entfernt. Die
          * Zuordnung bleibt stehen und wandert in die Historie darunter.
          * Der Grund ist Pflicht, weil er drei Wochen spaeter die einzige
          * Antwort auf „warum ist PO1003 nicht mitgefahren" ist.
          */}
        {gefahren && (
          <p className="log__hint" data-testid="plp-gefahren">{t("plp.inTransitHint")}</p>
        )}
        {mayWrite && !gefahren && loesen !== null && (
          <div className="sup__kform" data-testid="plp-loesenmaske">
            <label className="sup__kfeld sup__kfeld--breit">
              <span className="sup__klabel">
                {t("plp.releaseFrom", { po: loesen.dteNumber, cn: d.number })}
              </span>
              <input className="log__input" value={loesen.grund} data-feld="releaseReason"
                aria-label={t("plp.releaseReason")}
                placeholder={t("plp.releaseReasonPlaceholder")}
                onChange={(e) => setLoesen({ ...loesen, grund: e.target.value })} />
            </label>
            <button className="btn btn--primary" data-testid="plp-loesenok"
              disabled={speichert || loesen.grund.trim() === ""}
              onClick={() => { const l = loesen; setLoesen(null);
                tun(api.releaseOrder(id, l.poId, { releaseReason: l.grund.trim() })); }}>
              {t("plp.unassign")}
            </button>
            <button className="btn" onClick={() => setLoesen(null)}>{t("common.cancel")}</button>
          </div>
        )}
        {/*
          * DIE GELOESTEN STEHEN NACHRANGIG, nicht zwischen den aktiven:
          * sie sind keine halben aktiven, sondern abgeschlossene
          * Tatsachen ueber die Vergangenheit. Eingeklappt, weil sie beim
          * Oeffnen der Akte niemanden interessieren -- und aufklappbar,
          * weil sie genau dann wichtig werden, wenn jemand fragt, warum
          * etwas fehlt.
          */}
        {d.releasedOrders.length > 0 && (
          <details className="plp__geloest" data-testid="plp-geloeste">
            <summary className="plp__geloesttitel">
              {t("plp.historyTitle")}{" ("}{d.releasedOrders.length}{")"}
            </summary>
            <ul className="plp__poliste">
              {d.releasedOrders.map((r) => (
                <li key={r.id} className="plp__pozeile" data-testid="plp-geloest">
                  <button type="button" className="plp__punktziel"
                    onClick={() => onOpenPo(r.purchaseOrderId)}>{r.dteNumber}</button>
                  <span className="plp__lanesupplier">{r.supplierName}</span>
                  <span className="plp__terminherkunft">
                    {t("plp.releasedOn", { date: kurzDatum(r.releasedAt.slice(0, 10), i18n.lang) })}
                    {r.releasedBy !== null && ` · ${r.releasedBy.name}`}
                    {" · "}{r.releaseReason}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {mayWrite && !gefahren && (d.status === "geplant" || d.status === "gebucht")
          && offen.length > 0 && (
          <div className="sup__kform" data-testid="plp-cnzuordnen">
            <label className="sup__kfeld sup__kfeld--breit">
              <span className="sup__klabel">{t("plp.addOrder")}</span>
              <select className="log__input" defaultValue="" data-feld="assign"
                aria-label={t("plp.addOrder")}
                onChange={(e) => { if (e.target.value !== "") tun(api.assignOrder(id, Number(e.target.value))); }}>
                <option value="">{t("plp.chooseOrder")}</option>
                {offen.map((p) => (
                  <option key={p.id} value={p.id}>{p.dteNumber} · {p.supplierName}</option>
                ))}
              </select>
            </label>
          </div>
        )}
        {mayWrite && (
          <div className="sup__kneu">
            {/*
              * ABSCHLIESSEN ERST NACH DER ZUSTELLUNG.
              *
              * Bis 0083 hiess „geschlossen" FUER ZUORDNUNG GESCHLOSSEN --
              * ein Riegel beim Planen. Den braucht es nicht mehr: eine
              * Sendung nimmt nur bis zur Carrier-Uebergabe Bestellungen
              * auf, und das entscheidet der Prozess. Der Code bedeutet
              * jetzt den administrativen Abschluss, und der folgt der
              * tatsaechlichen Anlieferung.
              */}
            {d.derivedStatus === "zugestellt" && (
              <button className="btn btn--small" data-testid="plp-cnschliessen" disabled={speichert}
                onClick={() => tun(api.updateConsolidation(id, { status: "geschlossen" }))}>
                {t("plp.closeCn")}
              </button>
            )}
            {/* Das Verwerfen ist ein Ausnahmeweg und steht deshalb
                abgesetzt -- nicht als gleichwertiger Knopf daneben. */}
            {d.status !== "verworfen" && !gefahren && (
              <button className="btn btn--small plp__ausnahme" data-testid="plp-cnverwerfen"
                disabled={speichert}
                onClick={() => tun(api.updateConsolidation(id, { status: "verworfen" }))}>
                {t("plp.discardCn")}
              </button>
            )}
          </div>
        )}
      </section>

      {/*
        * ── DER PROZESSVERLAUF -- EINGEKLAPPT ──
        *
        * Zehn Tatsachen in der Reihenfolge des Weges, mit dem, was
        * dasteht, was als Naechstes kommt, was noch gesperrt ist und
        * was es bei Anlage dieses Vorgangs nicht gab. Eine Zeile sagt,
        * wie weit es ist; wer mehr will, klappt auf.
        */}
      {/*
        * DIE PAPIERE DER BESTELLUNGEN -- an der Sendung nur GEZEIGT.
        *
        * Sie stehen DIREKT NACH den Bestellungen: ein Dokument gehoert
        * gedanklich zu seiner Bestellung, und den Prozessverlauf
        * dazwischenzuschieben trennt, was zusammengehoert.
        *
        * Wer eine Sendung verzollt, braucht die Packing Lists aller
        * Bestellungen. Sie dafuer ein zweites Mal an der Sendung
        * abzulegen hiesse, zwei Orte zu haben, an denen dieselbe Datei
        * auseinanderlaufen kann.
        */}
      <Sendungsdokumente cnId={id} i18n={i18n} />

      <Prozessverlauf codes={codes} milestones={d.milestones} prozess={d.process}
        i18n={i18n} testid="plp-verlauf" />

      {/*
        * ── DIE VOLLSTAENDIGE TERMINPFLEGE -- HINTER DEM STIFT ──
        *
        * DER BEFUND DER ABNAHME: sie stand als eigener Bereich in der
        * Hauptansicht und machte die Sendungsakte zu einem
        * Terminformular mit dreizehn Wahlmoeglichkeiten. Die Frage,
        * mit der jemand herkommt, ist aber eine andere -- was ist
        * jetzt zu tun --, und die beantwortet die Arbeitszone oben.
        *
        * ENTFERNT WURDE NICHTS: jeder Termin, jede Sicherheit, jede
        * Legacy- und Abhaengigkeitsregel sind unveraendert erreichbar
        * -- jetzt ueber „Bearbeiten". Das ist der Weg fuer
        * Korrekturen und Sonderfaelle, nicht der taegliche.
        *
        * UND DER KNOPF OBEN FINDET SIE WEITERHIN. Wer in der
        * Arbeitszone „Jetzt erfassen" drueckt, bekommt die Maske
        * aufgeklappt und vorgewaehlt -- derselbe Weg wie bisher, nur
        * nicht mehr dauerhaft offen.
        */}
      {(bearbeitet || terminePflege) && (
      <details className="plp__termineAlle" data-testid="plp-alletermine" ref={alleTermine}>
        <summary className="plp__geloesttitel">{t("plp.editMilestones")}</summary>
        <Termine subject="consolidation" subjectId={id} milestones={d.milestones}
          codes={codes} prozess={d.process}
          i18n={i18n} mayWrite={mayWrite} onChanged={nachher} onError={melden} vorwahl={cnVorwahl} onVorwahl={cnVorwahlVerbraucht}
        />
      </details>
      )}

      {/*
        * ABSCHLUSS DER SENDUNG -- und hier gehoert die Wartezeit hin.
        *
        * Eine Konsolidierung faehrt, wenn die letzte Bestellung da ist.
        * Was die erste in der Zwischenzeit gewartet hat, ist eine
        * Eigenschaft DIESER Sendung und keine Schwaeche jener
        * Bestellung. Deshalb steht die Zahl hier und die Bewertung
        * daneben.
        */}
      {beurteilbar && <Abschluss sterne={d.reviewStars} rueckblick={d.reviewNote}
        i18n={i18n} mayWrite={mayWrite} speichert={speichert} testid="plp-cnabschluss"
        onSpeichern={async (st, rb) => {
          setSpeichert(true);
          try {
            setD(await api.updateConsolidation(id, { reviewStars: st, reviewNote: rb } as never));
            setFehler(null); onChanged();
          } catch (e) { melden(e); } finally { setSpeichert(false); }
        }} />}
      {/*
        * ── PLAN GEGEN IST, DREI ZEILEN ──
        *
        * Dieselben drei Etappen wie oben im Transport, hier mit der
        * Abweichung. Sie steht erst da, wenn BEIDE Werte vorliegen --
        * eine Zahl gegen ein fehlendes Datum waere keine.
        *
        * UND SIE SAGT NICHT, WER SCHULD IST. Eine spaete Abfahrt kann
        * eine bewusste Entscheidung sein: wir warten auf eine
        * verspaetete Bestellung und nehmen sie mit. HOMEBASE weiss
        * das nicht und behauptet es deshalb auch nicht -- die Zahl
        * sagt, WAS war. Wer den Grund kennt, schreibt ihn in den
        * Rueckblick darueber.
        */}
      {/* DERSELBE BEFUND: die Karte erscheint erst mit einem
         auswertbaren Paar -- der Hinweis muss ihr folgen. Er stand
         sonst am Ende der Akte und erklaerte eine Abweichung, die
         nirgends zu sehen war. */}
      {planIst.some((z) => z.wert !== null) && (
        <>
          <HilfeHinweis bereich="cn.planist" />
          <Prozessdaten testid="plp-cnplanist" i18n={i18n} titel={t("plp.planVsActual")}
            zeilen={planIst} raster />
        </>
      )}
      {/*
        * ── EINE WARTEZEIT VON NULL TAGEN IST KEINE WARTEZEIT ────────
        *
        * DER BEFUND DER ABNAHME. Produktiv stand dort: erste
        * Bestellung 06.10., letzte Bestellung 06.10., Wartezeit
        * 0 Tage. Drei Zeilen fuer die Auskunft „es hat niemand
        * gewartet" -- das ist keine Erkenntnis, sondern eine Karte.
        *
        * DIE DATEN AENDERN SICH NICHT. Gerechnet wird weiter wie
        * bisher; gezeigt wird die Karte nur, wenn tatsaechlich
        * jemand gewartet hat.
        */}
      {wartezeitEcht && (
        <>
          <Prozessdaten testid="plp-cnprozessdaten" i18n={i18n} titel={t("plp.waitingTimes")}
            zeilen={wartezeiten} raster />
          <p className="log__hint plp__fairness" data-testid="plp-fairness">
            {t("plp.dFairness")}
          </p>
        </>
      )}
      <Kalenderraum />
    </div>
    </HilfeRahmen>
  );
}

/* ── Der Rahmen ─────────────────────────────────────────────────────────── */

/**
 * ══════════════════════════════════════════════════════════════════════
 * DIE CHINA-EREIGNISSE PFLEGEN
 * ══════════════════════════════════════════════════════════════════════
 *
 * SIE SIND FACHDATEN UND KEIN QUELLTEXT. Wanderfeste haben jedes Jahr
 * ein anderes Datum, und die Vor- und Nachlaufzeiten sind
 * Erfahrungswerte, die sich fortschreiben. Eine Liste im Code hiesse:
 * Codeaenderung, Build und Auslieferung wegen eines Feiertags.
 *
 * DASSELBE BEDIENMUSTER WIE UEBERALL. Ein Stift im Kartenkopf fuehrt
 * hinein; Speichern und Abbrechen stehen dort, wo gearbeitet wird. Keine
 * zweite Editierlogik daneben.
 *
 * UND STILLLEGEN STATT LOESCHEN. Ein Ereignis, das sich als unerheblich
 * erweist, verschwindet aus der Achse -- aber nicht aus der Geschichte.
 */
function ChinaPflege({ i18n, onChanged, onSchliessen }: {
  i18n: I18n; onChanged: () => void;
  /**
   * DER WEG HINAUS.
   *
   * Diese Karte steht nicht mehr dauerhaft in der Uebersicht; sie
   * wird aus der China-Spur geoeffnet und schliesst sich nach
   * Speichern oder Abbrechen wieder. Sie kennt deshalb keinen
   * Lesezustand mehr -- wer sie sieht, bearbeitet.
   */
  onSchliessen: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [liste, setListe] = useState<ChinaEvent[] | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<number, ChinaEntwurf>>({});
  const [neu, setNeu] = useState<ChinaEntwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  /* Beim Oeffnen geladen UND sofort in Entwuerfe ueberfuehrt: wer
     diese Karte sieht, bearbeitet sie. */
  const laden = useCallback(() => {
    api.chinaEvents().then((r) => {
      setListe([...r.items]);
      setEntwuerfe(Object.fromEntries(r.items.map((c) => [c.id, ausEvent(c)])));
    }).catch(setFehler);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const schliessen = (): void => { setEntwuerfe({}); setNeu(null); onSchliessen(); };
  const sichern = async (): Promise<void> => {
    setSpeichert(true); setFehler(null);
    try {
      for (const c of liste ?? []) {
        const e = entwuerfe[c.id];
        if (e === undefined || !veraendert(c, e)) continue;
        await api.updateChinaEvent(c.id, zuEingabe(e));
      }
      if (neu !== null && neu.name.trim() !== "") await api.createChinaEvent(zuEingabe(neu));
      onChanged(); schliessen();
    } catch (e) { setFehler(e); } finally { setSpeichert(false); }
  };
  const stilllegen = async (id: number): Promise<void> => {
    setSpeichert(true); setFehler(null);
    try { await api.deactivateChinaEvent(id); onChanged(); schliessen(); }
    catch (e) { setFehler(e); } finally { setSpeichert(false); }
  };

  const feld = (id: number | "neu", k: keyof ChinaEntwurf, wert: string): void => {
    if (id === "neu") { setNeu((n) => n === null ? n : { ...n, [k]: wert }); return; }
    setEntwuerfe((m) => ({ ...m, [id]: { ...m[id]!, [k]: wert } }));
  };

  const zeile = (c: ChinaEvent | null, e: ChinaEntwurf, id: number | "neu"
  ): React.JSX.Element => (
    <div key={String(id)} className="plp__chinazeile" data-testid="plp-chinaform">
      <label className="sup__kfeld"><span className="sup__klabel">{t("china.name")}</span>
        <input className="log__input log__input--text" value={e.name} data-feld="name"
          aria-label={t("china.name")} disabled={speichert}
          onChange={(x) => feld(id, "name", x.target.value)} /></label>
      <label className="sup__kfeld"><span className="sup__klabel">{t("china.from")}</span>
        <input className="log__input log__input--text" type="date" value={e.von}
          data-feld="coreStart" aria-label={t("china.from")} disabled={speichert}
          onChange={(x) => feld(id, "von", x.target.value)} /></label>
      <label className="sup__kfeld"><span className="sup__klabel">{t("china.to")}</span>
        <input className="log__input log__input--text" type="date" value={e.bis}
          data-feld="coreEnd" aria-label={t("china.to")} disabled={speichert}
          onChange={(x) => feld(id, "bis", x.target.value)} /></label>
      <label className="sup__kfeld"><span className="sup__klabel">{t("china.leadIn")}</span>
        <input className="log__input log__input--text" type="number" min="0" value={e.vor}
          data-feld="leadInDays" aria-label={t("china.leadIn")} disabled={speichert}
          onChange={(x) => feld(id, "vor", x.target.value)} /></label>
      <label className="sup__kfeld"><span className="sup__klabel">{t("china.leadOut")}</span>
        <input className="log__input log__input--text" type="number" min="0" value={e.nach}
          data-feld="leadOutDays" aria-label={t("china.leadOut")} disabled={speichert}
          onChange={(x) => feld(id, "nach", x.target.value)} /></label>
      {c !== null && (c.active ? (
        <button type="button" className="bew__weg" disabled={speichert}
          data-testid="plp-chinaaus"
          onClick={() => void stilllegen(c.id)}>{t("china.deactivate")}</button>
      ) : (
        /* Stillgelegt heisst: aus der Achse fort, nicht aus der
           Geschichte. Die Zeile bleibt bearbeitbar -- und sagt, dass
           sie nicht mehr mitfaehrt. */
        <span className="plp__chinastatus" data-testid="plp-chinaaus-an">
          {t("china.inactive")}</span>
      ))}
    </div>
  );

  /*
   * EIGENER KLASSENNAME, WEIL ES EINE ANDERE SACHE IST.
   *
   * `plp__china` traegt das EREIGNIS in der Achse und ist dort absolut
   * gesetzt. Diese Karte stand kurzzeitig unter demselben Namen -- und
   * wurde damit aus dem Fluss der Seite gehoben und ueber alles andere
   * gelegt, mitten ueber die Navigation. Ein Name, zwei Dinge: beim
   * Lesen eine Falle, beim Gestalten ein Fehler.
   */
  return (
    <section className="log__group plp__chinakarte" data-testid="plp-chinapflege">
      {/*
        * KEIN ZWEITER STIFT. Der Einstieg steht an der China-Spur;
        * diese Karte IST der Bearbeitungsmodus. Ein Stift in ihrem
        * Kopf waere eine zweite gleichwertige Aktion fuer dieselbe
        * Funktion -- und damit genau das, was Abschnitt 15 verbietet.
        */}
      <h4 className="log__grouptitle">{t("china.title")}</h4>
      <p className="log__hint">{t("china.hint")}</p>
      {fehler !== null && <p className="log__error">{errorText(i18n, fehler)}</p>}
      {liste === null ? <p className="log__hint">{t("common.loading")}</p> : (
        <div className="plp__chinaliste">
          {/* DER HINWEIS STEHT IN DER LISTE UND NICHT AN IHRER STELLE.
              Anders waere mit leerer Liste auch „+ Ereignis" fort --
              und damit der einzige Weg, die erste Zeile anzulegen. */}
          {liste.length === 0 && neu === null && (
            <p className="log__hint">{t("china.none")}</p>
          )}
          {liste.map((c) => zeile(c, entwuerfe[c.id] ?? ausEvent(c), c.id))}
          {neu !== null && zeile(null, neu, "neu")}
          <span className="sup__kformaktionen">
            {neu === null && (
              <button className="btn btn--small" disabled={speichert}
                data-testid="plp-chinaneu"
                onClick={() => setNeu({ name: "", von: "", bis: "", vor: "0", nach: "0" })}>
                {t("china.add")}
              </button>
            )}
            <button className="btn btn--primary btn--small" disabled={speichert}
              data-testid="plp-chinaspeichern"
              onClick={() => void sichern()}>{t("log.save")}</button>
            <button className="btn btn--small" disabled={speichert}
              data-testid="plp-chinaabbrechen"
              onClick={schliessen}>{t("common.cancel")}</button>
          </span>
        </div>
      )}
    </section>
  );
}

/** Der Entwurf einer Zeile -- Zeichenketten, weil Formulare das sind. */
type ChinaEntwurf = {
  name: string; von: string; bis: string; vor: string; nach: string;
};
const ausEvent = (c: ChinaEvent): ChinaEntwurf => ({
  name: c.name, von: c.coreStart, bis: c.coreEnd,
  vor: String(c.leadInDays), nach: String(c.leadOutDays),
});
const veraendert = (c: ChinaEvent, e: ChinaEntwurf): boolean =>
  c.name !== e.name || c.coreStart !== e.von || c.coreEnd !== e.bis
  || String(c.leadInDays) !== e.vor || String(c.leadOutDays) !== e.nach;
/*
 * DIE QUELLE UND DER STATUS WERDEN HIER NICHT MITGESCHICKT.
 *
 * Wer ein Datum korrigiert, trifft keine Aussage darueber, ob die
 * amtliche Regelung inzwischen veroeffentlicht ist. Diese Felder
 * bleiben deshalb, was sie waren -- der Fachkern laesst sie
 * unveraendert, wenn sie nicht im Aufruf stehen (siehe
 * `chinaAendern`). Dass er es TUT, prueft LG13al2b am Bestand: zuvor
 * hat genau dieser Weg den Quellenhinweis geloescht.
 */
const zuEingabe = (e: ChinaEntwurf): UpsertChinaEventRequest => ({
  name: e.name.trim(), coreStart: e.von, coreEnd: e.bis,
  leadInDays: e.vor === "" ? 0 : Number(e.vor),
  leadOutDays: e.nach === "" ? 0 : Number(e.nach),
});

export function ProcurementPortal({ meta, capabilities, onOpenPartners }: {
  meta: MetaResponse; capabilities: readonly string[];
  /** Sprung in die Geschaeftspartner -- siehe `plp.manageForwarders`. */
  onOpenPartners?: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const mayWrite = capabilities.includes("procurement.write");
  const [daten, setDaten] = useState<TimelineResponse | null>(null);
  const [ladeFehler, setLadeFehler] = useState<unknown>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [stufe, setStufe] = useState<Stufe>("woche");
  /* Welche Sendung gerade ihren Lieferinhalt zeigt -- `null` ist keine. */
  const [inhalt, setInhalt] = useState<Auswahl>(null);
  const [poOffen, setPoOffen] = useState<number | null>(null);
  const [cnOffen, setCnOffen] = useState<number | null>(null);
  /*
   * ZWEI EINSTIEGE, EINE MASKE.
   *
   * „+ Bestellung" fragt zuerst, WIE -- hochladen oder von Hand. Danach
   * fuehren beide in dieselbe `OrderCapture`. Zwei aehnliche Masken
   * waeren zwei Orte, an denen dieselbe Pruefung stehen muesste, und der
   * zweite ist der, an dem sie eines Tages fehlt.
   */
  const [einstieg, setEinstieg] = useState<"wahl" | "manuell" | null>(null);
  const [capture, setCapture] = useState<PurchaseOrderCapture | null>(null);
  const [speichert, setSpeichert] = useState(false);

  /* Ob die China-Pflege gerade aufgeklappt ist. Sie steht nicht
     dauerhaft da -- siehe den Aufruf unten. */
  const [chinaOffen, setChinaOffen] = useState(false);

  const laden = useCallback(async () => {
    try { setDaten(await api.timeline()); setLadeFehler(null); }
    catch (e) { setLadeFehler(e); }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  if (ladeFehler !== null) return <p className="log__error">{errorText(i18n, ladeFehler)}</p>;
  if (daten === null) return <p className="work__empty">{t("common.loading")}</p>;

  if (einstieg === "manuell" || capture !== null) {
    return <OrderCapture meta={meta} capture={capture} i18n={i18n}
      onAbbruch={() => { setEinstieg(null); setCapture(null); }}
      onFertig={(poId) => { setEinstieg(null); setCapture(null); void laden(); setPoOffen(poId); }}
      onOpenPo={(poId) => { setEinstieg(null); setCapture(null); setPoOffen(poId); }} />;
  }
  if (poOffen !== null) {
    return <Bestellakte id={poOffen} meta={meta} codes={daten.codes} i18n={i18n}
      mayWrite={mayWrite} onClose={() => { setPoOffen(null); void laden(); }}
      onChanged={() => void laden()} />;
  }
  if (cnOffen !== null) {
    return <CnAkte id={cnOffen} meta={meta} codes={daten.codes} i18n={i18n} mayWrite={mayWrite}
      onClose={() => { setCnOffen(null); void laden(); }}
      onChanged={() => void laden()}
      onOpenPo={(poId) => { setCnOffen(null); setPoOffen(poId); }} onOpenPartners={onOpenPartners} />;
  }

  /**
   * Einen Beleg hochladen.
   *
   * Gelesen wird die Datei im Browser und als Base64 geschickt --
   * derselbe Weg wie bei den Produktdokumenten (0052). Was daraus
   * entsteht, ist ein VORSCHLAG; die Bestellung entsteht erst mit der
   * Bestaetigung in der Maske.
   */
  async function belegHochladen(datei: File): Promise<void> {
    setSpeichert(true);
    try {
      const base64 = await new Promise<string>((ok, weg) => {
        const leser = new FileReader();
        leser.onerror = () => weg(leser.error);
        leser.onload = () => {
          const s = String(leser.result);
          ok(s.slice(s.indexOf(",") + 1));
        };
        leser.readAsDataURL(datei);
      });
      const neu = await api.createCapture({ fileName: datei.name, contentBase64: base64 });
      setFehler(null); setEinstieg(null); setCapture(neu);
    } catch (e) { setFehler(e); } finally { setSpeichert(false); }
  }

  return (
    <HilfeRahmen>
    <div className="plp">
      <div className="sup__kopf">
        <h2 className="sup__titel">{t("plp.title")}</h2>
        {/* EIN Schalter je Seite -- nicht einer je Bereich. */}
        <HilfeSchalter />
        <span className="sup__zahl">
          {t("plp.count", { n: String(daten.lanes.filter((l) => l.kind === "purchase_order").length) })}
        </span>
        <span className="plp__stufen" role="group" aria-label={t("plp.zoom")}>
          {(Object.keys(STUFEN) as Stufe[]).map((s) => (
            <button key={s} type="button" data-testid="plp-stufe" data-stufe={s}
              className={s === stufe ? "sdb__sichtknopf sdb__sichtknopf--on" : "sdb__sichtknopf"}
              aria-pressed={s === stufe} onClick={() => setStufe(s)}>
              {i18n.tCode("plpZoom", s)}
            </button>
          ))}
        </span>
        {mayWrite && (
          <>
            <button className="btn btn--primary btn--small" data-testid="plp-poneu"
              onClick={() => setEinstieg("wahl")}>
              {t("plp.addOrderShort")}
            </button>
            <button className="btn btn--small" data-testid="plp-cnneu" disabled={speichert}
              onClick={() => { setSpeichert(true);
                void api.createConsolidation({}).then((c) => { setFehler(null); setCnOffen(c.id); })
                  .catch(setFehler).finally(() => setSpeichert(false)); }}>
              {t("plp.addCn")}
            </button>
          </>
        )}
      </div>
      {fehler !== null && (
        <p className="log__error" role="alert" data-testid="plp-fehler">{errorText(i18n, fehler)}</p>
      )}

      {/*
        * ZWEI VERSTAENDLICHE MOEGLICHKEITEN -- und danach dieselbe Maske.
        *
        * Der Beleg ist der haeufigere Fall und steht deshalb zuerst. Wer
        * keinen hat, legt von Hand an; beides fuehrt in dasselbe
        * Formular und in dieselben Tabellen.
        */}
      {einstieg === "wahl" && (
        <section className="log__group sup__anlage" data-testid="plp-anlage">
          <h4 className="log__grouptitle">{t("plp.newOrder")}</h4>
          <div className="sup__kneu plp__einstiege">
            <label className="btn btn--primary btn--small plp__hochladen"
              data-testid="plp-belegwaehlen">
              {speichert ? t("props.saving") : t("plp.uploadOrder")}
              <input type="file" accept="application/pdf" hidden disabled={speichert}
                aria-label={t("plp.uploadOrder")}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f !== undefined) void belegHochladen(f);
                }} />
            </label>
            <button className="btn btn--small" data-testid="plp-manuell" disabled={speichert}
              onClick={() => setEinstieg("manuell")}>{t("plp.manualOrder")}</button>
            <button className="btn btn--small" disabled={speichert}
              onClick={() => setEinstieg(null)}>{t("common.cancel")}</button>
          </div>
          {/* Der Beleg ist EVIDENZ, nicht bestaetigte Wahrheit -- und das
              steht da, bevor jemand hochlaedt. */}
          <p className="log__hint">{t("plp.uploadHint")}</p>
          <p className="log__hint">{t("plp.numberHint")}</p>
        </section>
      )}

      {/*
        * ── DIE SENDUNG FUEHRT, NICHT DIE BESTELLUNG ──────────────────
        *
        * DER BEFUND DER ABNAHME. Die Uebersicht war PO-zentriert; die
        * Frage eines normalen Nutzers ist aber eine andere: WELCHE
        * LIEFERUNG KOMMT, wann, mit was drin, und gibt es ein Problem?
        * Die Sendung ist die Transporteinheit -- die Bestellungen sind
        * ihre Bestandteile.
        */}
      {/*
        * EIN HINWEIS STEHT NUR DA, WENN SEIN GEGENSTAND DASTEHT.
        *
        * DER BEFUND DER BILDABNAHME: der Hinweis war unbedingt, die
        * Auswahl nicht -- ohne eine einzige Sendung erklaerte der
        * Hilfemodus eine Auswahl, die gar nicht da war. Ein Hinweis
        * auf etwas Unsichtbares ist keine Hilfe, sondern Verwirrung.
        */}
      {daten.lanes.some((l) => l.kind === "consolidation") && (
        <HilfeHinweis bereich="ov.cnwahl" />
      )}
      <CnWahl lanes={daten.lanes} gewaehlt={inhalt} i18n={i18n}
        onWahl={setInhalt} onAkte={(id) => setCnOffen(id)} onPo={setPoOffen} />

      <HilfeHinweis bereich="timeline" />
      <Zeitachse daten={daten} stufe={stufe} i18n={i18n} gewaehlt={inhalt}
        onLane={(l) => l.kind === "purchase_order" ? setPoOffen(l.id) : setCnOffen(l.id)}
        onEvent={(l) => l.kind === "purchase_order" ? setPoOffen(l.id) : setCnOffen(l.id)}
        onChinaPflege={mayWrite ? () => setChinaOffen((an) => !an) : undefined}
        chinaOffen={chinaOffen} />

      {/*
        * ── DIE CHINA-PFLEGE KLAPPT UNTER DER ACHSE AUF ─────────────
        *
        * DER BEFUND DER BROWSER-RE-ABNAHME (Abschnitt 10). Die grosse
        * Karte stand dauerhaft in der Uebersicht und nahm senkrechten
        * Platz, den im Alltag die Zeitachse braucht -- gepflegt werden
        * diese Daten nur gelegentlich.
        *
        * SIE STEHT JETZT GENAU DANN DA, WENN SIE GEBRAUCHT WIRD: der
        * Stift an der China-Spur oeffnet sie, Speichern und Abbrechen
        * schliessen sie wieder. Es ist dieselbe Pflegeoberflaeche wie
        * zuvor -- keine zweite Architektur, nur ein anderer Einstieg.
        */}
      {chinaOffen && (
        <ChinaPflege i18n={i18n} onChanged={() => { void laden(); }}
          onSchliessen={() => setChinaOffen(false)} />
      )}

      {/*
        * ── WAS KOMMT AN -- OHNE ZUSATZSCHRITT ────────────────────────
        *
        * Der Lieferinhalt war hinter einem kleinen Knopf an der Spur
        * versteckt. Wer ihn nicht kannte, fand ihn nicht. Jetzt folgt
        * er der Auswahl oben und steht einfach da.
        */}
      {/*
        * UND ZU „NICHT ZUGEORDNET" GIBT ES KEINEN LIEFERINHALT.
        *
        * Er ist die Zusammenfassung EINER Sendung. Unzugeordnete
        * Bestellungen sind keine Sendung -- sie reisen gar nicht
        * gemeinsam. Eine Aggregation darueber waere eine Lieferung,
        * die es nicht gibt.
        *
        * DIE KARTE VERSCHWINDET VOLLSTAENDIG. Den Inhalt der ZUVOR
        * gewaehlten Sendung stehen zu lassen waere die schlimmste
        * Variante: die Zahlen gehoerten dann sichtbar zu etwas
        * anderem als das, was oben gedrueckt ist.
        */}
      {typeof inhalt === "number" && <HilfeHinweis bereich="ov.ware" />}
      {typeof inhalt === "number" && (
        <Lieferinhalt id={inhalt} i18n={i18n}
          nummer={daten.lanes.find((l) => l.kind === "consolidation" && l.id === inhalt)
            ?.label ?? ""} />
      )}

      <div className="log__cluster plp__unten">
        <Arbeitsliste daten={daten} i18n={i18n}
          onSubject={(kind, id) => kind === "purchase_order" ? setPoOffen(id) : setCnOffen(id)} />
        <Fenster daten={daten} i18n={i18n} mayWrite={mayWrite}
          onZuordnen={(w) => { setSpeichert(true);
            void api.assignOrder(w.consolidationId, w.purchaseOrderId)
              .then(() => { setFehler(null); return laden(); })
              .catch(setFehler).finally(() => setSpeichert(false)); }} />
      </div>
    </div>
    </HilfeRahmen>
  );
}

export type { LeadTimeRule };
